import { produce } from 'immer';
import { nanoid } from 'nanoid';

import { diagramActionTypes } from './diagram.types';
import { utilHistoryUndo, utilHistoryRedo, HISTORY } from './diagram.utils';

// eslint-disable-next-line
import { getElementsChangesView } from '../../utils/get-tables-changes-view';
import { _buildLinePath } from '../../components/Canvas/CanvasLine';

const INITIAL_SCALE_VALUE = 0.9; // 1;
const TABLE_ROW_HEIGHT = 36;

// let tables_limit = 20;
// let columns_limit = 20;
// let notes_limit = 20;
// let pens_limit = 50;
// let areas_limit = 10;

let tables_limit = 40;
let columns_limit = 150;
let notes_limit = 20;
let pens_limit = 50;
let areas_limit = 20;

function checkTables(elementsValue, addCount = 1) {
    let tablesCount = 0;

    for (const key in elementsValue) {
        const element = elementsValue[key];

        switch (element.type) {
            case 'table': {
                tablesCount++;

                if ((tablesCount + addCount) > tables_limit) {
                    return false;
                }
                break;
            }
            default:
                break;
        }
    }
    return true;
}

function checkColumns(elementsValue, elementId, addCount = 1) {
    if (elementId) {
        const element = elementsValue[elementId];
        const columns = element?.value?.columns;

        if (columns && (Object.keys(columns)?.length + addCount) > columns_limit) {
            return false;
        }
    }
    return true;
}

function checkNotes(elementsValue, addCount = 1) {
    let notesCount = 0;

    for (const key in elementsValue) {
        const element = elementsValue[key];

        switch (element.type) {
            case 'note': {
                notesCount++;

                if ((notesCount + addCount) > notes_limit) {
                    return false;
                }
                break;
            }
            default:
                break;
        }
    }
    return true;
}

function checkPens(elementsValue, addCount = 1) {
    let pensCount = 0;

    for (const key in elementsValue) {
        const element = elementsValue[key];

        switch (element.type) {
            case 'pen-segment': {
                pensCount++;

                if ((pensCount + addCount) > pens_limit) {
                    return false;
                }
                break;
            }
            default:
                break;
        }
    }
    return true;
}

function checkAreas(areasValue, addCount = 1) {
    if (areasValue && (Object.keys(areasValue)?.length + addCount) > areas_limit) {
        return false;
    }
    return true;
}



const initialState = {
    // pricing plan limit
    isModalSignInRestrictEnabled: false,
    modalSignInRestrictType: '',

    // modal sign in remind
    isModalSignInRemindEnabled: false,

    // BASIC
    scale: INITIAL_SCALE_VALUE,
    originXY: { x: 0, y: 0 },

    // MODES
    viewMode: 'full', // full|keys|names
    themeMode: false, // = isEnabledLightMode

    // ITEMS
    elements: {},
    elementsLive: {}, // show live elements positions to other users on the board
    elementsLastCommited: {},
    canvasLimits: {},

    lines: {},
    areas: {},

    // FOCUSED ITEMS
    focusedElements: {},
    focusedElementsIds: [],
    focusedInsideElement: null,

    focusedLine: null,
    focusedLineId: null,

    focusedArea: null,
    focusedAreaId: null,


    // FOCUSED GROUP ITEMS
    focusedGroupElements: {},
    focusedGroupElementsIds: [],
    focusedGroupAreas: {},
    focusedGroupAreasIds: [],


    // CONTROLS TOP LEFT
    isBtnSettingsEnabled: false,
    isBtnModalShortcutsEnabled: false,

    isBtnLeftPanelEnabled: false,
    isBtnSearchEnabled: false,

    isBtnExportEnabled: false,

    isBtnExportImageEnabled: false,
    isBtnExportImageClickEnabled: false,

    isBtnExportCodeEnabled: false,

    isBtnSqlSyntaxEnabled: false,
    // sqlSyntax: null,

    isBtnViewModesEnabled: false,
    isBtnRedGreenViewEnabled: false,

    // CONTROLS TOP RIGHT
    isBtnPopupNewsEnabled: false,


    isBtnProfileEnabled: false,


    // CONTROLS LEFT
    isCursorHand: false,
    isBtnNewTableClickEnabled: false,
    isBtnNewTableGrabEnabled: false,
    isBtnNewNoteClickEnabled: false,
    isBtnNewNoteGrabEnabled: false,
    isBtnNewLineEnabled: false,
    isBtnNewAreaEnabled: false,
    isBtnPenEnabled: false,

    // CONTROLS BOTTOM
    isBtnMinimapEnabled: false,
    isBtnInfoResourcesEnabled: false,

    isCanvasGridEnabled: true,

    // PEN MENU
    newPen: {
        color: 'yellow',
        width: 3,
        id: nanoid(),
    },

    // CONTEXT MENU
    isContextMenuShowed: true,
    isContextMenuVisible: true,

    // HISTORY
    history: [],
    historyPointer: -1,
    historyIsUndoAvailable: false,
    historyIsRedoAvailable: false,
    isHistoryClicked: false,

    isEnabledElementDescriptionEditing: false,

    isContentChanged: false,

    // modal diagram export sql
    isModalDiagramExportSqlEnabled: false,
    modalDiagramExportSqlData: { dbEngine: null },

};

function calculateCanvasLimits(state, draft) {
    const elementsKeys = Object.keys(draft.elements);
    const areasKeys = Object.keys(draft.areas);

    const elementsCount = elementsKeys.length;
    const areasCount = areasKeys.length;

    if (!elementsCount && !areasCount) {
        return draft;
    }

    const firstElement = elementsCount
        ? draft.elements[elementsKeys[0]]
        : draft.areas[areasKeys[0]];

    let canvasLimits = {
        minX: firstElement.x + 100,
        maxX: firstElement.x + 100,
        minY: firstElement.y + 200,
        maxY: firstElement.y + 200,
    };

    if (elementsCount) {
        Object.keys(draft.elements).forEach((key) => {
            const element = draft.elements[key];
            canvasLimits = {
                minX: Math.min(canvasLimits.minX, element.x),
                maxX: Math.max(canvasLimits.maxX, element.x + element.width),
                minY: Math.min(canvasLimits.minY, element.y),
                maxY: Math.max(canvasLimits.maxY, element.y + element.height),
            };
        });
    }

    if (areasCount) {
        Object.keys(draft.areas).forEach((key) => {
            const area = draft.areas[key];
            canvasLimits = {
                minX: Math.min(canvasLimits.minX, area.x),
                maxX: Math.max(canvasLimits.maxX, area.x + area.width),
                minY: Math.min(canvasLimits.minY, area.y),
                maxY: Math.max(canvasLimits.maxY, area.y + area.height),
            };
        });
    }

    draft.canvasLimits = canvasLimits;

    return draft;
}

function unfocusFocusedItems(state, draft) {
    // FOCUSED LINE
    if (state.focusedLine || state.focusedLineId) {
        draft.focusedLine = null;
        draft.focusedLineId = null;
    }
    // FOCUSED AREA
    if (state.focusedArea || state.focusedAreaId) {
        draft.focusedArea = null;
        draft.focusedAreaId = null;
    }
    // FOCUSED INSIDE ELEMENT
    if (state.focusedInsideElement) {
        draft.focusedInsideElement = null;
    }
    // FOCUSED ELEMENTS
    if (state.focusedElementsIds?.length) {
        draft.focusedElements = {};
        draft.focusedElementsIds = [];
    }

    // focused group elements and areas
    if (state.focusedGroupElementsIds?.length) {
        draft.focusedGroupElements = {};
        draft.focusedGroupElementsIds = [];
    }
    if (state.focusedGroupAreasIds?.length) {
        draft.focusedGroupAreas = {};
        draft.focusedGroupAreasIds = [];
    }

    return draft;
}


function getNewAreaIdForElement(areas, element) {
    if (!element) {
        return null;
    }

    const {
        x: elementX,
        y: elementY,
        width: elementWidth,
        height: elementHeight,
    } = element;

    let newAreaId = null;

    for (const key in areas) {
        const area = areas[key];
        const {
            id,
            x: areaX,
            y: areaY,
            width: areaWidth,
            height: areaHeight,
        } = area;

        const minX = areaX;
        const minY = areaY;
        const maxX = areaX + areaWidth;
        const maxY = areaY + areaHeight;

        if (
            elementX + elementWidth > minX &&
            elementX < maxX &&
            elementY + elementHeight > minY &&
            elementY < maxY
        ) {
            newAreaId = id;
        }
    }

    return newAreaId;
}

function unfocusEnabledControls(state, draft, exceptControl = null, exceptControlSecond = null) {
    // CONTROLS TOP LEFT
    if (state.isBtnSettingsEnabled && exceptControl !== 'isBtnSettingsEnabled') {
        draft.isBtnSettingsEnabled = false;
    }
    if (state.isBtnModalShortcutsEnabled && exceptControl !== 'isBtnModalShortcutsEnabled') {
        draft.isBtnModalShortcutsEnabled = false;
    }

    if (state.isBtnProfileEnabled && exceptControl !== 'isBtnProfileEnabled') {
        draft.isBtnProfileEnabled = false;
    }

    if (state.isBtnSearchEnabled && exceptControl !== 'isBtnSearchEnabled') {
        draft.isBtnSearchEnabled = false;
    }

    if (state.isBtnExportEnabled && exceptControl !== 'isBtnExportEnabled') {
        draft.isBtnExportEnabled = false;
    }
    if (state.isBtnExportImageEnabled && exceptControl !== 'isBtnExportImageEnabled' && exceptControlSecond !== 'isBtnExportImageEnabled') {
        draft.isBtnExportImageEnabled = false;
    }
    if (state.isBtnExportCodeEnabled && exceptControl !== 'isBtnExportCodeEnabled') {
        draft.isBtnExportCodeEnabled = false;
    }

    if (state.isBtnSqlSyntaxEnabled && exceptControl !== 'isBtnSqlSyntaxEnabled') {
        draft.isBtnSqlSyntaxEnabled = false;
    }


    if (state.isBtnViewModesEnabled && exceptControl !== 'isBtnViewModesEnabled') {
        draft.isBtnViewModesEnabled = false;
    }
    if (state.isBtnRedGreenViewEnabled && exceptControl !== 'isBtnRedGreenViewEnabled') {
        draft.isBtnRedGreenViewEnabled = false;
    }

    // CONTROLS LEFT
    if (state.isBtnNewTableClickEnabled && exceptControl !== 'isBtnNewTableClickEnabled') {
        draft.isBtnNewTableClickEnabled = false;
    }
    if (state.isBtnNewTableGrabEnabled && exceptControl !== 'isBtnNewTableGrabEnabled') {
        draft.isBtnNewTableGrabEnabled = false;
    }
    if (state.isBtnNewNoteClickEnabled && exceptControl !== 'isBtnNewNoteClickEnabled') {
        draft.isBtnNewNoteClickEnabled = false;
    }
    if (state.isBtnNewNoteGrabEnabled && exceptControl !== 'isBtnNewNoteGrabEnabled') {
        draft.isBtnNewNoteGrabEnabled = false;
    }
    if (state.isBtnNewLineEnabled && exceptControl !== 'isBtnNewLineEnabled') {
        draft.isBtnNewLineEnabled = false;
    }
    if (state.isBtnNewAreaEnabled && exceptControl !== 'isBtnNewAreaEnabled') {
        draft.isBtnNewAreaEnabled = false;
    }
    if (state.isBtnPenEnabled && exceptControl !== 'isBtnPenEnabled') {
        draft.isBtnPenEnabled = false;
    }

    if (state.isBtnInfoResourcesEnabled && exceptControl !== 'isBtnInfoResourcesEnabled') {
        draft.isBtnInfoResourcesEnabled = false;
    }

    // if (state.isCursorHand && exceptControl !== 'isCursorHand') {
    //     draft.isCursorHand = false;
    // }

    if (draft.isHistoryClicked) {
        draft.isHistoryClicked = false;
    }

    return draft;
}

function getIndexNumber(columns, columnId) {
    let newInsideElementIndex = 0;
    const keys = Object.keys(columns);

    for (let i = 0; i < keys.length; i++) {
        if (keys[i] === columnId) {
            newInsideElementIndex = i;
            break;
        }
    }
    return newInsideElementIndex;
}

function updateLinesPaths(draft, linesIds) {
    linesIds.forEach((lineId) => {
        const line = draft.lines[lineId];
        const {
            value: {
                fromElementId,
                fromInsideElementId,
                fromRelationType,
                toElementId,
                toInsideElementId,
                toRelationType,
            },
        } = line;

        const fromElement = draft.elements[fromElementId];
        const toElement = draft.elements[toElementId];

        if (!fromElement || !toElement) {
            return;
        }
        const fromInsideElement =
            fromElement.value.columns[fromInsideElementId];
        const toInsideElement = toElement.value.columns[toInsideElementId];

        if (!fromInsideElement || !toInsideElement) {
            return;
        }

        const lineData = {
            mouseX: null,
            mouseY: null,

            fromElementType: fromElement.type,
            fromElementWidth: fromElement.width,
            fromElementX: fromElement.x,
            fromElementY: fromElement.y,
            fromInsideElementIndex: getIndexNumber(
                fromElement.value.columns,
                fromInsideElementId
            ),
            fromRelationType,

            toElementType: toElement.type,
            toElementWidth: toElement.width,
            toElementX: toElement.x,
            toElementY: toElement.y,
            toInsideElementIndex: getIndexNumber(
                toElement.value.columns,
                toInsideElementId
            ),
            toRelationType,
        };

        const path = _buildLinePath(lineData);

        draft.lines[lineId].value.path = path;

        if (draft.focusedLineId == lineId && draft.focusedLine?.id == lineId) {
            draft.focusedLine.value.path = path;
        }
    });

    return draft;
}

function saveHistoryValue(state, draft, historyValue) {
    const isHistoryPointerOnLastHistoryItem =
        state.historyPointer == state.history.length - 1;
    // draft.history = (state.historyPointer == state.history.length - 1)
    draft.history = isHistoryPointerOnLastHistoryItem
        ? [...state.history, historyValue]
        : [...state.history.slice(0, state.historyPointer + 1), historyValue];

    draft.historyPointer = state.historyPointer + 1;
    draft.historyIsUndoAvailable = true;
    draft.historyIsRedoAvailable = false;

    return draft;
}

const diagramReducer = (state = initialState, action) => {
    if (state.isStoredState) {
        state = { ...initialState, ...state, isStoredState: false };
    }

    return produce(state, (draft) => {
        switch (action.type) {
            case diagramActionTypes.PRELOAD_STATE: {
                draft.scale = action.scale;
                draft.originXY = action.originXY;

                draft.elements = action.elements;

                draft.elementsLive = action.elements;
                draft.elementsLastCommited = action.elements;
                // draft.canvasLimits = action.canvasLimits;

                draft.lines = action.lines;
                draft.areas = action.areas;

                break;
            }
            case diagramActionTypes.CHANGE_THEME: {
                if (action.payload.theme) {
                    draft.themeMode = action.payload.theme === 'light';
                } else {
                    draft.themeMode = !state.themeMode;
                }

                break;
            }
            case diagramActionTypes.CHANGE_CURSOR: {
                draft.isCursorHand = !state.isCursorHand;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                break;
            }

            // ------------------------------------------------------------------

            case diagramActionTypes.UPDATE_FOCUSED_GROUP: {
                let focusedGroupElements = {};
                let focusedGroupAreas = {};
                action.payload.elementsIds.forEach((elementId) => {
                    focusedGroupElements[elementId] = state.elements[elementId];
                });
                action.payload.areasIds.forEach((areaId) => {
                    focusedGroupAreas[areaId] = state.areas[areaId];
                });

                // 1. update focused inside element
                draft.focusedInsideElement = null;
                // 2. update focused elements
                draft.focusedElements = {};
                // 3. update focused elements ids
                draft.focusedElementsIds = [];

                // 4. update focused line
                draft.focusedLineId = null;
                draft.focusedLine = null;

                // ? when you create new area and move mouse too fast => add new area + unfocus new area
                draft.focusedAreaId = null;
                draft.focusedArea = null;


                // . update focused group elements
                draft.focusedGroupElements = focusedGroupElements;
                // . update focused group elements ids
                draft.focusedGroupElementsIds = action.payload.elementsIds;

                // . update focused group areas
                draft.focusedGroupAreas = focusedGroupAreas;
                // . update focused group areas ids
                draft.focusedGroupAreasIds = action.payload.areasIds;

                break;
            }

            case diagramActionTypes.UPDATE_FOCUSED_ELEMENTS: {
                let focusedElements = {};
                action.payload.forEach((elementId) => {
                    focusedElements[elementId] = state.elements[elementId];
                });

                // 1. update focused inside element
                draft.focusedInsideElement = null;

                // 2. update focused elements
                draft.focusedElements = focusedElements;

                // 3. update focused elements ids
                draft.focusedElementsIds = action.payload;

                // 4. update focused line
                draft.focusedLineId = null;
                draft.focusedLine = null;

                draft.focusedAreaId = null;
                draft.focusedArea = null;

                break;
            }
            case diagramActionTypes.UPDATE_FOCUSED_LINE: {
                // 1. update focused line
                draft.focusedLineId = action.payload ? action.payload : null;
                draft.focusedLine = action.payload
                    ? state.lines[action.payload]
                    : null;

                // 2. update focused inside element
                draft.focusedInsideElement = null;

                // 3. update focused elements
                draft.focusedElements = [];

                // 4. update focused elements ids
                draft.focusedElementsIds = [];

                draft.focusedAreaId = null;
                draft.focusedArea = null;

                break;
            }
            case diagramActionTypes.UPDATE_FOCUSED_AREA: {
                // 1. update focused area
                draft.focusedAreaId = action.payload ? action.payload : null;
                draft.focusedArea = action.payload
                    ? state.areas[action.payload]
                    : null;

                // 2. update focused inside element
                draft.focusedInsideElement = null;

                // 3. update focused elements
                draft.focusedElements = [];

                // 4. update focused elements ids
                draft.focusedElementsIds = [];

                // 5. update focused line
                draft.focusedLineId = null;
                draft.focusedLine = null;

                break;
            }

            case diagramActionTypes.UPDATE_ELEMENTS_POSITIONS: {
                let historyValue = [];

                for (const key in action.payload.updatedElements) {
                    const oldElement = state.elements[key];
                    const data = action.payload.updatedElements[key];

                    let newElement = {
                        ...oldElement,
                        x: data.x || oldElement.x || 0,
                        y: data.y || oldElement.y || 0,

                        translateX: data.translateX || 0,
                        translateY: data.translateY || 0,
                        width: data.width || oldElement.width || 306,
                        height: data.height || oldElement.height || 200,
                    };

                    // if (newElement.type === 'pen-segment') {
                    //     newElement.translateX =
                    // }

                    // 1. update elements
                    draft.elements[key] = newElement;

                    // 2. update focused elements
                    if (draft.focusedElements[key]) {
                        draft.focusedElements[key] = newElement;
                    }

                    // ----------------------------------------------------------------
                    // HISTORY: GET VALUE
                    if (newElement.type === 'pen-segment') {
                        historyValue.push({
                            elementId: oldElement.id,
                            action: HISTORY.MOVE_PEN_SEGMENT,
                            prevValue: {
                                x: oldElement.x,
                                y: oldElement.y,
                                translateX: oldElement.translateX,
                                translateY: oldElement.translateY,
                            },
                            nextValue: {
                                x: newElement.x,
                                y: newElement.y,
                                translateX: newElement.translateX,
                                translateY: newElement.translateY,
                            },
                        });
                    } else {
                        historyValue.push({
                            elementId: oldElement.id,
                            action: HISTORY.MOVE_ELEMENT,
                            prevValue: {
                                x: oldElement.x,
                                y: oldElement.y,

                                width: oldElement.width,
                                height: oldElement.height,
                            },
                            nextValue: {
                                x: newElement.x,
                                y: newElement.y,

                                width: data.width || oldElement.width || 306,
                                height: data.height || oldElement.height || 200,
                            },
                        });
                    }
                    // ----------------------------------------------------------------
                }

                for (const key in action.payload.updatedLines) {
                    const oldLine = state.lines[key];
                    const newLine = action.payload.updatedLines[key];

                    // 3. update lines
                    draft.lines[key] = {
                        ...oldLine,
                        value: {
                            ...oldLine.value,

                            fromElementX:
                                draft.elements[oldLine.value.fromElementId]
                                    ?.x || oldLine.value.fromElementX,
                            fromElementY:
                                draft.elements[oldLine.value.fromElementId]
                                    ?.y || oldLine.value.fromElementY,
                            toElementX:
                                draft.elements[oldLine.value.toElementId]?.x ||
                                oldLine.value.toElementX,
                            toElementY:
                                draft.elements[oldLine.value.toElementId]?.y ||
                                oldLine.value.toElementY,
                            path: newLine.value.path,
                        },
                    };
                }

                // -----------------------------------
                // UPDATE LINES POSITIONS FROM/TO THE TABLE
                const linesIds = [];
                const elementsIds = Object.keys(action.payload.updatedElements);

                // find lines from/to the table
                for (const key in draft.lines) {
                    const line = draft.lines[key];
                    const {
                        value: { fromElementId, toElementId },
                    } = line;

                    if (
                        elementsIds.includes(fromElementId) ||
                        elementsIds.includes(toElementId)
                    ) {
                        linesIds.push(line.id);
                    }
                }

                // update lines (need for update lines by updated elements positions)
                draft = updateLinesPaths(draft, linesIds);
                // -----------------------------------

                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                draft.isContentChanged = true;
                break;
            }

            // HISTORY - add history
            case diagramActionTypes.UPDATE_AREAS_POSITIONS: {
                let historyValue = [];

                for (const key in action.payload.updatedAreas) {
                    const oldArea = draft.areas[key];
                    const data = action.payload.updatedAreas[key];

                    let newArea = {
                        ...oldArea,
                        x: data.x || 0,
                        y: data.y || 0,

                        width: data.width || oldArea.width,
                        height: data.height || oldArea.height,

                        value: {
                            ...oldArea.value,
                            nestedElementsIds: data.nestedElementsIds || oldArea.nestedElementsIds,
                        }
                    };
                    // newArea.value.nestedElementsIds = data.nestedElementsIds || oldArea.nestedElementsIds;

                    historyValue.push({
                        areaId: oldArea.id,
                        action: HISTORY.MOVE_AREA,
                        prevValue: {
                            x: oldArea.x,
                            y: oldArea.y,

                            width: oldArea.width,
                            height: oldArea.height,
                            nestedElementsIds: oldArea.nestedElementsIds,
                        },
                        nextValue: {
                            x: newArea.x,
                            y: newArea.y,

                            width: data.width || oldArea.width,
                            height: data.height || oldArea.height,
                            nestedElementsIds: data.nestedElementsIds || oldArea.nestedElementsIds,
                        },
                    });

                    // 1. update areas
                    draft.areas[key] = newArea;

                    // 2. update focused areas
                    if (draft.focusedArea?.id == newArea.id) {
                        draft.focusedArea = newArea;
                    }
                }

                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.UPDATE_LIVE_ELEMENTS_POSITIONS: {
                for (const key in action.payload) {
                    const oldElement = state.elementsLive[key];
                    const data = action.payload[key];

                    // 1. update elements live
                    draft.elementsLive[key] = {
                        ...oldElement,
                        x: data.x || 0,
                        y: data.y || 0,
                        width: data.width || oldElement.width || 306,
                        height: data.height || oldElement.height || 200,
                    };
                }
                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.ADD_NEW_ELEMENT: {
                if (action.payload.type === 'table') {
                    if (!checkTables(draft.elements)) {
                        draft.isModalSignInRestrictEnabled = true;
                        draft.modalSignInRestrictType = 'tables';
                        break;
                    }
                } else if (action.payload.type === 'note') {
                    if (!checkNotes(draft.elements)) {
                        draft.isModalSignInRestrictEnabled = true;
                        draft.modalSignInRestrictType = 'notes';
                        break;
                    }
                } else if (action.payload.type === 'pen-segment') {
                    if (!checkPens(draft.elements)) {
                        draft.isModalSignInRestrictEnabled = true;
                        draft.modalSignInRestrictType = 'pens';
                        break;
                    }
                }

                let newElement = action.payload;

                // check area id
                const areaId = getNewAreaIdForElement(draft.areas, newElement);
                if (areaId) {
                    newElement.value.areaId = areaId;
                    draft.areas[areaId].value.nestedElementsIds.push(newElement.id);
                }



                // 1. update elements
                draft.elements[action.payload.id] = newElement;

                // 2. update elements live
                draft.elementsLive[action.payload.id] = newElement;

                // 3. update focused elements
                draft.focusedElements = { [action.payload.id]: newElement };

                // 4. update focused elements ids
                draft.focusedElementsIds = [action.payload.id];

                if (action.payload.type === 'table') {
                    // const firstColumnKey = Object.keys(draft.elements[action.payload.id].value.columns)[0];

                    // 5. update focused inside element
                    // draft.focusedInsideElement = {
                    //     elementId: action.payload.id,
                    //     insideElementId: firstColumnKey,
                    //     insideElement: draft.elements[action.payload.id].value.columns[firstColumnKey],
                    // };

                    // const newInsideElement = draft.elements[action.payload.id].value.columns[firstColumnKey];

                    // TODO: use just indexNumber
                    // const newInsideElementIndex = draft.elements[action.payload.id].value.columns[firstColumnKey].indexNumber;

                    // let newInsideElementIndex = 0;
                    // const keys = Object.keys(draft.elements[action.payload.id].value.columns);

                    // for (let i = 0; i < keys.length; i++) {
                    //     if (keys[i] === firstColumnKey) {
                    //         newInsideElementIndex = i;
                    //         break;
                    //     }
                    // }

                    // draft.focusedInsideElement = {
                    //     elementId: action.payload.id,
                    //     insideElementId: firstColumnKey,
                    //     insideElement: newInsideElement,
                    //     insideElementIndex: newInsideElementIndex,
                    // };

                    draft.focusedInsideElement = {
                        elementId: action.payload.id,
                        insideElementId: null,
                        insideElement:
                            draft.elements[action.payload.id].value.header,
                        insideElementIndex: null,
                        insideElementType: 'header',
                    };
                } else {
                    draft.focusedInsideElement = null;
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.id,
                        action: HISTORY.ADD_ELEMENT,
                        prevValue: {
                            element: null,
                        },
                        nextValue: {
                            element: newElement,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                draft.isContentChanged = true;
                break;
            }
            case diagramActionTypes.ADD_NEW_LINE: {
                // 1. update lines
                draft.lines[action.payload.id] = action.payload;

                const {
                    value: {
                        fromElementId,
                        fromInsideElementId,
                        toElementId,
                        toInsideElementId,
                    },
                } = action.payload;
                draft.elements[fromElementId].value.columns[
                    fromInsideElementId
                ].isRelated = true;
                draft.elements[toElementId].value.columns[
                    toInsideElementId
                ].isRelated = true;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                draft.focusedLineId = action.payload.id;
                draft.focusedLine = draft.lines[action.payload.id];

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        lineId: action.payload.id,
                        action: HISTORY.ADD_LINE,
                        prevValue: {
                            line: null,
                        },
                        nextValue: {
                            line: action.payload,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isBtnNewLineEnabled = false;
                draft.isCursorHand = false;

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.ADD_NEW_AREA: {
                if (!checkAreas(draft.areas)) {
                    draft.isModalSignInRestrictEnabled = true;
                    draft.modalSignInRestrictType = 'areas';
                    break;
                }

                // 1. update areas
                draft.areas[action.payload.id] = action.payload;

                // 2. update nested elements
                const nestedElementsIds =
                    action.payload.value.nestedElementsIds;
                nestedElementsIds.forEach((elementId) => {
                    if (state.elements[elementId]) {
                        draft.elements[elementId].value.areaId =
                            action.payload.id;
                    }
                });

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                draft.focusedAreaId = action.payload.id;
                draft.focusedArea = draft.areas[action.payload.id];

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        areaId: action.payload.id,
                        action: HISTORY.ADD_AREA,
                        prevValue: {
                            area: null,
                        },
                        nextValue: {
                            area: action.payload,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isBtnNewAreaEnabled = false;
                draft.isCursorHand = false;

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_NEW_AREA_ID_FOR_ELEMENT: {
                // action.payload.areaId can be 'null'

                if (action.payload.areaId) {
                    // 1.  if element.value.areaId is not null, remove nestedElementId from area
                    // 2. set element's areaId to new areaId
                    // 3. set newArea's nestedElementsIds with new element.id

                    // 1. if element.value.areaId is not null, remove nestedElementId from area
                    const prevAreaId =
                        state.elements[action.payload.elementId].value.areaId;
                    if (prevAreaId) {
                        const newPrevAreaNestedElementsIds = state.areas[
                            prevAreaId
                        ].value.nestedElementsIds.filter(
                            (id) => id !== action.payload.elementId
                        );
                        draft.areas[prevAreaId].value.nestedElementsIds = [
                            ...new Set(newPrevAreaNestedElementsIds),
                        ];
                    }

                    // 2. set element's areaId to new areaId
                    draft.elements[action.payload.elementId].value.areaId =
                        action.payload.areaId;

                    // 3. set newArea's nestedElementsIds with new element.id
                    const prevNewAreaNestedElementsId =
                        state.areas[action.payload.areaId].value
                            .nestedElementsIds;
                    draft.areas[action.payload.areaId].value.nestedElementsIds =
                        [
                            ...new Set([
                                ...prevNewAreaNestedElementsId,
                                action.payload.elementId,
                            ]),
                        ];
                } else {
                    // 1.  if element.value.areaId is not null, remove nestedElementId from area
                    // 2. set element's areaId to new areaId (null)

                    // 1. if element.value.areaId is not null, remove nestedElementId from area
                    const prevAreaId =
                        state.elements[action.payload.elementId].value.areaId;
                    if (prevAreaId) {
                        const newPrevAreaNestedElementsIds = state.areas[
                            prevAreaId
                        ].value.nestedElementsIds.filter(
                            (id) => id !== action.payload.elementId
                        );
                        draft.areas[prevAreaId].value.nestedElementsIds = [
                            ...new Set(newPrevAreaNestedElementsIds),
                        ];
                    }

                    // 2. set element's areaId to new areaId (null)
                    draft.elements[action.payload.elementId].value.areaId =
                        action.payload.areaId;

                    // // 3. set newArea's nestedElementsIds with new element.id
                    // if (action.payload.areaId) {
                    //     const prevNewAreaNestedElementsId = state.areas[action.payload.areaId].value.nestedElementsIds;
                    //     draft.areas[action.payload.areaId].value.nestedElementsIds = [...prevNewAreaNestedElementsId, action.payload.elementId];
                    // }
                }

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.ADD_NEW_AREA_FOR_FOCUSED_ELEMENTS: {
                if (!checkAreas(draft.areas)) {
                    draft.isModalSignInRestrictEnabled = true;
                    draft.modalSignInRestrictType = 'areas';
                    break;
                }

                // get focused elements mesures
                // create new area
                // nested Elements = focused Elements
                // add areaId for all focused elements

                if (state.focusedElementsIds.length > 0) {
                    const firstElementId = state.focusedElementsIds[0];
                    const firstElement = state.elements[firstElementId];

                    let minX = firstElement.x;
                    let maxX = firstElement.x + firstElement.width;
                    let minY = firstElement.y;
                    let maxY = firstElement.y + firstElement.height;

                    state.focusedElementsIds.forEach((elementId) => {
                        const element = state.elements[elementId];
                        const { x, y, width, height } = element;

                        minX = Math.min(minX, x);
                        maxX = Math.max(maxX, x + width);
                        minY = Math.min(minY, y);
                        maxY = Math.max(maxY, y + height);
                    });

                    const distance = 100;
                    minX -= distance;
                    maxX += distance;
                    minY -= distance;
                    maxY += distance;

                    const newAreaId = nanoid();
                    const newArea = {
                        id: newAreaId,
                        type: 'area',
                        value: {
                            // border: 'dashes',
                            border: 'solid',
                            color: 'blue',
                            name: 'area_new',
                            nestedElementsIds: state.focusedElementsIds,
                        },
                        x: minX,
                        y: minY,
                        width: maxX - minX,
                        height: maxY - minY,
                    };

                    // 0. remove focusedElements from prev areas newstedElements
                    state.focusedElementsIds.forEach((elementId) => {
                        const element = state.elements[elementId];
                        const areaId = element.value.areaId;

                        if (areaId) {
                            const newNestedElementsIds = draft.areas[areaId].value.nestedElementsIds.filter(
                                (id) => id !== elementId
                            );
                            draft.areas[areaId].value.nestedElementsIds = [
                                ...new Set(newNestedElementsIds),
                            ];
                        }
                    });

                    // 1. update areas
                    draft.areas[newAreaId] = newArea;

                    // 2. update nested(focused) elements
                    state.focusedElementsIds.forEach((elementId) => {
                        if (state.elements[elementId]) {
                            draft.elements[elementId].value.areaId = newAreaId;
                        }
                    });

                    // UNFOCUS FOCUSED ITEMS
                    draft = unfocusFocusedItems(state, draft);

                    draft.focusedAreaId = newAreaId;
                    draft.focusedArea = newArea;

                    // ----------------------------------------------------------------
                    // HISTORY: GET VALUE
                    const historyValue = [
                        {
                            areaId: newAreaId,
                            action: HISTORY.ADD_AREA,
                            prevValue: {
                                area: null,
                            },
                            nextValue: {
                                area: newArea,
                            },
                        },
                    ];
                    // ----------------------------------------------------------------
                    // HISTORY: SAVE VALUE
                    saveHistoryValue(state, draft, historyValue);
                    // ----------------------------------------------------------------

                    // CANVAS LIMITS
                    draft = calculateCanvasLimits(state, draft);

                    draft.isContentChanged = true;
                }

                break;
            }

            case diagramActionTypes.ADD_NEW_PEN_SEGMENT: {
                if (!checkPens(draft.elements)) {
                    draft.isModalSignInRestrictEnabled = true;
                    draft.modalSignInRestrictType = 'pens';
                    break;
                }

                const newSegment = {
                    ...action.payload,
                    newPenId: state.newPen.id,
                };

                // check area id
                const areaId = getNewAreaIdForElement(draft.areas, newSegment);
                if (areaId) {
                    newSegment.value.areaId = areaId;
                    draft.areas[areaId].value.nestedElementsIds.push(newSegment.id);
                }

                // 1. update elements
                draft.elements[newSegment.id] = newSegment;

                // 2. update elements live
                draft.elementsLive[newSegment.id] = newSegment;

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: newSegment.id,
                        action: HISTORY.ADD_ELEMENT,
                        prevValue: {
                            element: null,
                        },
                        nextValue: {
                            element: newSegment,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                draft.isContentChanged = true;
                break;
            }

            // 1. CONTROLS LEFT | NEW TABLE CLICK
            case diagramActionTypes.ENABLE_BTN_NEW_TABLE_CLICK: {
                draft.isBtnNewTableClickEnabled =
                    !state.isBtnNewTableClickEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnNewTableClickEnabled'
                );

                break;
            }
            case diagramActionTypes.DISABLE_BTN_NEW_TABLE_CLICK: {
                draft.isBtnNewTableClickEnabled = false;
                draft.isCursorHand = false;
                break;
            }

            // 2. CONTROLS LEFT | NEW TABLE GRAB
            case diagramActionTypes.ENABLE_BTN_NEW_TABLE_GRAB: {
                draft.isBtnNewTableGrabEnabled = true;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnNewTableGrabEnabled'
                );
                break;
            }
            case diagramActionTypes.DISABLE_BTN_NEW_TABLE_GRAB: {
                draft.isBtnNewTableGrabEnabled = false;
                draft.isCursorHand = false;

                break;
            }

            // 3. CONTROLS LEFT | NEW NOTE CLICK
            case diagramActionTypes.ENABLE_BTN_NEW_NOTE_CLICK: {
                draft.isBtnNewNoteClickEnabled =
                    !state.isBtnNewNoteClickEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnNewNoteClickEnabled'
                );

                break;
            }
            case diagramActionTypes.DISABLE_BTN_NEW_NOTE_CLICK: {
                draft.isBtnNewNoteClickEnabled = false;
                draft.isCursorHand = false;

                break;
            }

            // 4. CONTROLS LEFT | NEW NOTE GRAB
            case diagramActionTypes.ENABLE_BTN_NEW_NOTE_GRAB: {
                draft.isBtnNewNoteGrabEnabled = true;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnNewNoteGrabEnabled'
                );

                break;
            }
            case diagramActionTypes.DISABLE_BTN_NEW_NOTE_GRAB: {
                draft.isBtnNewNoteGrabEnabled = false;
                draft.isCursorHand = false;

                break;
            }

            // 5. CONTROLS LEFT | NEW LINE
            case diagramActionTypes.TOGGLE_BTN_NEW_LINE: {
                draft.isBtnNewLineEnabled = !state.isBtnNewLineEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnNewLineEnabled'
                );

                break;
            }

            case diagramActionTypes.DISABLE_BTN_NEW_AREA: {
                draft.isBtnNewAreaEnabled = false;
                draft.isCursorHand = false;
                break;
            }

            case diagramActionTypes.TOGGLE_BTN_NEW_AREA: {
                if (
                    action.payload.isKeyClick &&
                    state.focusedElementsIds.length
                ) {
                    // get focused elements mesures
                    // create new area
                    // nested Elements = focused Elements
                    // add areaId for all focused elements

                    if (state.focusedElementsIds.length > 0) {
                        if (!checkAreas(draft.areas)) {
                            draft.isModalSignInRestrictEnabled = true;
                            draft.modalSignInRestrictType = 'areas';
                            break;
                        }


                        const firstElementId = state.focusedElementsIds[0];
                        const firstElement = state.elements[firstElementId];

                        let minX = firstElement.x;
                        let maxX = firstElement.x + firstElement.width;
                        let minY = firstElement.y;
                        let maxY = firstElement.y + firstElement.height;

                        state.focusedElementsIds.forEach((elementId) => {
                            const element = state.elements[elementId];
                            const { x, y, width, height } = element;

                            minX = Math.min(minX, x);
                            maxX = Math.max(maxX, x + width);
                            minY = Math.min(minY, y);
                            maxY = Math.max(maxY, y + height);
                        });

                        const distance = 100;
                        minX -= distance;
                        maxX += distance;
                        minY -= distance;
                        maxY += distance;

                        const newAreaId = nanoid();
                        const newArea = {
                            id: newAreaId,
                            type: 'area',
                            value: {
                                // border: 'dashes',
                                border: 'solid',
                                color: 'blue',
                                name: 'area_new',
                                nestedElementsIds: state.focusedElementsIds,
                            },
                            x: minX,
                            y: minY,
                            width: maxX - minX,
                            height: maxY - minY,
                        };

                        // 0. remove focusedElements from prev areas newstedElements
                        state.focusedElementsIds.forEach((elementId) => {
                            const element = state.elements[elementId];
                            const areaId = element.value.areaId;

                            if (areaId) {
                                // TODO: ERROR (when delete something like table, and add area for this table)
                                const newNestedElementsIds = draft.areas[
                                    areaId
                                ].value.nestedElementsIds.filter(
                                    (id) => id !== elementId
                                );
                                draft.areas[areaId].value.nestedElementsIds = [
                                    ...new Set(newNestedElementsIds),
                                ];
                            }
                        });

                        // 1. update areas
                        draft.areas[newAreaId] = newArea;

                        // 2. update nested(focused) elements
                        state.focusedElementsIds.forEach((elementId) => {
                            if (state.elements[elementId]) {
                                draft.elements[elementId].value.areaId =
                                    newAreaId;
                            }
                        });

                        // UNFOCUS FOCUSED ITEMS
                        draft = unfocusFocusedItems(state, draft);

                        draft.focusedAreaId = newAreaId;
                        draft.focusedArea = newArea;

                        draft.isContentChanged = true;

                        // ----------------------------------------------------------------
                        // HISTORY: GET VALUE
                        const historyValue = [
                            {
                                areaId: newAreaId,
                                action: HISTORY.ADD_AREA,
                                prevValue: {
                                    area: null,
                                },
                                nextValue: {
                                    area: newArea,
                                },
                            },
                        ];
                        // ----------------------------------------------------------------
                        // HISTORY: SAVE VALUE
                        saveHistoryValue(state, draft, historyValue);
                        // ----------------------------------------------------------------
                    }
                } else {
                    draft.isBtnNewAreaEnabled = !state.isBtnNewAreaEnabled;

                    // UNFOCUS FOCUSED ITEMS
                    draft = unfocusFocusedItems(state, draft);

                    // UNFOCUS ENABLED CONTROLS
                    draft = unfocusEnabledControls(
                        state,
                        draft,
                        'isBtnNewAreaEnabled'
                    );
                }

                break;
            }

            case diagramActionTypes.TOGGLE_BTN_PEN: {
                draft.isBtnPenEnabled = !state.isBtnPenEnabled;

                // update newPen.id
                if (draft.isBtnPenEnabled) {
                    draft.newPen.id = nanoid();
                    draft.newPen.color = 'yellow';
                }

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnPenEnabled');
                break;
            }

            case diagramActionTypes.SET_ELEMENTS_VIEW_MODE: {
                draft.viewMode = action.payload.viewMode;
                draft.isBtnViewModesEnabled = false;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnViewModesEnabled',
                    'isBtnExportImageEnabled'
                );

                break;
            }

            // 7. CONTROLS TOP | EXPORT
            case diagramActionTypes.TOGGLE_BTN_EXPORT: {
                draft.isBtnExportEnabled = !state.isBtnExportEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnExportEnabled'
                );

                break;
            }
            // 7. CONTROLS TOP | EXPORT
            case diagramActionTypes.DISABLE_BTN_EXPORT: {
                draft.isBtnExportEnabled = false;
                break;
            }

            // 7. CONTROLS TOP | EXPORT IMAGE
            case diagramActionTypes.TOGGLE_BTN_EXPORT_IMAGE: {
                draft.isBtnExportImageEnabled = !state.isBtnExportImageEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnExportImageEnabled'
                );
                break;
            }
            // 7. CONTROLS TOP | EXPORT IMAGE
            case diagramActionTypes.DISABLE_BTN_EXPORT_IMAGE: {
                draft.isBtnExportImageEnabled = false;
                draft.isBtnExportEnabled = false;
                break;
            }

            // 7. CONTROLS TOP | EXPORT CODE
            case diagramActionTypes.TOGGLE_BTN_EXPORT_CODE: {
                draft.isBtnExportCodeEnabled = !state.isBtnExportCodeEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnExportCodeEnabled'
                );

                // if (draft.isBtnExportCodeEnabled) {
                draft.isBtnExportEnabled = true; // the same as 'isBtnExportCodeEnabled'
                // }

                break;
            }
            // 7. CONTROLS TOP | EXPORT CODE
            case diagramActionTypes.DISABLE_BTN_EXPORT_CODE: {
                draft.isBtnExportCodeEnabled = false;
                draft.isBtnExportEnabled = false;
                break;
            }

            // 7. CONTROLS TOP | VIEW MODES
            case diagramActionTypes.TOGGLE_BTN_VIEW_MODES: {
                draft.isBtnViewModesEnabled = !state.isBtnViewModesEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnViewModesEnabled',
                    'isBtnExportImageEnabled'
                );

                break;
            }

            case diagramActionTypes.TOGGLE_BTN_SQL_SYNTAX: {
                draft.isBtnSqlSyntaxEnabled = !state.isBtnSqlSyntaxEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnSqlSyntaxEnabled');
                break;
            }

            case diagramActionTypes.SET_SQL_SYNTAX: {
                // draft.sqlSyntax = action.payload.sqlSyntax;
                draft.isBtnSqlSyntaxEnabled = false;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnSqlSyntaxEnabled');

                break;
            }


            // 7. CONTROLS TOP | VIEW MODES
            case diagramActionTypes.DISABLE_BTN_VIEW_MODES: {
                draft.isBtnViewModesEnabled = false;
                break;
            }

            // 7. CONTROLS TOP | SEARCH
            case diagramActionTypes.TOGGLE_BTN_SEARCH: {
                draft.isBtnSearchEnabled = !state.isBtnSearchEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnSearchEnabled'
                );

                break;
            }

            case diagramActionTypes.TOGGLE_BTN_SETTINGS: {
                draft.isBtnSettingsEnabled = !state.isBtnSettingsEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnSettingsEnabled');
                break;
            }
            case diagramActionTypes.TOGGLE_BTN_MODAL_SHORTCUTS: {
                draft.isBtnModalShortcutsEnabled = !state.isBtnModalShortcutsEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnModalShortcutsEnabled');
                break;
            }


            case diagramActionTypes.TOGGLE_BTN_PROFILE: {
                draft.isBtnProfileEnabled = !state.isBtnProfileEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnProfileEnabled');
                break;
            }





            // COPY OF CLICK_ESCAPE (IN CANVAS) || MOVE_TO_MAIN || UNFOCUS ENABLED CONTROLS
            case diagramActionTypes.CANVAS_STARTED: {
                // custom logic for focused inside element and focused element
                // FOCUSED ELEMENTS
                if (state.focusedLine || state.focusedLineId) {
                    draft.focusedLine = null;
                    draft.focusedLineId = null;
                }
                if (state.focusedArea || state.focusedAreaId) {
                    draft.focusedArea = null;
                    draft.focusedAreaId = null;
                }
                if (state.focusedInsideElement) {
                    draft.focusedInsideElement = null;
                } else if (state.focusedElementsIds?.length) {
                    draft.focusedElements = {};
                    draft.focusedElementsIds = [];
                }

                // focused group elements and areas
                if (state.focusedGroupElementsIds?.length) {
                    draft.focusedGroupElements = {};
                    draft.focusedGroupElementsIds = [];
                }
                if (state.focusedGroupAreasIds?.length) {
                    draft.focusedGroupAreas = {};
                    draft.focusedGroupAreasIds = [];
                }

                if (draft.isHistoryClicked) {
                    draft.isHistoryClicked = false;
                }
                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isCursorHand');

                break;
            }

            // 8. CONTROLS TOP | LEFT PANEL
            case diagramActionTypes.TOGGLE_BTN_LEFT_PANEL: {
                draft.isBtnLeftPanelEnabled = !state.isBtnLeftPanelEnabled;
                break;
            }

            // 9. CONTROLS POPUP | NEWS
            case diagramActionTypes.DISABLE_BTN_POPUP_NEWS: {
                draft.isBtnPopupNewsEnabled = false;

                break;
            }

            // 9. CONTROLS BOTTOM | MINIMAP
            case diagramActionTypes.TOGGLE_BTN_MINIMAP: {
                draft.isBtnMinimapEnabled = !state.isBtnMinimapEnabled;

                break;
            }
            // 9. CONTROLS BOTTOM | INFORMATION & RESOURCES
            case diagramActionTypes.TOGGLE_BTN_INFO_RESOURCES: {
                draft.isBtnInfoResourcesEnabled =
                    !state.isBtnInfoResourcesEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnInfoResourcesEnabled'
                );

                break;
            }

            case diagramActionTypes.DELETE_FOCUSED_AREA: {
                if (state.focusedAreaId) {
                    // 0. make focused all nested elements
                    let focusedElements = {};

                    // TODO: ERROR
                    // Uncaught TypeError: Cannot read properties of undefined (reading 'forEach')
                    state.focusedArea.value.nestedElementsIds.forEach(
                        (elementId) => {
                            focusedElements[elementId] =
                                state.elements[elementId];
                        }
                    );
                    draft.focusedElements = focusedElements;
                    draft.focusedElementsIds =
                        state.focusedArea.value.nestedElementsIds;

                    // 1. remove nested elements areas
                    const nestedElementsIds =
                        state.focusedArea.value.nestedElementsIds;
                    nestedElementsIds.forEach((elementId) => {
                        if (draft.elements[elementId]?.value?.areaId) {
                            draft.elements[elementId].value.areaId = null;
                        }
                    });

                    // 2. delete area from areas
                    delete draft.areas[state.focusedAreaId];

                    // UNFOCUS FOCUSED ITEMS
                    draft = unfocusFocusedItems(state, draft);

                    // ----------------------------------------------------------------
                    // HISTORY: GET VALUE
                    const historyValue = [
                        {
                            areaId: state.focusedAreaId,
                            action: HISTORY.DELETE_AREA,
                            prevValue: {
                                area: state.areas[state.focusedAreaId],
                            },
                            nextValue: {
                                area: null,
                            },
                        },
                    ];
                    // ----------------------------------------------------------------
                    // HISTORY: SAVE VALUE
                    saveHistoryValue(state, draft, historyValue);
                    // ----------------------------------------------------------------

                    // CANVAS LIMITS
                    draft = calculateCanvasLimits(state, draft);
                }

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.DELETE_FOCUSED_GROUP: {
                let prevAreasForHistory = {};
                let prevElementsForHistory = {};
                let prevRelatedLinesForHistory = {};

                if (state.focusedGroupElementsIds?.length) {
                    state.focusedGroupElementsIds.forEach((elementId) => {
                        if (draft.elements[elementId]) {
                            switch (draft.elements[elementId].type) {
                                case 'table': {
                                    // 0. delete lines
                                    for (const key in state.lines) {
                                        const line = state.lines[key];
                                        const {
                                            id,
                                            value: {
                                                fromElementId,
                                                fromInsideElementId,
                                                toElementId,
                                                toInsideElementId,
                                            },
                                        } = line;

                                        if ([fromElementId, toElementId].includes(elementId)) {
                                            if (draft.elements[fromElementId]) {
                                                draft.elements[fromElementId].value.columns[fromInsideElementId].isRelated = false;
                                            }
                                            if (draft.elements[toElementId]) {
                                                draft.elements[toElementId].value.columns[toInsideElementId].isRelated = false;
                                            }

                                            prevRelatedLinesForHistory[id] = state.lines[id];
                                            delete draft.lines[id];
                                        }
                                    }
                                    break;
                                }
                                default:
                                    break;
                            }

                            // // ----------------------------------------------------------------
                            // // ========== HISTORY SECTION

                            // 0. GET ELEMENTS for history value
                            prevElementsForHistory[elementId] = draft.elements[elementId];
                            // ----------------------------------------------------------------

                            // remove element from area nestedElements
                            if (draft.elements[elementId].value.areaId && draft.areas[draft.elements[elementId].value.areaId]
                            ) {
                                draft.areas[draft.elements[elementId].value.areaId].value.nestedElementsIds =
                                    draft.areas[draft.elements[elementId].value.areaId].value.nestedElementsIds
                                        .filter((id) => id !== elementId);
                            }

                            // 1. delete from elements
                            delete draft.elements[elementId];
                            // 2. delete from elements live
                            // delete draft.elementsLive[elementId];
                        }
                    });
                }

                if (state.focusedGroupAreasIds?.length) {
                    state.focusedGroupAreasIds.forEach((areaId) => {
                        if (draft.areas[areaId]) {

                            // // ----------------------------------------------------------------
                            // // ========== HISTORY SECTION

                            // 0. GET ELEMENTS for history value
                            prevAreasForHistory[areaId] = state.areas[areaId];
                            // ----------------------------------------------------------------

                            delete draft.areas[areaId];
                        }
                    });
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementsIds: state.focusedGroupElementsIds,
                        action: HISTORY.DELETE_GROUP,
                        prevValue: {
                            areas: prevAreasForHistory,
                            elements: prevElementsForHistory,
                            lines: prevRelatedLinesForHistory,
                        },
                        nextValue: {
                            areasIds: Object.keys(prevAreasForHistory),
                            elements: null,
                            linesIds: Object.keys(prevRelatedLinesForHistory),
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------


                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.DUPLICATE_FOCUSED_GROUP: {
                if (state.focusedGroupElementsIds?.length) {
                    let tablesCount = 0;
                    let notesCount = 0;
                    let pensCount = 0;

                    state.focusedGroupElementsIds.forEach((elementId) => {
                        if (state.elements[elementId]?.type === 'table') {
                            tablesCount++;
                        } else if (state.elements[elementId]?.type === 'note') {
                            notesCount++;
                        } else if (state.elements[elementId]?.type === 'pen-segment') {
                            pensCount++;
                        }
                    })

                    if (tablesCount) {
                        if (!checkTables(draft.elements, tablesCount)) {
                            draft.isModalSignInRestrictEnabled = true;
                            draft.modalSignInRestrictType = 'tables';
                            break;
                        }
                    } else if (notesCount) {
                        if (!checkNotes(draft.elements, notesCount)) {
                            draft.isModalSignInRestrictEnabled = true;
                            draft.modalSignInRestrictType = 'notes';
                            break;
                        }
                    } else if (pensCount) {
                        if (!checkPens(draft.elements, pensCount)) {
                            draft.isModalSignInRestrictEnabled = true;
                            draft.modalSignInRestrictType = 'pens';
                            break;
                        }
                    }
                }

                if (state.focusedGroupAreasIds?.length) {
                    if (!checkAreas(draft.areas, state.focusedGroupAreasIds?.length)) {
                        draft.isModalSignInRestrictEnabled = true;
                        draft.modalSignInRestrictType = 'areas';
                        break;
                    }
                }


                let oldAreasIds = [];
                let oldNewAreasIdsMap = {};

                let oldElementsIds = [];
                let oldNewElementsIdsMap = {};

                let newFocusedGroupElements = {};
                let newFocusedGroupElementsIds = [];
                let newFocusedGroupAreas = {};
                let newFocusedGroupAreasIds = [];




                // let prevAreasForHistory = {};
                // let prevElementsForHistory = {};
                // let prevRelatedLinesForHistory = {};


                // const newElementsForHistory = {};


                if (state.focusedGroupAreasIds?.length) {
                    state.focusedGroupAreasIds.forEach((areaId) => {
                        const oldArea = draft.areas[areaId];
                        if (oldArea) {
                            const newAreaId = nanoid();
                            const newArea = structuredClone(state.areas[areaId]);

                            newArea.id = newAreaId;
                            newArea.x += 100;
                            newArea.y += 100;
                            newArea.value.nestedElementsIds = [];

                            draft.areas[newAreaId] = newArea;

                            oldAreasIds.push(areaId);
                            oldNewAreasIdsMap[areaId] = newAreaId;

                            newFocusedGroupAreasIds.push(newAreaId);
                            newFocusedGroupAreas[newAreaId] = newArea;
                        }
                    });
                }


                const HEADER_LETTER_WIDTH = 12; //13
                const HEADER_LEFT_SPACE = 25;
                const HEADER_RIGHT_SPACE = 25;

                if (state.focusedGroupElementsIds?.length) {
                    state.focusedGroupElementsIds.forEach((elementId) => {
                        const oldElement = draft.elements[elementId];
                        if (oldElement) {
                            if (oldElement?.type === 'table') {
                                const newElementId = nanoid();
                                const newElement = structuredClone(state.elements[elementId]);

                                const newColumns = {};
                                for (const index in oldElement.value.columns) {
                                    const column = oldElement.value.columns[index];
                                    const newColumnId = nanoid();
                                    const newColumn = {
                                        ...column,
                                        id: newColumnId,
                                    };
                                    newColumns[newColumnId] = newColumn;
                                }

                                newElement.value.columns = newColumns;
                                newElement.id = newElementId;
                                newElement.x += 100;
                                newElement.y += 100;
                                if (newElement.value.header.name !== '') {
                                    newElement.value.header.name = newElement.value.header.name + '_copy';
                                }

                                const headerWidth = newElement.value.header.name.length * HEADER_LETTER_WIDTH + HEADER_LEFT_SPACE + HEADER_RIGHT_SPACE;
                                newElement.width = Math.max(headerWidth, newElement.width);

                                if (oldElement?.value?.areaId && oldAreasIds.includes(oldElement?.value?.areaId)) {
                                    let oldAreaId = oldElement?.value?.areaId;
                                    let newAreaId = oldNewAreasIdsMap[oldAreaId];
                                    if (newAreaId) {
                                        newElement.value.areaId = newAreaId;
                                    }
                                }

                                // 1.1. copy to elements
                                draft.elements[newElementId] = newElement;

                                oldElementsIds.push(elementId);
                                oldNewElementsIdsMap[elementId] = newElementId;

                                newFocusedGroupElementsIds.push(newElementId);
                                newFocusedGroupElements[newElementId] = newElement;


                                // // ----------------------------------------------------------------
                                // // ========== HISTORY SECTION

                                // // 0. GET ELEMENTS for history value
                                // newElementsForHistory[newElementId] = newElement;
                                // // ----------------------------------------------------------------

                            } else if (oldElement?.type === 'note') {
                                // const newElementId = nanoid();
                                // const newElement = structuredClone(
                                //     state.elements[elementId]
                                // );
                                // newElement.id = newElementId;
                                // newElement.x += 100;
                                // newElement.y += 100;

                                // // 1.2. copy to elements
                                // draft.elements[newElementId] = newElement;

                                // // // ----------------------------------------------------------------
                                // // // ========== HISTORY SECTION

                                // // // 0. GET ELEMENTS for history value
                                // // newElementsForHistory[newElementId] = newElement;
                                // // // ----------------------------------------------------------------

                            } else if (oldElement?.type === 'pen-segment') {
                                // const newElementId = nanoid();
                                // const newElement = structuredClone(state.elements[elementId]);
                                // newElement.id = newElementId;
                                // newElement.x += 100;
                                // // newElement.y += 100;
                                // newElement.translateX += 100;
                                // // newElement.translateY += 100;

                                // // 1.2. copy to elements
                                // draft.elements[newElementId] = newElement;

                                // // // ----------------------------------------------------------------
                                // // // ========== HISTORY SECTION

                                // // // 0. GET ELEMENTS for history value
                                // // newElementsForHistory[newElementId] = newElement;
                                // // // ----------------------------------------------------------------
                            }



                            // // // ----------------------------------------------------------------
                            // // // ========== HISTORY SECTION

                            // // 0. GET ELEMENTS for history value
                            // prevElementsForHistory[elementId] = draft.elements[elementId];
                            // // ----------------------------------------------------------------

                            // // remove element from area nestedElements
                            // if (draft.elements[elementId].value.areaId && draft.areas[draft.elements[elementId].value.areaId]
                            // ) {
                            //     draft.areas[draft.elements[elementId].value.areaId].value.nestedElementsIds =
                            //         draft.areas[draft.elements[elementId].value.areaId].value.nestedElementsIds
                            //             .filter((id) => id !== elementId);
                            // }

                            // 1. delete from elements
                            // delete draft.elements[elementId];
                            // 2. delete from elements live
                            // delete draft.elementsLive[elementId];
                        }
                    });
                }

                if (state.focusedGroupAreasIds?.length) {
                    state.focusedGroupAreasIds.forEach((areaId) => {
                        const oldArea = draft.areas[areaId];
                        const newAreaId = oldNewAreasIdsMap[areaId];

                        if (oldArea) {
                            let newNestedElementsIds = [];
                            if (oldArea.value.nestedElementsIds?.length) {
                                oldArea.value.nestedElementsIds.forEach((elementId) => {
                                    if (oldElementsIds.includes(elementId) && oldNewElementsIdsMap[elementId]) {
                                        newNestedElementsIds.push(oldNewElementsIdsMap[elementId]);
                                    }
                                })
                            }

                            if (newNestedElementsIds?.length && draft.areas[newAreaId]?.value) {
                                draft.areas[newAreaId].value.nestedElementsIds = newNestedElementsIds;
                            }
                        }
                    });
                }

                // // ----------------------------------------------------------------
                // // HISTORY: GET VALUE
                // const historyValue = [
                //     {
                //         // elementId: action.payload.id,
                //         elementsIds: newElementsIds,
                //         action: HISTORY.DUPLICATE_ELEMENTS,
                //         prevValue: {
                //             elements: null,
                //         },
                //         nextValue: {
                //             elements: newElementsForHistory,
                //         },
                //     },
                // ];

                // // ----------------------------------------------------------------
                // // HISTORY: SAVE VALUE
                // saveHistoryValue(state, draft, historyValue);
                // // ----------------------------------------------------------------


                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                draft.focusedGroupElements = newFocusedGroupElements;
                draft.focusedGroupElementsIds = newFocusedGroupElementsIds;
                draft.focusedGroupAreas = newFocusedGroupAreas;
                draft.focusedGroupAreasIds = newFocusedGroupAreasIds;

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.DELETE_ELEMENTS_BY_IDS: {
                let prevElementsForHistory = {};
                let prevRelatedLinesForHistory = {};

                state.focusedElementsIds.forEach((elementId) => {
                    if (draft.elements[elementId]) {
                        // TODO:
                        switch (draft.elements[elementId].type) {
                            case 'table': {
                                // 0. delete lines
                                for (const key in state.lines) {
                                    const line = state.lines[key];
                                    const {
                                        id,
                                        value: {
                                            fromElementId,
                                            fromInsideElementId,
                                            toElementId,
                                            toInsideElementId,
                                        },
                                    } = line;

                                    if ([fromElementId, toElementId].includes(elementId)) {
                                        if (draft.elements[fromElementId]) {
                                            draft.elements[
                                                fromElementId
                                            ].value.columns[
                                                fromInsideElementId
                                            ].isRelated = false;
                                        }
                                        if (draft.elements[toElementId]) {
                                            draft.elements[
                                                toElementId
                                            ].value.columns[
                                                toInsideElementId
                                            ].isRelated = false;
                                        }

                                        if (
                                            draft.focusedElementsIds.includes(
                                                fromElementId
                                            ) &&
                                            draft.focusedElements[fromElementId]
                                        ) {
                                            draft.focusedElements[
                                                fromElementId
                                            ].value.columns[
                                                fromInsideElementId
                                            ].isRelated = false;
                                        }
                                        if (
                                            draft.focusedElementsIds.includes(
                                                toElementId
                                            ) &&
                                            draft.focusedElements[toElementId]
                                        ) {
                                            draft.focusedElements[
                                                toElementId
                                            ].value.columns[
                                                toInsideElementId
                                            ].isRelated = false;
                                        }

                                        prevRelatedLinesForHistory[id] =
                                            state.lines[id];
                                        delete draft.lines[id];
                                    }
                                }

                                // // 1. remove table from area nestedElements
                                // ALREADY IMPLEMENTED BELOW

                                // for (const key in state.areas) {
                                //     const area = state.areas[key];
                                //     const { id, value: { nestedElementsIds } } = area;

                                //     if (nestedElementsIds.includes(elementId)) {
                                //         draft.areas[id].value.nestedElementsIds = state.areas[id].value.nestedElementsIds.filter(id => id !== elementId);
                                //     }
                                // }

                                break;
                            }
                            case 'note': {
                                // remove note from area nestedElements
                                // ALREADY IMPLEMENTED BELOW
                                break;
                            }
                            default:
                                break;
                        }

                        // ----------------------------------------------------------------
                        // ========== HISTORY SECTION

                        // 0. GET ELEMENTS for history value
                        prevElementsForHistory[elementId] =
                            draft.elements[elementId];
                        // ----------------------------------------------------------------

                        // remove element from area nestedElements
                        // HERE
                        if (draft.elements[elementId].value.areaId &&
                            draft.areas[draft.elements[elementId].value.areaId]
                        ) {
                            draft.areas[draft.elements[elementId].value.areaId].value.nestedElementsIds =
                                draft.areas[draft.elements[elementId].value.areaId].value.nestedElementsIds
                                    .filter((id) => id !== elementId);
                        }

                        // 1. delete from elements
                        delete draft.elements[elementId];

                        // 2. delete from elements live
                        delete draft.elementsLive[elementId];
                    }
                });

                // 3. delete from focused inside element
                draft.focusedInsideElement = null;

                // 4. delete from focused elements
                draft.focusedElements = {};

                // 5. delete from focused elements ids
                draft.focusedElementsIds = [];

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        // elementId: action.payload.id,
                        elementsIds: state.focusedElementsIds,
                        action: HISTORY.DELETE_ELEMENTS,
                        prevValue: {
                            elements: prevElementsForHistory,
                            lines: prevRelatedLinesForHistory,
                        },
                        nextValue: {
                            elements: null,
                            linesIds: Object.keys(prevRelatedLinesForHistory),
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                draft.isContentChanged = true;
                break;
            }
            case diagramActionTypes.DUPLICATE_ELEMENTS_BY_IDS: {
                let tablesCount = 0;
                let notesCount = 0;
                let pensCount = 0;

                state.focusedElementsIds.forEach((elementId) => {
                    if (state.elements[elementId]?.type === 'table') {
                        tablesCount++;
                    } else if (state.elements[elementId]?.type === 'note') {
                        notesCount++;
                    } else if (state.elements[elementId]?.type === 'pen-segment') {
                        pensCount++;
                    }
                })

                if (tablesCount) {
                    if (!checkTables(draft.elements, tablesCount)) {
                        draft.isModalSignInRestrictEnabled = true;
                        draft.modalSignInRestrictType = 'tables';
                        break;
                    }
                } else if (notesCount) {
                    if (!checkNotes(draft.elements, notesCount)) {
                        draft.isModalSignInRestrictEnabled = true;
                        draft.modalSignInRestrictType = 'notes';
                        break;
                    }
                } else if (pensCount) {
                    if (!checkPens(draft.elements, pensCount)) {
                        draft.isModalSignInRestrictEnabled = true;
                        draft.modalSignInRestrictType = 'pens';
                        break;
                    }
                }

                const newElementsForHistory = {};

                const newElementsIds = [];
                let focusedElements = {};

                const HEADER_LETTER_WIDTH = 12; //13
                const HEADER_LEFT_SPACE = 25;
                const HEADER_RIGHT_SPACE = 25;

                state.focusedElementsIds.forEach((elementId) => {
                    if (state.elements[elementId]?.type === 'table') {
                        const newElementId = nanoid();
                        const newElement = structuredClone(
                            state.elements[elementId]
                        );

                        const newColumns = {};
                        for (const index in state.elements[elementId].value
                            .columns) {
                            const column =
                                state.elements[elementId].value.columns[index];
                            const newColumnId = nanoid();
                            const newColumn = {
                                ...column,
                                id: newColumnId,
                            };
                            newColumns[newColumnId] = newColumn;
                        }

                        newElement.value.columns = newColumns;
                        newElement.id = newElementId;
                        newElement.x += 100;
                        newElement.y += 100;
                        if (newElement.value.header.name !== '') {
                            newElement.value.header.name = newElement.value.header.name + '_copy';
                        }

                        const headerWidth =
                            newElement.value.header.name.length *
                            HEADER_LETTER_WIDTH +
                            HEADER_LEFT_SPACE +
                            HEADER_RIGHT_SPACE;
                        newElement.width = Math.max(
                            headerWidth,
                            newElement.width
                        );

                        // 1.1. copy to elements
                        draft.elements[newElementId] = newElement;

                        // 2.1. copy to elements live
                        draft.elementsLive[newElementId] = newElement;
                        focusedElements[newElementId] = newElement;

                        newElementsIds.push(newElementId);

                        // 2.2 check areas
                        if (newElement.value.areaId && draft.areas[newElement.value.areaId]) {
                            draft.areas[newElement.value.areaId].value.nestedElementsIds.push(newElementId);
                        }

                        // ----------------------------------------------------------------
                        // ========== HISTORY SECTION

                        // 0. GET ELEMENTS for history value
                        newElementsForHistory[newElementId] = newElement;
                        // ----------------------------------------------------------------
                    } else if (state.elements[elementId]?.type === 'note') {
                        const newElementId = nanoid();
                        const newElement = structuredClone(
                            state.elements[elementId]
                        );
                        newElement.id = newElementId;
                        newElement.x += 100;
                        newElement.y += 100;
                        // newElement.value.text = newElement.value.text + '_copy';

                        // 1.2. copy to elements
                        draft.elements[newElementId] = newElement;

                        // 2.2. copy to elements live
                        draft.elementsLive[newElementId] = newElement;
                        focusedElements[newElementId] = newElement;

                        newElementsIds.push(newElementId);

                        // 2.2 check areas
                        if (newElement.value.areaId && draft.areas[newElement.value.areaId]) {
                            draft.areas[newElement.value.areaId].value.nestedElementsIds.push(newElementId);
                        }

                        // ----------------------------------------------------------------
                        // ========== HISTORY SECTION

                        // 0. GET ELEMENTS for history value
                        newElementsForHistory[newElementId] = newElement;
                        // ----------------------------------------------------------------
                    } else if (
                        state.elements[elementId]?.type === 'pen-segment'
                    ) {
                        const newElementId = nanoid();
                        const newElement = structuredClone(
                            state.elements[elementId]
                        );
                        newElement.id = newElementId;
                        newElement.x += 100;
                        // newElement.y += 100;
                        newElement.translateX += 100;
                        // newElement.translateY += 100;

                        // newElement.value.text = newElement.value.text + '_copy';

                        // 1.2. copy to elements
                        draft.elements[newElementId] = newElement;

                        // 2.2. copy to elements live
                        draft.elementsLive[newElementId] = newElement;
                        focusedElements[newElementId] = newElement;

                        newElementsIds.push(newElementId);

                        // ----------------------------------------------------------------
                        // ========== HISTORY SECTION

                        // 0. GET ELEMENTS for history value
                        newElementsForHistory[newElementId] = newElement;
                        // ----------------------------------------------------------------
                    }
                });

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                // 3. copy to focused elements
                draft.focusedElements = focusedElements;

                // 4. copy to focused elements ids
                draft.focusedElementsIds = newElementsIds;


                if (
                    newElementsIds?.length === 1 &&
                    focusedElements && focusedElements[newElementsIds[0]] &&
                    focusedElements[newElementsIds[0]]?.type === 'table'
                ) {
                    const newInsideElement = focusedElements[newElementsIds[0]]?.value?.header;
                    draft.focusedInsideElement = {
                        elementId: focusedElements[newElementsIds[0]]?.id,
                        insideElementId: null,
                        insideElement: newInsideElement,
                        insideElementIndex: null,
                        insideElementType: 'header',
                    };
                } else {
                    draft.focusedInsideElement = null;
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        // elementId: action.payload.id,
                        elementsIds: newElementsIds,
                        action: HISTORY.DUPLICATE_ELEMENTS,
                        prevValue: {
                            elements: null,
                        },
                        nextValue: {
                            elements: newElementsForHistory,
                        },
                    },
                ];

                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }
            case diagramActionTypes.DUPLICATE_FOCUSED_AREA: {
                if (!checkAreas(draft.areas)) {
                    draft.isModalSignInRestrictEnabled = true;
                    draft.modalSignInRestrictType = 'areas';
                    break;
                }

                if (state.focusedAreaId) {
                    const newAreaId = nanoid();
                    const newArea = structuredClone(
                        state.areas[state.focusedAreaId]
                    );

                    newArea.id = newAreaId;
                    newArea.x += 100;
                    newArea.y += 100;
                    newArea.value.nestedElementsIds = [];

                    draft.areas[newAreaId] = newArea;

                    draft.focusedAreaId = newAreaId;
                    draft.focusedArea = newArea;

                    // ----------------------------------------------------------------
                    // HISTORY: GET VALUE
                    const historyValue = [
                        {
                            areaId: newAreaId,
                            action: HISTORY.DUPLICATE_AREA,
                            prevValue: {
                                area: null,
                            },
                            nextValue: {
                                area: newArea,
                            },
                        },
                    ];

                    // ----------------------------------------------------------------
                    // HISTORY: SAVE VALUE
                    saveHistoryValue(state, draft, historyValue);
                    // ----------------------------------------------------------------
                }

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.AUTO_RESIZE_ELEMENTS_BY_IDS: {
                const TABLE_HEADER_HEIGHT = 40;
                const TABLE_ROW_HEIGHT = 36; // prev value 28 with font 13(not 16)
                const TABLE_WIDTH = 320;
                const TABLE_BORDER_RADIUS = 3;
                const HEADER_BODY_MARGIN = 4;

                const HEADER_LETTER_WIDTH = 12; //13
                const HEADER_LEFT_SPACE = 25;
                const HEADER_RIGHT_SPACE = 25;

                const DEFAULT_LETTER_WIDTH = 10;
                const LEFT_SPACE = 25;
                const RIGHT_SPACE = 28;
                const MIN_CENTER_SPACE = 15;

                state.focusedElementsIds.forEach((elementId) => {
                    const element = state.elements[elementId];

                    let headerWidth =
                        element.value.header.name.length * HEADER_LETTER_WIDTH +
                        HEADER_LEFT_SPACE +
                        HEADER_RIGHT_SPACE;
                    let maxColumnWidth = headerWidth;

                    Object.keys(element.value.columns).forEach((key) => {
                        const column = element.value.columns[key];

                        const columnWidth =
                            (column.name.length + column.type.length) *
                            DEFAULT_LETTER_WIDTH +
                            LEFT_SPACE +
                            RIGHT_SPACE +
                            MIN_CENTER_SPACE;
                        maxColumnWidth = Math.max(columnWidth, maxColumnWidth);
                    });

                    // 1. update elements
                    draft.elements[elementId].width = Math.max(
                        maxColumnWidth,
                        TABLE_WIDTH
                    );
                    draft.elements[elementId].height =
                        TABLE_HEADER_HEIGHT +
                        Object.keys(element.value.columns).length *
                        TABLE_ROW_HEIGHT +
                        HEADER_BODY_MARGIN +
                        TABLE_BORDER_RADIUS +
                        1;

                    // 2. update elements live
                    draft.elementsLive[elementId].width = Math.max(
                        maxColumnWidth,
                        TABLE_WIDTH
                    );
                    draft.elementsLive[elementId].height =
                        TABLE_HEADER_HEIGHT +
                        Object.keys(element.value.columns).length *
                        TABLE_ROW_HEIGHT +
                        HEADER_BODY_MARGIN +
                        TABLE_BORDER_RADIUS +
                        1;
                });


                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                break;
            }
            case diagramActionTypes.SET_ELEMENTS_BORDER: {
                const nextHistoryValue = [];
                const prevHistoryValue = [];

                // 1. no focused elements
                if (!state.focusedElementsIds.length) {
                    return;

                    // 2. one focused element
                } else if (state.focusedElementsIds.length == 1) {
                    draft.elements[state.focusedElementsIds[0]].value.border =
                        action.payload.border;
                    draft.focusedElements[state.focusedElementsIds[0]] =
                        draft.elements[state.focusedElementsIds[0]];

                    // history value
                    prevHistoryValue.push([
                        state.focusedElementsIds[0],
                        state.elements[state.focusedElementsIds[0]].value
                            .border,
                    ]);
                    nextHistoryValue.push([
                        state.focusedElementsIds[0],
                        action.payload.border,
                    ]);

                    // 3. many focused elements
                } else {
                    state.focusedElementsIds.forEach((elementId) => {
                        if (
                            !state.elements[elementId].value.border !==
                            action.payload.border
                        ) {
                            draft.elements[elementId].value.border =
                                action.payload.border;
                            draft.focusedElements[elementId] =
                                draft.elements[elementId];

                            // history value
                            prevHistoryValue.push([
                                elementId,
                                state.elements[elementId].value.border,
                            ]);
                            nextHistoryValue.push([
                                elementId,
                                action.payload.border,
                            ]);
                        }
                    });

                    // if some element without border => all with border

                    // let isElementWithoutBorder = false;
                    // state.focusedElementsIds.forEach(elementId => {
                    //     if (!state.elements[elementId].value.withBorder) {
                    //         isElementWithoutBorder = true;
                    //     }
                    // });

                    // // 3.1. all elements should be with border
                    // if (isElementWithoutBorder) {
                    //     state.focusedElementsIds.forEach(elementId => {
                    //         if (!state.elements[elementId].value.withBorder) {
                    //             draft.elements[elementId].value.withBorder = true;
                    //         }
                    //     });

                    //     // 3.2. all elements should be without border
                    // } else {
                    //     state.focusedElementsIds.forEach(elementId => {
                    //         if (state.elements[elementId].value.withBorder) {
                    //             draft.elements[elementId].value.withBorder = false;
                    //         }
                    //     });
                    // }
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        action: HISTORY.UPDATE_ELEMENTS_BORDER,
                        prevValue: {
                            elements: prevHistoryValue,
                        },
                        nextValue: {
                            elements: nextHistoryValue,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_AREA_BORDER: {
                // 1. no focused area
                if (!state.focusedAreaId.length) {
                    return;

                    // 2. focused area
                } else {
                    draft.areas[state.focusedAreaId].value.border =
                        action.payload.border;
                    draft.focusedArea = draft.areas[state.focusedAreaId];

                    // ----------------------------------------------------------------
                    // HISTORY: GET VALUE
                    const historyValue = [
                        {
                            areaId: state.focusedAreaId,
                            action: HISTORY.UPDATE_AREA_BORDER,
                            prevValue: {
                                border: state.areas[state.focusedAreaId].value
                                    .border,
                            },
                            nextValue: {
                                border: draft.areas[state.focusedAreaId].value
                                    .border,
                            },
                        },
                    ];
                    // ----------------------------------------------------------------
                    // HISTORY: SAVE VALUE
                    saveHistoryValue(state, draft, historyValue);
                    // ----------------------------------------------------------------
                }

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_ELEMENTS_COLOR: {
                const nextHistoryValue = [];
                const prevHistoryValue = [];

                // 1. no focused elements
                if (!state.focusedElementsIds.length) {
                    return;

                    // 2. one focused element
                } else if (state.focusedElementsIds.length == 1) {
                    draft.elements[state.focusedElementsIds[0]].value.color =
                        action.payload.color;
                    draft.focusedElements[state.focusedElementsIds[0]] =
                        draft.elements[state.focusedElementsIds[0]];

                    // history value
                    prevHistoryValue.push([
                        state.focusedElementsIds[0],
                        state.elements[state.focusedElementsIds[0]].value.color,
                    ]);
                    nextHistoryValue.push([
                        state.focusedElementsIds[0],
                        action.payload.color,
                    ]);

                    // 3. many focused elements
                } else {
                    state.focusedElementsIds.forEach((elementId) => {
                        if (
                            !state.elements[elementId].value.color !==
                            action.payload.color
                        ) {
                            draft.elements[elementId].value.color =
                                action.payload.color;
                            draft.focusedElements[elementId] =
                                draft.elements[elementId];

                            // history value
                            prevHistoryValue.push([
                                elementId,
                                state.elements[elementId].value.color,
                            ]);
                            nextHistoryValue.push([
                                elementId,
                                action.payload.color,
                            ]);
                        }
                    });

                    // if some element without color => all with color

                    // let isElementWithoutColor = false;
                    // state.focusedElementsIds.forEach(elementId => {
                    //     if (!state.elements[elementId].value.withColor) {
                    //         isElementWithoutColor = true;
                    //     }
                    // });

                    // // 3.1. all elements should be with color
                    // if (isElementWithoutColor) {
                    //     state.focusedElementsIds.forEach(elementId => {
                    //         if (!state.elements[elementId].value.withColor) {
                    //             draft.elements[elementId].value.withColor = true;
                    //         }
                    //     });

                    //     // 3.2. all elements should be without color
                    // } else {
                    //     state.focusedElementsIds.forEach(elementId => {
                    //         if (state.elements[elementId].value.withColor) {
                    //             draft.elements[elementId].value.withColor = false;
                    //         }
                    //     });
                    // }
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        action: HISTORY.UPDATE_ELEMENTS_COLOR,
                        prevValue: {
                            elements: prevHistoryValue,
                        },
                        nextValue: {
                            elements: nextHistoryValue,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_AREA_COLOR: {
                // 1. no focused area
                if (!state.focusedAreaId) {
                    return;

                    // 2. focused area
                } else {
                    draft.areas[state.focusedAreaId].value.color =
                        action.payload.color;
                    draft.focusedArea = draft.areas[state.focusedAreaId];

                    // ----------------------------------------------------------------
                    // HISTORY: GET VALUE
                    const historyValue = [
                        {
                            areaId: state.focusedAreaId,
                            action: HISTORY.UPDATE_AREA_COLOR,
                            prevValue: {
                                color: state.areas[state.focusedAreaId].value
                                    .color,
                            },
                            nextValue: {
                                color: draft.areas[state.focusedAreaId].value
                                    .color,
                            },
                        },
                    ];
                    // ----------------------------------------------------------------
                    // HISTORY: SAVE VALUE
                    saveHistoryValue(state, draft, historyValue);
                    // ----------------------------------------------------------------
                }

                draft.isContentChanged = true;
                break;
            }
            case diagramActionTypes.UPDATE_SCALE_MINUS: {
                const newScale = state.scale / 1.25;
                const clampedScale = Math.min(Math.max(newScale, 0.045), 4.05);

                if (clampedScale == newScale) {
                    const centerX = window.innerWidth / 2;
                    const centerY = window.innerHeight / 2;

                    const zoomFactor = newScale / state.scale;

                    const newOriginX =
                        (state.originXY.x - centerX) * zoomFactor + centerX;
                    const newOriginY =
                        (state.originXY.y - centerY) * zoomFactor + centerY;

                    // 1. update scale
                    draft.scale = newScale;

                    // 2. update originXY
                    draft.originXY.x = newOriginX;
                    draft.originXY.y = newOriginY;
                }
                break;
            }
            case diagramActionTypes.UPDATE_SCALE_PLUS: {
                const newScale = state.scale * 1.25;
                const clampedScale = Math.min(Math.max(newScale, 0.045), 4.05);

                if (clampedScale == newScale) {
                    const centerX = window.innerWidth / 2;
                    const centerY = window.innerHeight / 2;

                    const zoomFactor = newScale / state.scale;

                    const newOriginX =
                        (state.originXY.x - centerX) * zoomFactor + centerX;
                    const newOriginY =
                        (state.originXY.y - centerY) * zoomFactor + centerY;

                    // 1. udpate scale
                    draft.scale = Math.min(Math.max(newScale, 0.045), 4.05);

                    // 2. update originXY
                    draft.originXY.x = newOriginX;
                    draft.originXY.y = newOriginY;
                }
                break;
            }
            case diagramActionTypes.RESET_SCALE: {
                const newScale = INITIAL_SCALE_VALUE;
                const centerX = window.innerWidth / 2;
                const centerY = window.innerHeight / 2;

                const zoomFactor = newScale / state.scale;

                const newOriginX = (state.originXY.x - centerX) * zoomFactor + centerX;
                const newOriginY = (state.originXY.y - centerY) * zoomFactor + centerY;

                // 1. update scale
                draft.scale = newScale;

                // 2. update originXY
                draft.originXY.x = newOriginX;
                draft.originXY.y = newOriginY;
                break;
            }
            case diagramActionTypes.UPDATE_SCALE: {
                // 1. update scale
                draft.scale = Math.min(
                    Math.max(action.payload.scale, 0.045),
                    4.05
                );

                // 2. update originXY
                draft.originXY.x = action.payload.x;
                draft.originXY.y = action.payload.y;
                break;
            }
            case diagramActionTypes.UPDATE_ORIGIN_XY: {
                // 1. update originXY
                draft.originXY.x = action.payload.x;
                draft.originXY.y = action.payload.y;

                // TODO: ?? should be here context menu location? also in scale reducer

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.UPDATE_SCALE_TO_FIT: {
                draft = calculateCanvasLimits(state, draft);
                const canvasLimits = draft.canvasLimits;


                // if canvasLimits is exist (more than one element on the board) => zoom to fit
                if (canvasLimits?.minX && canvasLimits?.maxX) {
                    const offset = 65;
                    const leftOffset = offset + 25; // 25 = controlsWidth/2

                    // const offsetLeft = 90;
                    // const offsetRight = 65;

                    const offsetTop = 75;
                    const offsetBottom = 70;


                    const elementsWidth = canvasLimits.maxX - canvasLimits.minX;
                    const elementsHeight = canvasLimits.maxY - canvasLimits.minY;
                    const canvasWidth = window.innerWidth - 2 * offset;
                    const canvasHeight = window.innerHeight - (offsetTop + offsetBottom);

                    const zoomX = canvasWidth / elementsWidth;
                    const zoomY = canvasHeight / elementsHeight;
                    // const newZoom = Math.min(Math.min(zoomX, zoomY), INITIAL_SCALE_VALUE);

                    // ASAS
                    // const newZoom = Math.min(Math.min(zoomX, zoomY), 1.26);
                    const newZoom = Math.min(Math.min(zoomX, zoomY), 1.08);
                    const newOriginX = (canvasWidth - elementsWidth * newZoom) / 2 - canvasLimits.minX * newZoom + leftOffset;
                    const newOriginY = (canvasHeight - elementsHeight * newZoom) / 2 - canvasLimits.minY * newZoom + offsetTop;

                    // 1. update scale
                    draft.scale = newZoom;

                    // 2. update originXY
                    draft.originXY.x = newOriginX;
                    draft.originXY.y = newOriginY;

                    // UNFOCUS FOCUSED ITEMS
                    draft = unfocusFocusedItems(state, draft);

                    // if canvasLimits is not exist (no one element on the board) => reset zoom = zoom to 100%
                } else {
                    const newScale = INITIAL_SCALE_VALUE;
                    const centerX = window.innerWidth / 2;
                    const centerY = window.innerHeight / 2;

                    const zoomFactor = newScale / state.scale;

                    const newOriginX = (state.originXY.x - centerX) * zoomFactor + centerX;
                    const newOriginY = (state.originXY.y - centerY) * zoomFactor + centerY;

                    // 1. update scale
                    draft.scale = newScale;

                    // 2. update originXY
                    draft.originXY.x = newOriginX;
                    draft.originXY.y = newOriginY;
                }

                // // 1. update scale
                // draft.scale = newZoom;

                // // 2. update originXY
                // draft.originXY.x = newOriginX;
                // draft.originXY.y = newOriginY;

                // // UNFOCUS FOCUSED ITEMS
                // draft = unfocusFocusedItems(state, draft);

                break;
            }

            case diagramActionTypes.SET_COLUMN_KEY: {
                // 1. update elements
                if (!action.payload.keyType) {
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].isPK = false;
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].isFK = false;
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].fkRefTable = null;
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].fkRefColumn = null;
                } else if (action.payload.keyType === 'pk') {
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].isPK = true;
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].isFK = false;
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].fkRefTable = null;
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].fkRefColumn = null;
                } else {
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].isPK = false;
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].isFK = true;
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].fkRefTable = 'table1';
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].fkRefColumn = 'column1';
                }

                // 2. update focused inside element
                if (
                    draft.focusedInsideElement?.elementId ==
                    action.payload.tableId &&
                    draft.focusedInsideElement?.insideElementId ==
                    action.payload.columnId
                ) {
                    draft.focusedInsideElement.insideElement =
                        draft.elements[action.payload.tableId].value.columns[
                        action.payload.columnId
                        ];
                }

                // 3. update focused elements
                if (
                    draft.focusedElements &&
                    draft.focusedElements[action.payload.tableId]
                ) {
                    draft.focusedElements[action.payload.tableId].value.columns[
                        action.payload.columnId
                    ] =
                        draft.elements[action.payload.tableId].value.columns[
                        action.payload.columnId
                        ];
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.UPDATE_COLUMN_KEY,
                        prevValue: {
                            columnId: action.payload.columnId,
                            isPK: state.elements[action.payload.tableId].value
                                .columns[action.payload.columnId].isPK,
                            isFK: state.elements[action.payload.tableId].value
                                .columns[action.payload.columnId].isFK,
                        },
                        nextValue: {
                            columnId: action.payload.columnId,
                            isPK: draft.elements[action.payload.tableId].value
                                .columns[action.payload.columnId].isPK,
                            isFK: draft.elements[action.payload.tableId].value
                                .columns[action.payload.columnId].isFK,
                        },
                    },
                ];

                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }
            case diagramActionTypes.SET_COLUMN_NOT_NULL: {
                // 1. update elements
                draft.elements[action.payload.tableId].value.columns[
                    action.payload.columnId
                ].isNN =
                    !state.elements[action.payload.tableId].value.columns[
                        action.payload.columnId
                    ].isNN;

                // 2. update focused inside element
                if (
                    draft.focusedInsideElement?.elementId ==
                    action.payload.tableId &&
                    draft.focusedInsideElement?.insideElementId ==
                    action.payload.columnId
                ) {
                    draft.focusedInsideElement.insideElement =
                        draft.elements[action.payload.tableId].value.columns[
                        action.payload.columnId
                        ];
                }

                // 3. update focused elements
                if (state.focusedElements[action.payload.tableId]) {
                    draft.focusedElements[action.payload.tableId].value.columns[
                        action.payload.columnId
                    ].isNN =
                        draft.elements[action.payload.tableId].value.columns[
                            action.payload.columnId
                        ].isNN;
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.UPDATE_COLUMN_NN,
                        prevValue: {
                            columnId: action.payload.columnId,
                            isNN: state.elements[action.payload.tableId].value
                                .columns[action.payload.columnId].isNN,
                        },
                        nextValue: {
                            columnId: action.payload.columnId,
                            isNN: draft.elements[action.payload.tableId].value
                                .columns[action.payload.columnId].isNN,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_COLUMN_AUTO_INCREMENT: {
                // 1. update elements
                draft.elements[action.payload.tableId].value.columns[
                    action.payload.columnId
                ].isAutoIncrement =
                    !state.elements[action.payload.tableId].value.columns[
                        action.payload.columnId
                    ].isAutoIncrement;

                // 2. update focused inside element
                if (
                    draft.focusedInsideElement?.elementId ==
                    action.payload.tableId &&
                    draft.focusedInsideElement?.insideElementId ==
                    action.payload.columnId
                ) {
                    draft.focusedInsideElement.insideElement =
                        draft.elements[action.payload.tableId].value.columns[
                        action.payload.columnId
                        ];
                }

                // 3. update focused elements
                if (state.focusedElements[action.payload.tableId]) {
                    draft.focusedElements[action.payload.tableId].value.columns[
                        action.payload.columnId
                    ].isAutoIncrement =
                        draft.elements[action.payload.tableId].value.columns[
                            action.payload.columnId
                        ].isAutoIncrement;
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.UPDATE_COLUMN_NN,
                        prevValue: {
                            columnId: action.payload.columnId,
                            isAutoIncrement: state.elements[action.payload.tableId].value
                                .columns[action.payload.columnId].isAutoIncrement,
                        },
                        nextValue: {
                            columnId: action.payload.columnId,
                            isAutoIncrement: draft.elements[action.payload.tableId].value
                                .columns[action.payload.columnId].isAutoIncrement,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_COLUMN_ENUM: {
                const isEnum = !!action.payload.values;
                const columnType = state.elements[action.payload.tableId].value.columns[action.payload.columnId].type;


                if (isEnum) {
                    // enum
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].isEnum = true;
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].type = 'enum';
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].enumValues = action.payload.values;

                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].prevType = state.elements[action.payload.tableId].value.columns[action.payload.columnId].type;
                } else if (columnType == 'enum') {
                    // not enum
                    const prevType = state.elements[action.payload.tableId].value.columns[action.payload.columnId].prevType;

                    delete draft.elements[action.payload.tableId].value.columns[action.payload.columnId].isEnum;
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].type = prevType ? prevType : 'int';
                    delete draft.elements[action.payload.tableId].value.columns[action.payload.columnId].enumValues;

                    delete draft.elements[action.payload.tableId].value.columns[action.payload.columnId].prevType;
                }


                // 1. update elements

                // 2. update focused inside element
                if (draft.focusedInsideElement?.elementId == action.payload.tableId && draft.focusedInsideElement?.insideElementId == action.payload.columnId) {
                    draft.focusedInsideElement.insideElement = draft.elements[action.payload.tableId].value.columns[action.payload.columnId];
                }

                // 3. update focused elements
                if (state.focusedElements[action.payload.tableId]) {

                    if (isEnum) {
                        // enum
                        draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].isEnum = true;
                        draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].type = 'enum';
                        draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].enumValues = action.payload.values;

                        draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].prevType = state.elements[action.payload.tableId].value.columns[action.payload.columnId].type;
                    } else if (columnType == 'enum') {
                        // not enum
                        const prevType = state.elements[action.payload.tableId].value.columns[action.payload.columnId].prevType;

                        delete draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].isEnum;
                        draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].type = prevType ? prevType : 'int';
                        delete draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].enumValues;

                        delete draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].prevType;
                    }
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.UPDATE_COLUMN_NN,
                        prevValue: {
                            columnId: action.payload.columnId,
                            isEnum: state.elements[action.payload.tableId].value.columns[action.payload.columnId].isEnum,
                        },
                        nextValue: {
                            columnId: action.payload.columnId,
                            isEnum: draft.elements[action.payload.tableId].value.columns[action.payload.columnId].isEnum,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }


            case diagramActionTypes.SET_COLUMN_DEFAULT_VALUE: {
                const isDefaultValue = !!action.payload.value;

                if (isDefaultValue) {
                    // default value
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].defaultValue = action.payload.value;
                } else {
                    // not default value
                    delete draft.elements[action.payload.tableId].value.columns[action.payload.columnId].defaultValue;
                }


                // 1. update elements

                // 2. update focused inside element
                if (draft.focusedInsideElement?.elementId == action.payload.tableId && draft.focusedInsideElement?.insideElementId == action.payload.columnId) {
                    draft.focusedInsideElement.insideElement = draft.elements[action.payload.tableId].value.columns[action.payload.columnId];
                }

                // 3. update focused elements
                if (state.focusedElements[action.payload.tableId]) {

                    if (isDefaultValue) {
                        // default value
                        draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].defaultValue = action.payload.value;
                    } else {
                        // not default value
                        delete draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].defaultValue;
                    }
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.UPDATE_COLUMN_NN,
                        prevValue: {
                            columnId: action.payload.columnId,
                        },
                        nextValue: {
                            columnId: action.payload.columnId,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }


            case diagramActionTypes.SET_COLUMN_GENERATED_VALUE: {
                const isGeneratedValue = !!action.payload.value;

                if (isGeneratedValue) {
                    // generated value
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].generatedValue = action.payload.value;
                } else {
                    // not generated value
                    delete draft.elements[action.payload.tableId].value.columns[action.payload.columnId].generatedValue;
                }


                // 1. update elements

                // 2. update focused inside element
                if (draft.focusedInsideElement?.elementId == action.payload.tableId && draft.focusedInsideElement?.insideElementId == action.payload.columnId) {
                    draft.focusedInsideElement.insideElement = draft.elements[action.payload.tableId].value.columns[action.payload.columnId];
                }

                // 3. update focused elements
                if (state.focusedElements[action.payload.tableId]) {

                    if (isGeneratedValue) {
                        // generated value
                        draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].generatedValue = action.payload.value;
                    } else {
                        // not generated value
                        delete draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].generatedValue;
                    }
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.UPDATE_COLUMN_NN,
                        prevValue: {
                            columnId: action.payload.columnId,
                        },
                        nextValue: {
                            columnId: action.payload.columnId,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }


            case diagramActionTypes.SET_COLUMN_CHECK_VALUE: {
                const isCheckValue = !!action.payload.value;

                if (isCheckValue) {
                    // check value
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].checkValue = action.payload.value;
                } else {
                    // not check value
                    delete draft.elements[action.payload.tableId].value.columns[action.payload.columnId].checkValue;
                }


                // 1. update elements

                // 2. update focused inside element
                if (draft.focusedInsideElement?.elementId == action.payload.tableId && draft.focusedInsideElement?.insideElementId == action.payload.columnId) {
                    draft.focusedInsideElement.insideElement = draft.elements[action.payload.tableId].value.columns[action.payload.columnId];
                }

                // 3. update focused elements
                if (state.focusedElements[action.payload.tableId]) {

                    if (isCheckValue) {
                        // check value
                        draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].checkValue = action.payload.value;
                    } else {
                        // not check value
                        delete draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].checkValue;
                    }
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.UPDATE_COLUMN_NN,
                        prevValue: {
                            columnId: action.payload.columnId,
                        },
                        nextValue: {
                            columnId: action.payload.columnId,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_COLUMN_DESCRIPTION: {
                const isDescription = !!action.payload.value;

                draft.isEnabledElementDescriptionEditing = false;

                if (isDescription) {
                    // description
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].description = action.payload.value;
                } else {
                    // not description
                    delete draft.elements[action.payload.tableId].value.columns[action.payload.columnId].description;
                }

                // 1. update elements

                // 2. update focused inside element
                if (draft.focusedInsideElement?.elementId == action.payload.tableId && draft.focusedInsideElement?.insideElementId == action.payload.columnId) {
                    draft.focusedInsideElement.insideElement = draft.elements[action.payload.tableId].value.columns[action.payload.columnId];
                }

                // 3. update focused elements
                if (state.focusedElements[action.payload.tableId]) {

                    if (isDescription) {
                        // description
                        draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].description = action.payload.value;
                    } else {
                        // not description
                        delete draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].description;
                    }
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.UPDATE_COLUMN_NN,
                        prevValue: {
                            columnId: action.payload.columnId,
                        },
                        nextValue: {
                            columnId: action.payload.columnId,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_ELEMENT_DESCRIPTION: {
                const isDescription = !!action.payload.value;

                draft.isEnabledElementDescriptionEditing = false;

                if (isDescription) {
                    // description
                    draft.elements[action.payload.elementId].value.description = action.payload.value;
                } else {
                    // not description
                    delete draft.elements[action.payload.elementId].value.description;
                }

                // 3. update focused elements
                if (state.focusedElements[action.payload.elementId]) {

                    if (isDescription) {
                        // description
                        draft.focusedElements[action.payload.elementId].value.description = action.payload.value;
                    } else {
                        // not description
                        delete draft.focusedElements[action.payload.elementId].value.description;
                    }
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.elementId,
                        action: HISTORY.UPDATE_COLUMN_NN,
                        prevValue: {

                        },
                        nextValue: {

                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_AREA_DESCRIPTION: {
                const isDescription = !!action.payload.value;

                draft.isEnabledElementDescriptionEditing = false;

                if (isDescription) {
                    // description
                    draft.areas[action.payload.areaId].value.description = action.payload.value;
                } else {
                    // not description
                    delete draft.areas[action.payload.areaId].value.description;
                }

                // 3. update focused areas
                if (state.focusedArea?.id == action.payload.areaId) {

                    if (isDescription) {
                        // description
                        draft.focusedArea.value.description = action.payload.value;
                    } else {
                        // not description
                        delete draft.focusedArea.value.description;
                    }
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        areaId: action.payload.areaId,
                        action: HISTORY.UPDATE_COLUMN_NN,
                        prevValue: {

                        },
                        nextValue: {

                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }


            case diagramActionTypes.SET_LINE_DESCRIPTION: {
                const isDescription = !!action.payload.value;

                draft.isEnabledElementDescriptionEditing = false;

                if (isDescription) {
                    // description
                    draft.lines[action.payload.lineId].value.description = action.payload.value;
                } else {
                    // not description
                    delete draft.lines[action.payload.lineId].value.description;
                }

                // 3. update focused lines
                if (state.focusedLine?.id == action.payload.lineId) {

                    if (isDescription) {
                        // description
                        draft.focusedLine.value.description = action.payload.value;
                    } else {
                        // not description
                        delete draft.focusedLine.value.description;
                    }
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        lineId: action.payload.lineId,
                        action: HISTORY.UPDATE_COLUMN_NN,
                        prevValue: {

                        },
                        nextValue: {

                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_COLUMN_INDEX: {
                // 1. update elements
                if (!action.payload.indexType) {
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].index = null;
                } else {
                    draft.elements[action.payload.tableId].value.columns[action.payload.columnId].index = { type: action.payload.indexType };
                }


                const columns = draft.elements[action.payload.tableId].value.columns;
                const columnsKeys = Object.keys(columns);
                let isTableWithIndexes = false;

                columnsKeys.forEach(columnKey => {
                    const column = columns[columnKey];
                    if (column.index) {
                        isTableWithIndexes = true;
                    }
                });

                draft.elements[action.payload.tableId].value.isTableWithIndexes = isTableWithIndexes;

                // 2. update focused inside element
                if (
                    draft.focusedInsideElement?.elementId == action.payload.tableId &&
                    draft.focusedInsideElement?.insideElementId == action.payload.columnId
                ) {
                    draft.focusedInsideElement.insideElement = draft.elements[action.payload.tableId].value.columns[action.payload.columnId];
                }

                // 3. update focused elements
                if (state.focusedElements[action.payload.tableId]) {
                    draft.focusedElements[action.payload.tableId].value.columns[action.payload.columnId].index =
                        draft.elements[action.payload.tableId].value.columns[action.payload.columnId].index;

                    draft.focusedElements[action.payload.tableId].value.isTableWithIndexes = isTableWithIndexes;
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.UPDATE_COLUMN_INDEX,
                        prevValue: {
                            columnId: action.payload.columnId,
                            index: state.elements[action.payload.tableId].value
                                .columns[action.payload.columnId].index,
                        },
                        nextValue: {
                            columnId: action.payload.columnId,
                            index: draft.elements[action.payload.tableId].value
                                .columns[action.payload.columnId].index,
                        },
                    },
                ];

                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.DELETE_COLUMN: {
                // delete column
                // focus on the next column
                let prevRelatedLinesForHistory = {};

                const columnsKeys = Object.keys(
                    draft.elements[action.payload.tableId].value.columns
                );
                let prevColumnIndex = -1;
                for (let i = 0; i < columnsKeys.length; i++) {
                    if (columnsKeys[i] === action.payload.columnId) {
                        prevColumnIndex = i;
                        break;
                    }
                }

                // 0. delete lines

                for (const key in state.lines) {
                    const line = state.lines[key];
                    const {
                        id,
                        value: {
                            fromElementId,
                            fromInsideElementId,
                            toElementId,
                            toInsideElementId,
                        },
                    } = line;

                    if (
                        [fromElementId, toElementId].includes(
                            action.payload.tableId
                        ) &&
                        [fromInsideElementId, toInsideElementId].includes(
                            action.payload.columnId
                        )
                    ) {
                        prevRelatedLinesForHistory[id] = state.lines[id];
                        delete draft.lines[id];
                        draft.elements[fromElementId].value.columns[
                            fromInsideElementId
                        ].isRelated = false;
                        draft.elements[toElementId].value.columns[
                            toInsideElementId
                        ].isRelated = false;

                        if (
                            draft.focusedElementsIds.includes(fromElementId) &&
                            draft.focusedElements[fromElementId]
                        ) {
                            draft.focusedElements[fromElementId].value.columns[
                                fromInsideElementId
                            ].isRelated = false;
                        }
                        if (
                            draft.focusedElementsIds.includes(toElementId) &&
                            draft.focusedElements[toElementId]
                        ) {
                            draft.focusedElements[toElementId].value.columns[
                                toInsideElementId
                            ].isRelated = false;
                        }
                    }
                }

                // DELETE

                // 1. delete from elements
                delete draft.elements[action.payload.tableId].value.columns[
                    action.payload.columnId
                ];

                // 2.1. update table height
                draft.elements[action.payload.tableId].height -=
                    TABLE_ROW_HEIGHT;

                // 2.2. update columns indexes
                Object.keys(
                    draft.elements[action.payload.tableId].value.columns
                ).forEach((key, index) => {
                    draft.elements[action.payload.tableId].value.columns[
                        key
                    ].indexNumber = index;
                });

                // // 3. delete from focused inside element
                // draft.focusedInsideElement = null;

                // 4. delete from focused elements
                if (state.focusedElements[action.payload.tableId]) {
                    // 4.1. delete from focused elements
                    delete draft.focusedElements[action.payload.tableId].value
                        .columns[action.payload.columnId];

                    // 4.2. update columns indexes
                    Object.keys(
                        draft.focusedElements[action.payload.tableId].value
                            .columns
                    ).forEach((key, index) => {
                        draft.focusedElements[
                            action.payload.tableId
                        ].value.columns[key].indexNumber = index;
                    });
                }

                // FOCUS ON THE NEXT COLUMN

                // 1. focus on NEXT column
                if (columnsKeys[prevColumnIndex + 1]) {
                    const nextColumnKey = columnsKeys[prevColumnIndex + 1];
                    const newInsideElement =
                        draft.elements[action.payload.tableId].value.columns[
                        nextColumnKey
                        ];

                    // TODO: use just indexNumber
                    // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[action.payload.columnId].indexNumber;

                    let newInsideElementIndex = 0;
                    const keys = Object.keys(
                        draft.elements[action.payload.tableId].value.columns
                    );
                    for (let i = 0; i < keys.length; i++) {
                        if (keys[i] === nextColumnKey) {
                            newInsideElementIndex = i;
                            break;
                        }
                    }

                    draft.focusedInsideElement = {
                        elementId: action.payload.tableId,
                        insideElementId: nextColumnKey,
                        insideElement: newInsideElement,
                        insideElementIndex: newInsideElementIndex,
                        insideElementType: 'column',
                    };

                    // 2. focus on PREVIOUS column
                } else if (columnsKeys[prevColumnIndex - 1]) {
                    const nextColumnKey = columnsKeys[prevColumnIndex - 1];
                    const newInsideElement =
                        draft.elements[action.payload.tableId].value.columns[
                        nextColumnKey
                        ];

                    // TODO: use just indexNumber
                    // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[action.payload.columnId].indexNumber;

                    let newInsideElementIndex = 0;
                    const keys = Object.keys(
                        draft.elements[action.payload.tableId].value.columns
                    );
                    for (let i = 0; i < keys.length; i++) {
                        if (keys[i] === nextColumnKey) {
                            newInsideElementIndex = i;
                            break;
                        }
                    }

                    draft.focusedInsideElement = {
                        elementId: action.payload.tableId,
                        insideElementId: nextColumnKey,
                        insideElement: newInsideElement,
                        insideElementIndex: newInsideElementIndex,
                        insideElementType: 'column',
                    };

                    // 3. focus on TABLE
                    // POINT 1 = FOCUS ON TABLE NAME
                } else {
                    draft.focusedInsideElement = null;
                }

                // -----------------------------------
                // UPDATE LINES POSITIONS FROM/TO THE TABLE
                const linesIds = [];
                // find lines from/to the table
                for (const key in draft.lines) {
                    const line = draft.lines[key];
                    const {
                        value: { fromElementId, toElementId },
                    } = line;

                    if (
                        [fromElementId, toElementId].includes(
                            action.payload.tableId
                        )
                    ) {
                        linesIds.push(line.id);
                    }
                }

                // update lines (need for update lines by updated columns positions/indexNumbers)
                draft = updateLinesPaths(draft, linesIds);
                // -----------------------------------

                // ----------------------------------------------------------------
                const afterColumnId = columnsKeys[prevColumnIndex - 1];
                const column =
                    state.elements[action.payload.tableId].value.columns[
                    action.payload.columnId
                    ];

                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.DELETE_COLUMN,
                        prevValue: {
                            afterColumnId: afterColumnId || null,
                            column: column,
                            lines: prevRelatedLinesForHistory,
                        },
                        nextValue: {
                            columnId: column.id,
                            linesIds: Object.keys(prevRelatedLinesForHistory),
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }
            case diagramActionTypes.ADD_NEW_COLUMN_ABOVE: {
                if (!checkColumns(draft.elements, action.payload.tableId)) {
                    draft.isModalSignInRestrictEnabled = true;
                    draft.modalSignInRestrictType = 'columns';
                    break;
                }

                const newColumnId = nanoid();
                const newColumn = {
                    id: newColumnId,
                    name: 'column_new',
                    type: 'int',
                };

                const newColumns = {};
                const columns =
                    state.elements[action.payload.tableId].value.columns;

                let hasNewColumn = false;
                Object.keys(columns).forEach((id, index) => {
                    if (id === action.payload.columnId) {
                        newColumns[newColumnId] = {
                            ...newColumn,
                            indexNumber: index,
                        };
                        hasNewColumn = true;
                    }

                    if (hasNewColumn) {
                        newColumns[id] = {
                            ...columns[id],
                            indexNumber: index + 1,
                        };
                    } else {
                        newColumns[id] = columns[id];
                    }
                });

                // for (const id in columns) {
                //     if (id === action.payload.columnId) {
                //         newColumns[newColumnId] = newColumn;
                //     }

                //     newColumns[id] = columns[id];
                // }

                // 1. update elements
                draft.elements[action.payload.tableId].value.columns =
                    newColumns;
                draft.elements[action.payload.tableId].height +=
                    TABLE_ROW_HEIGHT;

                // 2. update focused inside element
                // draft.focusedInsideElement = {
                //     elementId: action.payload.tableId,
                //     insideElementId: newColumnId,
                //     insideElement: newColumn,
                // };
                const newInsideElement =
                    draft.elements[action.payload.tableId].value.columns[
                    newColumnId
                    ];

                // TODO: use just indexNumber
                // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[newColumnId].indexNumber;

                let newInsideElementIndex = 0;
                const keys = Object.keys(
                    draft.elements[action.payload.tableId].value.columns
                );

                for (let i = 0; i < keys.length; i++) {
                    if (keys[i] === newColumnId) {
                        newInsideElementIndex = i;
                        break;
                    }
                }

                draft.focusedInsideElement = {
                    elementId: action.payload.tableId,
                    insideElementId: newColumnId,
                    insideElement: newInsideElement,
                    insideElementIndex: newInsideElementIndex,
                    insideElementType: 'column',
                };

                // 3. update focused elements
                if (state.focusedElements[action.payload.tableId]) {
                    draft.focusedElements[
                        action.payload.tableId
                    ].value.columns = newColumns;
                }

                // -----------------------------------
                // UPDATE LINES POSITIONS FROM/TO THE TABLE
                const linesIds = [];
                // find lines from/to the table
                for (const key in draft.lines) {
                    const line = draft.lines[key];
                    const {
                        value: { fromElementId, toElementId },
                    } = line;

                    if (
                        [fromElementId, toElementId].includes(
                            action.payload.tableId
                        )
                    ) {
                        linesIds.push(line.id);
                    }
                }

                // update lines (need for update lines by updated columns positions/indexNumbers)
                draft = updateLinesPaths(draft, linesIds);

                // -----------------------------------

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.ADD_COLUMN,
                        prevValue: {
                            columnId: newColumn.id,
                        },
                        nextValue: {
                            valueType: 'before',
                            beforeColumnId: action.payload.columnId,
                            column: newColumn,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }
            case diagramActionTypes.ADD_NEW_COLUMN_BELOW: {
                // 1. if true => find next inside element (next column) and make it focused
                if (action.payload.isMoveToNextInsideElement) {
                    const columnsKeys = Object.keys(
                        draft.elements[action.payload.tableId].value.columns
                    );

                    let prevColumnIndex = -1;
                    for (let i = 0; i < columnsKeys.length; i++) {
                        if (columnsKeys[i] === action.payload.columnId) {
                            prevColumnIndex = i;
                            break;
                        }
                    }


                    // 1.1. MAKE FOCUS ON THE NEXT COLUMN
                    if (
                        prevColumnIndex >= 0 &&
                        columnsKeys[prevColumnIndex] &&
                        columnsKeys[prevColumnIndex + 1]
                    ) {
                        const nextColumnKey = columnsKeys[prevColumnIndex + 1];

                        const newInsideElement =
                            draft.elements[action.payload.tableId].value
                                .columns[nextColumnKey];

                        // TODO: use just indexNumber
                        // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[action.payload.columnId].indexNumber;

                        let newInsideElementIndex = 0;
                        const keys = Object.keys(
                            draft.elements[action.payload.tableId].value.columns
                        );

                        for (let i = 0; i < keys.length; i++) {
                            if (keys[i] === nextColumnKey) {
                                newInsideElementIndex = i;
                                break;
                            }
                        }

                        draft.focusedInsideElement = {
                            elementId: action.payload.tableId,
                            insideElementId: nextColumnKey,
                            insideElement: newInsideElement,
                            insideElementIndex: newInsideElementIndex,
                            insideElementType: 'column',
                        };

                        break;

                        // 1.2. ADD NEW COLUMN BELOW
                    } else {
                        if (!checkColumns(draft.elements, action.payload.tableId)) {
                            draft.isModalSignInRestrictEnabled = true;
                            draft.modalSignInRestrictType = 'columns';
                            break;
                        }

                        const newColumnId = nanoid();
                        const newColumn = {
                            id: newColumnId,
                            name: 'column_new',
                            type: 'int',
                        };

                        const newColumns = {};
                        const columns =
                            state.elements[action.payload.tableId].value
                                .columns;

                        let hasNewColumn = false;
                        Object.keys(columns).forEach((id, index) => {
                            if (hasNewColumn) {
                                newColumns[id] = {
                                    ...columns[id],
                                    indexNumber: index + 1,
                                };
                            } else {
                                newColumns[id] = columns[id];
                            }

                            if (id === action.payload.columnId) {
                                newColumns[newColumnId] = {
                                    ...newColumn,
                                    indexNumber: index + 1,
                                };
                                hasNewColumn = true;
                            }
                        });
                        // for (const id in columns) {
                        //     newColumns[id] = columns[id];

                        //     if (id === action.payload.columnId) {
                        //         newColumns[newColumnId] = newColumn;
                        //     }
                        // }
                        // 1. update elements
                        draft.elements[action.payload.tableId].value.columns =
                            newColumns;
                        draft.elements[action.payload.tableId].height +=
                            TABLE_ROW_HEIGHT;

                        // 2. update focused inside element
                        // draft.focusedInsideElement = {
                        //     elementId: action.payload.tableId,
                        //     insideElementId: newColumnId,
                        //     insideElement: newColumn,
                        // };
                        const newInsideElement =
                            draft.elements[action.payload.tableId].value
                                .columns[newColumnId];

                        // TODO: use just indexNumber
                        // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[newColumnId].indexNumber;

                        let newInsideElementIndex = 0;
                        const keys = Object.keys(
                            draft.elements[action.payload.tableId].value.columns
                        );

                        for (let i = 0; i < keys.length; i++) {
                            if (keys[i] === newColumnId) {
                                newInsideElementIndex = i;
                                break;
                            }
                        }

                        draft.focusedInsideElement = {
                            elementId: action.payload.tableId,
                            insideElementId: newColumnId,
                            insideElement: newInsideElement,
                            insideElementIndex: newInsideElementIndex,
                            insideElementType: 'column',
                        };

                        // 3. update focused elements
                        if (state.focusedElements[action.payload.tableId]) {
                            draft.focusedElements[
                                action.payload.tableId
                            ].value.columns = newColumns;
                        }

                        // -----------------------------------
                        // UPDATE LINES POSITIONS FROM/TO THE TABLE
                        const linesIds = [];
                        // find lines from/to the table
                        for (const key in draft.lines) {
                            const line = draft.lines[key];
                            const {
                                value: { fromElementId, toElementId },
                            } = line;

                            if (
                                [fromElementId, toElementId].includes(
                                    action.payload.tableId
                                )
                            ) {
                                linesIds.push(line.id);
                            }
                        }

                        // update lines (need for update lines by updated columns positions/indexNumbers)
                        draft = updateLinesPaths(draft, linesIds);
                        // -----------------------------------

                        // ----------------------------------------------------------------
                        // HISTORY: GET VALUE
                        const historyValue = [
                            {
                                elementId: action.payload.tableId,
                                action: HISTORY.ADD_COLUMN,
                                prevValue: {
                                    columnId: newColumn.id,
                                },
                                nextValue: {
                                    valueType: 'after',
                                    afterColumnId: action.payload.columnId,
                                    column: newColumn,
                                },
                            },
                        ];
                        // ----------------------------------------------------------------
                        // HISTORY: SAVE VALUE
                        saveHistoryValue(state, draft, historyValue);
                        // ----------------------------------------------------------------
                    }

                    // 2. if false => create new inside element and make in focused
                } else {
                    if (!checkColumns(draft.elements, action.payload.tableId)) {
                        draft.isModalSignInRestrictEnabled = true;
                        draft.modalSignInRestrictType = 'columns';
                        break;
                    }

                    const newColumnId = nanoid();
                    const newColumn = {
                        id: newColumnId,
                        name: 'column_new',
                        type: 'int',
                    };

                    const newColumns = {};
                    const columns =
                        state.elements[action.payload.tableId].value.columns;

                    let hasNewColumn = false;
                    Object.keys(columns).forEach((id, index) => {
                        if (hasNewColumn) {
                            newColumns[id] = {
                                ...columns[id],
                                indexNumber: index + 1,
                            };
                        } else {
                            newColumns[id] = columns[id];
                        }

                        if (id === action.payload.columnId) {
                            newColumns[newColumnId] = {
                                ...newColumn,
                                indexNumber: index + 1,
                            };
                            hasNewColumn = true;
                        }
                    });
                    // for (const id in columns) {
                    //     newColumns[id] = columns[id];

                    //     if (id === action.payload.columnId) {
                    //         newColumns[newColumnId] = newColumn;
                    //     }
                    // }
                    // 1. update elements
                    draft.elements[action.payload.tableId].value.columns =
                        newColumns;
                    draft.elements[action.payload.tableId].height +=
                        TABLE_ROW_HEIGHT;

                    // 2. update focused inside element
                    // draft.focusedInsideElement = {
                    //     elementId: action.payload.tableId,
                    //     insideElementId: newColumnId,
                    //     insideElement: newColumn,
                    // };
                    const newInsideElement =
                        draft.elements[action.payload.tableId].value.columns[
                        newColumnId
                        ];

                    // TODO: use just indexNumber
                    // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[newColumnId].indexNumber;

                    let newInsideElementIndex = 0;
                    const keys = Object.keys(
                        draft.elements[action.payload.tableId].value.columns
                    );

                    for (let i = 0; i < keys.length; i++) {
                        if (keys[i] === newColumnId) {
                            newInsideElementIndex = i;
                            break;
                        }
                    }

                    draft.focusedInsideElement = {
                        elementId: action.payload.tableId,
                        insideElementId: newColumnId,
                        insideElement: newInsideElement,
                        insideElementIndex: newInsideElementIndex,
                        insideElementType: 'column',
                    };

                    // 3. update focused elements
                    if (state.focusedElements[action.payload.tableId]) {
                        draft.focusedElements[
                            action.payload.tableId
                        ].value.columns = newColumns;
                    }

                    // -----------------------------------
                    // UPDATE LINES POSITIONS FROM/TO THE TABLE
                    const linesIds = [];
                    // find lines from/to the table
                    for (const key in draft.lines) {
                        const line = draft.lines[key];
                        const {
                            value: { fromElementId, toElementId },
                        } = line;

                        if (
                            [fromElementId, toElementId].includes(
                                action.payload.tableId
                            )
                        ) {
                            linesIds.push(line.id);
                        }
                    }

                    // update lines (need for update lines by updated columns positions/indexNumbers)
                    draft = updateLinesPaths(draft, linesIds);
                    // -----------------------------------

                    // ----------------------------------------------------------------
                    // HISTORY: GET VALUE
                    const historyValue = [
                        {
                            elementId: action.payload.tableId,
                            action: HISTORY.ADD_COLUMN,
                            prevValue: {
                                columnId: newColumn.id,
                            },
                            nextValue: {
                                valueType: 'after',
                                afterColumnId: action.payload.columnId,
                                column: newColumn,
                            },
                        },
                    ];
                    // ----------------------------------------------------------------
                    // HISTORY: SAVE VALUE
                    saveHistoryValue(state, draft, historyValue);
                    // ----------------------------------------------------------------


                    draft.isContentChanged = true;
                }

                break;
            }

            case diagramActionTypes.ADD_NEW_FIRST_COLUMN: {
                // 1. if true => find first inside element (first column) and make it focused
                //    if false => add new column
                if (action.payload.isMoveToNextInsideElement) {
                    const columnsKeys = Object.keys(
                        draft.elements[action.payload.tableId].value.columns
                    );

                    // 1.1. MAKE FOCUS ON THE FIRST COLUMN
                    if (columnsKeys?.length && columnsKeys[0]) {
                        const firstColumnKey = columnsKeys[0];
                        const newInsideElement =
                            draft.elements[action.payload.tableId].value
                                .columns[firstColumnKey];

                        let newInsideElementIndex = 0;

                        draft.focusedInsideElement = {
                            elementId: action.payload.tableId,
                            insideElementId: firstColumnKey,
                            insideElement: newInsideElement,
                            insideElementIndex: newInsideElementIndex,
                            insideElementType: 'column',
                        };

                        break;

                        // 1.2. ADD NEW COLUMN BELOW
                    } else {
                        if (!checkColumns(draft.elements, action.payload.tableId)) {
                            draft.isModalSignInRestrictEnabled = true;
                            draft.modalSignInRestrictType = 'columns';
                            break;
                        }

                        const newColumnId = nanoid();
                        const newColumn = {
                            id: newColumnId,
                            name: 'column_new',
                            type: 'int',
                            indexNumber: 0,
                        };

                        const newColumns = {};
                        newColumns[newColumn.id] = newColumn;

                        // reorder columns indexNumbers
                        Object.keys(
                            draft.elements[action.payload.tableId].value.columns
                        ).forEach((columnKey, index) => {
                            let column =
                                draft.elements[action.payload.tableId].value
                                    .columns[columnKey];
                            column.indexNumber =
                                (column.indexNumber || index) + 1;

                            newColumns[column.id] = column;
                        });

                        // 1. update elements
                        draft.elements[action.payload.tableId].value.columns =
                            newColumns;
                        draft.elements[action.payload.tableId].height +=
                            TABLE_ROW_HEIGHT;

                        // 2. update focused inside element
                        // draft.focusedInsideElement = {
                        //     elementId: action.payload.tableId,
                        //     insideElementId: newColumnId,
                        //     insideElement: newColumn,
                        // };

                        const newInsideElement =
                            draft.elements[action.payload.tableId].value
                                .columns[newColumnId];

                        // TODO: use just indexNumber
                        // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[newColumnId].indexNumber;

                        let newInsideElementIndex = 0;
                        const keys = Object.keys(
                            draft.elements[action.payload.tableId].value.columns
                        );

                        for (let i = 0; i < keys.length; i++) {
                            if (keys[i] === newColumnId) {
                                newInsideElementIndex = i;
                                break;
                            }
                        }

                        draft.focusedInsideElement = {
                            elementId: action.payload.tableId,
                            insideElementId: newColumnId,
                            insideElement: newInsideElement,
                            insideElementIndex: newInsideElementIndex,
                            insideElementType: 'column',
                        };

                        // 3. update focused elements
                        draft.focusedElements = {
                            [action.payload.tableId]:
                                draft.elements[action.payload.tableId],
                        };

                        // 4. update focused elements ids
                        draft.focusedElementsIds = [action.payload.tableId];

                        // // 3. update focused elements
                        // if (state.focusedElements[action.payload.tableId]) {
                        //     draft.focusedElements[action.payload.tableId].value.columns = newColumns;
                        // }

                        // -----------------------------------
                        // UPDATE LINES POSITIONS FROM/TO THE TABLE
                        const linesIds = [];
                        // find lines from/to the table
                        for (const key in draft.lines) {
                            const line = draft.lines[key];
                            const {
                                value: { fromElementId, toElementId },
                            } = line;

                            if (
                                [fromElementId, toElementId].includes(
                                    action.payload.tableId
                                )
                            ) {
                                linesIds.push(line.id);
                            }
                        }

                        // update lines (need for update lines by updated columns positions/indexNumbers)
                        draft = updateLinesPaths(draft, linesIds);
                        // -----------------------------------

                        // ----------------------------------------------------------------
                        // HISTORY: GET VALUE
                        const historyValue = [
                            {
                                elementId: action.payload.tableId,
                                action: HISTORY.ADD_COLUMN,
                                prevValue: {
                                    columnId: newColumn.id,
                                },
                                nextValue: {
                                    valueType: 'start',
                                    column: newColumn,
                                },
                            },
                        ];
                        // ----------------------------------------------------------------
                        // HISTORY: SAVE VALUE
                        saveHistoryValue(state, draft, historyValue);
                        // ----------------------------------------------------------------

                        draft.isContentChanged = true;
                    }

                    // 2. if false => create new first column and make in focused
                } else {
                    if (!checkColumns(draft.elements, action.payload.tableId)) {
                        draft.isModalSignInRestrictEnabled = true;
                        draft.modalSignInRestrictType = 'columns';
                        break;
                    }

                    const newColumnId = nanoid();
                    const newColumn = {
                        id: newColumnId,
                        name: 'column_new',
                        type: 'int',
                        indexNumber: 0,
                    };

                    const newColumns = {};
                    newColumns[newColumn.id] = newColumn;

                    // reorder columns indexNumbers
                    Object.keys(
                        draft.elements[action.payload.tableId].value.columns
                    ).forEach((columnKey, index) => {
                        let column =
                            draft.elements[action.payload.tableId].value
                                .columns[columnKey];
                        column.indexNumber = (column.indexNumber || index) + 1;

                        newColumns[column.id] = column;
                    });

                    // 1. update elements
                    draft.elements[action.payload.tableId].value.columns =
                        newColumns;
                    draft.elements[action.payload.tableId].height +=
                        TABLE_ROW_HEIGHT;

                    // 2. update focused inside element
                    // draft.focusedInsideElement = {
                    //     elementId: action.payload.tableId,
                    //     insideElementId: newColumnId,
                    //     insideElement: newColumn,
                    // };

                    const newInsideElement =
                        draft.elements[action.payload.tableId].value.columns[
                        newColumnId
                        ];

                    // TODO: use just indexNumber
                    // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[newColumnId].indexNumber;

                    let newInsideElementIndex = 0;
                    const keys = Object.keys(
                        draft.elements[action.payload.tableId].value.columns
                    );

                    for (let i = 0; i < keys.length; i++) {
                        if (keys[i] === newColumnId) {
                            newInsideElementIndex = i;
                            break;
                        }
                    }

                    draft.focusedInsideElement = {
                        elementId: action.payload.tableId,
                        insideElementId: newColumnId,
                        insideElement: newInsideElement,
                        insideElementIndex: newInsideElementIndex,
                        insideElementType: 'column',
                    };

                    // 3. update focused elements
                    draft.focusedElements = {
                        [action.payload.tableId]:
                            draft.elements[action.payload.tableId],
                    };

                    // 4. update focused elements ids
                    draft.focusedElementsIds = [action.payload.tableId];

                    // -----------------------------------
                    // UPDATE LINES POSITIONS FROM/TO THE TABLE
                    const linesIds = [];
                    // find lines from/to the table
                    for (const key in draft.lines) {
                        const line = draft.lines[key];
                        const {
                            value: { fromElementId, toElementId },
                        } = line;

                        if (
                            [fromElementId, toElementId].includes(
                                action.payload.tableId
                            )
                        ) {
                            linesIds.push(line.id);
                        }
                    }

                    // update lines (need for update lines by updated columns positions/indexNumbers)
                    draft = updateLinesPaths(draft, linesIds);
                    // -----------------------------------

                    // ----------------------------------------------------------------
                    // HISTORY: GET VALUE
                    const historyValue = [
                        {
                            elementId: action.payload.tableId,
                            action: HISTORY.ADD_COLUMN,
                            prevValue: {
                                columnId: newColumn.id,
                            },
                            nextValue: {
                                valueType: 'start',
                                column: newColumn,
                            },
                        },
                    ];
                    // ----------------------------------------------------------------
                    // HISTORY: SAVE VALUE
                    saveHistoryValue(state, draft, historyValue);
                    // ----------------------------------------------------------------

                    draft.isContentChanged = true;
                }

                break;
            }

            case diagramActionTypes.FOCUS_ON_FIRST_COLUMN: {
                // 1.1. MAKE FOCUS ON THE FIRST COLUMN

                const columnsKeys = Object.keys(
                    draft.elements[action.payload.tableId].value.columns
                );

                if (columnsKeys?.length && columnsKeys[0]) {
                    const firstColumnKey = columnsKeys[0];
                    const newInsideElement =
                        draft.elements[action.payload.tableId].value.columns[
                        firstColumnKey
                        ];

                    let newInsideElementIndex = 0;

                    draft.focusedInsideElement = {
                        elementId: action.payload.tableId,
                        insideElementId: firstColumnKey,
                        insideElement: newInsideElement,
                        insideElementIndex: newInsideElementIndex,
                        insideElementType: 'column',
                    };
                }

                break;
            }

            case diagramActionTypes.ADD_NEW_COLUMN_IN_THE_END: {
                if (!checkColumns(draft.elements, action.payload.tableId)) {
                    draft.isModalSignInRestrictEnabled = true;
                    draft.modalSignInRestrictType = 'columns';
                    break;
                }

                const newColumnId = nanoid();
                const newColumnIndex = Object.keys(
                    draft.elements[action.payload.tableId].value.columns
                ).length;
                const newColumn = {
                    id: newColumnId,
                    name: 'column_new',
                    type: 'int',
                    indexNumber: newColumnIndex,
                };

                // 1. update elements
                draft.elements[action.payload.tableId].value.columns[
                    newColumnId
                ] = newColumn;
                draft.elements[action.payload.tableId].height +=
                    TABLE_ROW_HEIGHT;

                // 2. update focused inside element
                // draft.focusedInsideElement = {
                //     elementId: action.payload.tableId,
                //     insideElementId: newColumnId,
                //     insideElement: newColumn,
                // };

                const newInsideElement =
                    draft.elements[action.payload.tableId].value.columns[
                    newColumnId
                    ];

                // TODO: use just indexNumber
                // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[newColumnId].indexNumber;

                let newInsideElementIndex = 0;
                const keys = Object.keys(
                    draft.elements[action.payload.tableId].value.columns
                );

                for (let i = 0; i < keys.length; i++) {
                    if (keys[i] === newColumnId) {
                        newInsideElementIndex = i;
                        break;
                    }
                }

                draft.focusedInsideElement = {
                    elementId: action.payload.tableId,
                    insideElementId: newColumnId,
                    insideElement: newInsideElement,
                    insideElementIndex: newInsideElementIndex,
                    insideElementType: 'column',
                };

                // 3. update focused elements
                draft.focusedElements = {
                    [action.payload.tableId]:
                        draft.elements[action.payload.tableId],
                };

                // 4. update focused elements ids
                draft.focusedElementsIds = [action.payload.tableId];
                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.ADD_COLUMN,
                        prevValue: {
                            columnId: newColumn.id,
                        },
                        nextValue: {
                            valueType: 'end',
                            column: newColumn,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.ADD_NEW_COLUMN_IN_THE_START: {
                if (!checkColumns(draft.elements, action.payload.tableId)) {
                    draft.isModalSignInRestrictEnabled = true;
                    draft.modalSignInRestrictType = 'columns';
                    break;
                }

                const newColumnId = nanoid();
                const newColumn = {
                    id: newColumnId,
                    name: 'column_new',
                    type: 'int',
                    indexNumber: 0,
                };

                const newColumns = {};
                newColumns[newColumn.id] = newColumn;

                // reorder columns indexNumbers
                Object.keys(
                    draft.elements[action.payload.tableId].value.columns
                ).forEach((columnKey, index) => {
                    let column =
                        draft.elements[action.payload.tableId].value.columns[
                        columnKey
                        ];
                    column.indexNumber = (column.indexNumber || index) + 1;

                    newColumns[column.id] = column;
                });

                // 1. update elements
                draft.elements[action.payload.tableId].value.columns =
                    newColumns;
                draft.elements[action.payload.tableId].height +=
                    TABLE_ROW_HEIGHT;

                // 2. update focused inside element
                // draft.focusedInsideElement = {
                //     elementId: action.payload.tableId,
                //     insideElementId: newColumnId,
                //     insideElement: newColumn,
                // };

                const newInsideElement =
                    draft.elements[action.payload.tableId].value.columns[
                    newColumnId
                    ];

                // TODO: use just indexNumber
                // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[newColumnId].indexNumber;

                let newInsideElementIndex = 0;
                const keys = Object.keys(
                    draft.elements[action.payload.tableId].value.columns
                );

                for (let i = 0; i < keys.length; i++) {
                    if (keys[i] === newColumnId) {
                        newInsideElementIndex = i;
                        break;
                    }
                }

                draft.focusedInsideElement = {
                    elementId: action.payload.tableId,
                    insideElementId: newColumnId,
                    insideElement: newInsideElement,
                    insideElementIndex: newInsideElementIndex,
                    insideElementType: 'column',
                };

                // 3. update focused elements
                draft.focusedElements = {
                    [action.payload.tableId]:
                        draft.elements[action.payload.tableId],
                };

                // 4. update focused elements ids
                draft.focusedElementsIds = [action.payload.tableId];

                // -----------------------------------
                // UPDATE LINES POSITIONS FROM/TO THE TABLE
                const linesIds = [];
                // find lines from/to the table
                for (const key in draft.lines) {
                    const line = draft.lines[key];
                    const {
                        value: { fromElementId, toElementId },
                    } = line;

                    if (
                        [fromElementId, toElementId].includes(
                            action.payload.tableId
                        )
                    ) {
                        linesIds.push(line.id);
                    }
                }

                // update lines (need for update lines by updated columns positions/indexNumbers)
                draft = updateLinesPaths(draft, linesIds);
                // -----------------------------------

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.ADD_COLUMN,
                        prevValue: {
                            columnId: newColumn.id,
                        },
                        nextValue: {
                            valueType: 'start',
                            column: newColumn,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.MOVE_COLUMN_ABOVE: {
                const movableColumn =
                    state.elements[action.payload.tableId].value.columns[
                    action.payload.columnId
                    ];
                let movableColumnIndex = 0;

                const newColumns = {};

                const columnsKeys = Object.keys(
                    state.elements[action.payload.tableId].value.columns
                );
                for (let i = 0; i < columnsKeys.length; i++) {
                    if (columnsKeys[i] == movableColumn.id) {
                        movableColumnIndex = i;
                        break;
                    }
                }

                if (!movableColumnIndex) {
                    return;
                }

                let newMovableColumnIndex = movableColumnIndex - 1;
                // reorder columns indexNumbers
                columnsKeys.forEach((key, index) => {
                    if (
                        index < newMovableColumnIndex ||
                        index > movableColumnIndex
                    ) {
                        const column =
                            state.elements[action.payload.tableId].value
                                .columns[key];
                        newColumns[column.id] = {
                            ...column,
                            indexNumber: index,
                        };
                    } else if (index == newMovableColumnIndex) {
                        newColumns[movableColumn.id] = {
                            ...movableColumn,
                            indexNumber: index,
                        };
                    } else if (index == movableColumnIndex) {
                        const columnKey = columnsKeys[index - 1];
                        const column =
                            state.elements[action.payload.tableId].value
                                .columns[columnKey];
                        newColumns[column.id] = {
                            ...column,
                            indexNumber: index,
                        };
                    }
                });

                // 1. update elements
                draft.elements[action.payload.tableId].value.columns =
                    newColumns;

                // 2. update focused inside element
                // draft.focusedInsideElement = {
                //     elementId: action.payload.tableId,
                //     insideElementId: newColumnId,
                //     insideElement: newColumn,
                // };

                const newInsideElement =
                    draft.elements[action.payload.tableId].value.columns[
                    movableColumn.id
                    ];

                draft.focusedInsideElement = {
                    elementId: action.payload.tableId,
                    insideElementId: movableColumn.id,
                    insideElement: newInsideElement,
                    insideElementIndex: newMovableColumnIndex,
                    insideElementType: 'column',
                };

                // 3. update focused elements
                draft.focusedElements = {
                    [action.payload.tableId]:
                        draft.elements[action.payload.tableId],
                };

                // 4. update focused elements ids
                draft.focusedElementsIds = [action.payload.tableId];

                // -----------------------------------
                // UPDATE LINES POSITIONS FROM/TO THE TABLE
                const linesIds = [];
                // find lines from/to the table
                for (const key in draft.lines) {
                    const line = draft.lines[key];
                    const {
                        value: { fromElementId, toElementId },
                    } = line;

                    if (
                        [fromElementId, toElementId].includes(
                            action.payload.tableId
                        )
                    ) {
                        linesIds.push(line.id);
                    }
                }

                // update lines (need for update lines by updated columns positions/indexNumbers)
                draft = updateLinesPaths(draft, linesIds);
                // -----------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.MOVE_COLUMN_BELOW: {
                const movableColumn =
                    state.elements[action.payload.tableId].value.columns[
                    action.payload.columnId
                    ];
                let movableColumnIndex = 0;

                const newColumns = {};

                const columnsKeys = Object.keys(
                    state.elements[action.payload.tableId].value.columns
                );
                for (let i = 0; i < columnsKeys.length; i++) {
                    if (columnsKeys[i] == movableColumn.id) {
                        movableColumnIndex = i;
                        break;
                    }
                }

                if (movableColumnIndex == columnsKeys.length - 1) {
                    return;
                }

                let newMovableColumnIndex = movableColumnIndex + 1;
                // reorder columns indexNumbers
                columnsKeys.forEach((key, index) => {
                    if (
                        index < movableColumnIndex ||
                        index > newMovableColumnIndex
                    ) {
                        const column =
                            state.elements[action.payload.tableId].value
                                .columns[key];
                        newColumns[column.id] = {
                            ...column,
                            indexNumber: index,
                        };
                    } else if (index == movableColumnIndex) {
                        const columnKey = columnsKeys[index + 1];
                        const column =
                            state.elements[action.payload.tableId].value
                                .columns[columnKey];
                        newColumns[column.id] = {
                            ...column,
                            indexNumber: index,
                        };
                    } else if (index == newMovableColumnIndex) {
                        newColumns[movableColumn.id] = {
                            ...movableColumn,
                            indexNumber: index,
                        };
                    }
                });

                // 1. update elements
                draft.elements[action.payload.tableId].value.columns =
                    newColumns;

                // 2. update focused inside element
                // draft.focusedInsideElement = {
                //     elementId: action.payload.tableId,
                //     insideElementId: newColumnId,
                //     insideElement: newColumn,
                // };

                const newInsideElement =
                    draft.elements[action.payload.tableId].value.columns[
                    movableColumn.id
                    ];

                draft.focusedInsideElement = {
                    elementId: action.payload.tableId,
                    insideElementId: movableColumn.id,
                    insideElement: newInsideElement,
                    insideElementIndex: newMovableColumnIndex,
                    insideElementType: 'column',
                };

                // 3. update focused elements
                draft.focusedElements = {
                    [action.payload.tableId]:
                        draft.elements[action.payload.tableId],
                };

                // 4. update focused elements ids
                draft.focusedElementsIds = [action.payload.tableId];

                // -----------------------------------
                // UPDATE LINES POSITIONS FROM/TO THE TABLE
                const linesIds = [];
                // find lines from/to the table
                for (const key in draft.lines) {
                    const line = draft.lines[key];
                    const {
                        value: { fromElementId, toElementId },
                    } = line;

                    if (
                        [fromElementId, toElementId].includes(
                            action.payload.tableId
                        )
                    ) {
                        linesIds.push(line.id);
                    }
                }

                // update lines (need for update lines by updated columns positions/indexNumbers)
                draft = updateLinesPaths(draft, linesIds);
                // -----------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.DUPLICATE_COLUMN: {
                if (!checkColumns(draft.elements, action.payload.tableId)) {
                    draft.isModalSignInRestrictEnabled = true;
                    draft.modalSignInRestrictType = 'columns';
                    break;
                }

                const prevColumn = state.elements[action.payload.tableId].value.columns[action.payload.columnId];

                const newColumnId = nanoid();
                const newColumn = {
                    ...prevColumn,
                    id: newColumnId,
                };

                delete newColumn.isRelated;

                const newColumns = {};
                const columns = state.elements[action.payload.tableId].value.columns;

                let hasNewColumn = false;
                Object.keys(columns).forEach((id, index) => {
                    if (hasNewColumn) {
                        newColumns[id] = {
                            ...columns[id],
                            indexNumber: index + 1,
                        };
                    } else {
                        newColumns[id] = columns[id];
                    }

                    if (id === action.payload.columnId) {
                        newColumns[newColumnId] = {
                            ...newColumn,
                            indexNumber: index + 1,
                        };
                        hasNewColumn = true;
                    }
                });

                // 1. update elements
                draft.elements[action.payload.tableId].value.columns = newColumns;
                draft.elements[action.payload.tableId].height += TABLE_ROW_HEIGHT;

                const newInsideElement =
                    draft.elements[action.payload.tableId].value.columns[
                    newColumnId
                    ];

                // TODO: use just indexNumber
                // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[newColumnId].indexNumber;

                let newInsideElementIndex = 0;
                const keys = Object.keys(
                    draft.elements[action.payload.tableId].value.columns
                );

                for (let i = 0; i < keys.length; i++) {
                    if (keys[i] === newColumnId) {
                        newInsideElementIndex = i;
                        break;
                    }
                }

                draft.focusedInsideElement = {
                    elementId: action.payload.tableId,
                    insideElementId: newColumnId,
                    insideElement: newInsideElement,
                    insideElementIndex: newInsideElementIndex,
                    insideElementType: 'column',
                };

                // 3. update focused elements
                if (state.focusedElements[action.payload.tableId]) {
                    draft.focusedElements[
                        action.payload.tableId
                    ].value.columns = newColumns;
                }

                // -----------------------------------
                // UPDATE LINES POSITIONS FROM/TO THE TABLE
                const linesIds = [];
                // find lines from/to the table
                for (const key in draft.lines) {
                    const line = draft.lines[key];
                    const {
                        value: { fromElementId, toElementId },
                    } = line;

                    if (
                        [fromElementId, toElementId].includes(
                            action.payload.tableId
                        )
                    ) {
                        linesIds.push(line.id);
                    }
                }

                // update lines (need for update lines by updated columns positions/indexNumbers)
                draft = updateLinesPaths(draft, linesIds);

                // -----------------------------------

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        elementId: action.payload.tableId,
                        action: HISTORY.ADD_COLUMN,
                        prevValue: {
                            columnId: newColumn.id,
                        },
                        nextValue: {
                            valueType: 'after',
                            afterColumnId: action.payload.columnId,
                            column: newColumn,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_LINE_CONNECTORS_REVERSED: {
                // 1. update lines
                draft.lines[action.payload.lineId].value.fromRelationType =
                    state.lines[action.payload.lineId].value.toRelationType;
                draft.lines[action.payload.lineId].value.toRelationType =
                    state.lines[action.payload.lineId].value.fromRelationType;
                draft.focusedLine = draft.lines[action.payload.lineId];

                const line = draft.lines[action.payload.lineId];
                const {
                    value: {
                        fromElementId,
                        fromInsideElementId,
                        fromRelationType,
                        toElementId,
                        toInsideElementId,
                        toRelationType,
                    },
                } = line;

                const fromElement = state.elements[fromElementId];
                const toElement = state.elements[toElementId];

                if (!fromElement || !toElement) {
                    return;
                }
                const fromInsideElement =
                    fromElement.value.columns[fromInsideElementId];
                const toInsideElement =
                    toElement.value.columns[toInsideElementId];

                if (!fromInsideElement || !toInsideElement) {
                    return;
                }

                const lineData = {
                    mouseX: null,
                    mouseY: null,

                    fromElementType: fromElement.type,
                    fromElementWidth: fromElement.width,
                    fromElementX: fromElement.x,
                    fromElementY: fromElement.y,
                    fromInsideElementIndex:
                        fromElement.value.columns[fromInsideElementId]
                            .indexNumber,
                    fromRelationType,

                    toElementType: toElement.type,
                    toElementWidth: toElement.width,
                    toElementX: toElement.x,
                    toElementY: toElement.y,
                    toInsideElementIndex:
                        toElement.value.columns[toInsideElementId].indexNumber,
                    toRelationType,
                };

                const path = _buildLinePath(lineData);

                draft.lines[action.payload.lineId].value.path = path;
                draft.focusedLine = draft.lines[action.payload.lineId];

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        lineId: action.payload.lineId,
                        action: HISTORY.UPDATE_LINE_CONNECTOR,
                        prevValue: {
                            fromRelationType:
                                state.lines[action.payload.lineId].value
                                    .fromRelationType,
                            toRelationType:
                                state.lines[action.payload.lineId].value
                                    .toRelationType,
                        },
                        nextValue: {
                            fromRelationType:
                                draft.lines[action.payload.lineId].value
                                    .fromRelationType,
                            toRelationType:
                                draft.lines[action.payload.lineId].value
                                    .toRelationType,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_LINE_CONNECTOR: {
                // action.payload.lineId
                // action.payload.relationType = | 'one' | 'many'
                // action.payload.direction = 'from' | 'to'

                // 1. update lines
                if (action.payload.direction === 'from') {
                    draft.lines[action.payload.lineId].value.fromRelationType =
                        action.payload.relationType;
                } else if (action.payload.direction === 'to') {
                    draft.lines[action.payload.lineId].value.toRelationType =
                        action.payload.relationType;
                } else {
                    return;
                }

                draft.focusedLine = draft.lines[action.payload.lineId];

                const line = draft.lines[action.payload.lineId];
                const {
                    value: {
                        fromElementId,
                        fromInsideElementId,
                        fromRelationType,
                        toElementId,
                        toInsideElementId,
                        toRelationType,
                    },
                } = line;

                const fromElement = state.elements[fromElementId];
                const toElement = state.elements[toElementId];

                if (!fromElement || !toElement) {
                    return;
                }
                const fromInsideElement =
                    fromElement.value.columns[fromInsideElementId];
                const toInsideElement =
                    toElement.value.columns[toInsideElementId];

                if (!fromInsideElement || !toInsideElement) {
                    return;
                }

                const lineData = {
                    mouseX: null,
                    mouseY: null,

                    fromElementType: fromElement.type,
                    fromElementWidth: fromElement.width,
                    fromElementX: fromElement.x,
                    fromElementY: fromElement.y,
                    fromInsideElementIndex:
                        fromElement.value.columns[fromInsideElementId]
                            .indexNumber,
                    fromRelationType,

                    toElementType: toElement.type,
                    toElementWidth: toElement.width,
                    toElementX: toElement.x,
                    toElementY: toElement.y,
                    toInsideElementIndex:
                        toElement.value.columns[toInsideElementId].indexNumber,
                    toRelationType,
                };

                const path = _buildLinePath(lineData);

                draft.lines[action.payload.lineId].value.path = path;
                draft.focusedLine = draft.lines[action.payload.lineId];

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        lineId: action.payload.lineId,
                        action: HISTORY.UPDATE_LINE_CONNECTOR,
                        prevValue: {
                            fromRelationType:
                                state.lines[action.payload.lineId].value
                                    .fromRelationType,
                            toRelationType:
                                state.lines[action.payload.lineId].value
                                    .toRelationType,
                        },
                        nextValue: {
                            fromRelationType:
                                draft.lines[action.payload.lineId].value
                                    .fromRelationType,
                            toRelationType:
                                draft.lines[action.payload.lineId].value
                                    .toRelationType,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_LINE_SOLID_TYPE: {
                // action.payload.lineId
                // action.payload.solidType = | 'solid' | 'dashes' | 'dots'

                // 1. update lines
                draft.lines[action.payload.lineId].value.solidType =
                    action.payload.solidType;

                draft.focusedLine = draft.lines[action.payload.lineId];

                const line = draft.lines[action.payload.lineId];
                const {
                    value: {
                        fromElementId,
                        fromInsideElementId,
                        fromRelationType,
                        toElementId,
                        toInsideElementId,
                        toRelationType,
                    },
                } = line;

                const fromElement = state.elements[fromElementId];
                const toElement = state.elements[toElementId];

                if (!fromElement || !toElement) {
                    return;
                }
                const fromInsideElement =
                    fromElement.value.columns[fromInsideElementId];
                const toInsideElement =
                    toElement.value.columns[toInsideElementId];

                if (!fromInsideElement || !toInsideElement) {
                    return;
                }

                const lineData = {
                    mouseX: null,
                    mouseY: null,

                    fromElementType: fromElement.type,
                    fromElementWidth: fromElement.width,
                    fromElementX: fromElement.x,
                    fromElementY: fromElement.y,
                    fromInsideElementIndex:
                        fromElement.value.columns[fromInsideElementId]
                            .indexNumber,
                    fromRelationType,

                    toElementType: toElement.type,
                    toElementWidth: toElement.width,
                    toElementX: toElement.x,
                    toElementY: toElement.y,
                    toInsideElementIndex:
                        toElement.value.columns[toInsideElementId].indexNumber,
                    toRelationType,
                };

                const path = _buildLinePath(lineData);

                draft.lines[action.payload.lineId].value.path = path;
                draft.focusedLine = draft.lines[action.payload.lineId];

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        lineId: action.payload.lineId,
                        action: HISTORY.UPDATE_LINE_BORDER,
                        prevValue: {
                            solidType:
                                state.lines[action.payload.lineId].value
                                    .solidType,
                        },
                        nextValue: {
                            solidType:
                                draft.lines[action.payload.lineId].value
                                    .solidType,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_LINE_ON_TRIGGER: {
                // action.payload.lineId
                // action.payload.triggerType = 'update', 'delete'
                // action.payload.value = 'cascade', 'set_null', 'restrict', 'no_action', 'set_default'

                const isOnUpdateTrigger = action.payload.triggerType == 'update';

                // 1. update lines
                if (isOnUpdateTrigger) {
                    draft.lines[action.payload.lineId].value.onUpdateTrigger = action.payload.value;
                } else {
                    draft.lines[action.payload.lineId].value.onDeleteTrigger = action.payload.value;
                }

                draft.focusedLine = draft.lines[action.payload.lineId];

                const line = draft.lines[action.payload.lineId];
                const {
                    value: {
                        fromElementId,
                        fromInsideElementId,
                        fromRelationType,
                        toElementId,
                        toInsideElementId,
                        toRelationType,
                    },
                } = line;

                const fromElement = state.elements[fromElementId];
                const toElement = state.elements[toElementId];

                if (!fromElement || !toElement) {
                    return;
                }
                const fromInsideElement =
                    fromElement.value.columns[fromInsideElementId];
                const toInsideElement =
                    toElement.value.columns[toInsideElementId];

                if (!fromInsideElement || !toInsideElement) {
                    return;
                }

                const lineData = {
                    mouseX: null,
                    mouseY: null,

                    fromElementType: fromElement.type,
                    fromElementWidth: fromElement.width,
                    fromElementX: fromElement.x,
                    fromElementY: fromElement.y,
                    fromInsideElementIndex:
                        fromElement.value.columns[fromInsideElementId]
                            .indexNumber,
                    fromRelationType,

                    toElementType: toElement.type,
                    toElementWidth: toElement.width,
                    toElementX: toElement.x,
                    toElementY: toElement.y,
                    toInsideElementIndex:
                        toElement.value.columns[toInsideElementId].indexNumber,
                    toRelationType,
                };

                const path = _buildLinePath(lineData);

                draft.lines[action.payload.lineId].value.path = path;
                draft.focusedLine = draft.lines[action.payload.lineId];

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        lineId: action.payload.lineId,
                        action: HISTORY.UPDATE_LINE_BORDER,
                        prevValue: {
                            triggerType: state.lines[action.payload.lineId].value.triggerType,
                        },
                        nextValue: {
                            triggerType: draft.lines[action.payload.lineId].value.triggerType,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.SET_LINE_COLOR: {
                // action.payload.lineId
                // action.payload.color

                // 1. update lines
                draft.lines[action.payload.lineId].value.color =
                    action.payload.color;

                draft.focusedLine = draft.lines[action.payload.lineId];

                const line = draft.lines[action.payload.lineId];
                const {
                    value: {
                        fromElementId,
                        fromInsideElementId,
                        fromRelationType,
                        toElementId,
                        toInsideElementId,
                        toRelationType,
                    },
                } = line;

                const fromElement = state.elements[fromElementId];
                const toElement = state.elements[toElementId];

                if (!fromElement || !toElement) {
                    return;
                }
                const fromInsideElement =
                    fromElement.value.columns[fromInsideElementId];
                const toInsideElement =
                    toElement.value.columns[toInsideElementId];

                if (!fromInsideElement || !toInsideElement) {
                    return;
                }

                const lineData = {
                    mouseX: null,
                    mouseY: null,

                    fromElementType: fromElement.type,
                    fromElementWidth: fromElement.width,
                    fromElementX: fromElement.x,
                    fromElementY: fromElement.y,
                    fromInsideElementIndex:
                        fromElement.value.columns[fromInsideElementId]
                            .indexNumber,
                    fromRelationType,

                    toElementType: toElement.type,
                    toElementWidth: toElement.width,
                    toElementX: toElement.x,
                    toElementY: toElement.y,
                    toInsideElementIndex:
                        toElement.value.columns[toInsideElementId].indexNumber,
                    toRelationType,
                };

                const path = _buildLinePath(lineData);

                draft.lines[action.payload.lineId].value.path = path;
                draft.focusedLine = draft.lines[action.payload.lineId];

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                const historyValue = [
                    {
                        lineId: action.payload.lineId,
                        action: HISTORY.UPDATE_LINE_COLOR,
                        prevValue: {
                            color: state.lines[action.payload.lineId].value
                                .color,
                        },
                        nextValue: {
                            color: draft.lines[action.payload.lineId].value
                                .color,
                        },
                    },
                ];
                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }
            case diagramActionTypes.SHOW_CONTEXT_MENU: {
                draft.isContextMenuShowed = true;
                break;
            }
            case diagramActionTypes.HIDE_CONTEXT_MENU: {
                draft.isContextMenuShowed = false;
                break;
            }
            case diagramActionTypes.TOGGLE_VISIBILITY_CONTEXT_MENU: {
                draft.isContextMenuVisible = !state.isContextMenuVisible;
                break;
            }

            case diagramActionTypes.FOCUS_ON_COLUMN_BELOW: {
                const columnsKeys = Object.keys(
                    draft.elements[action.payload.tableId].value.columns
                );
                let prevColumnIndex = -1;
                for (let i = 0; i < columnsKeys.length; i++) {
                    if (columnsKeys[i] === action.payload.columnId) {
                        prevColumnIndex = i;
                        break;
                    }
                }

                // 1.1. MAKE FOCUS ON THE NEXT COLUMN
                if (
                    prevColumnIndex >= 0 &&
                    columnsKeys[prevColumnIndex] &&
                    columnsKeys[prevColumnIndex + 1]
                ) {
                    const nextColumnKey = columnsKeys[prevColumnIndex + 1];

                    const newInsideElement =
                        draft.elements[action.payload.tableId].value.columns[
                        nextColumnKey
                        ];

                    // TODO: use just indexNumber
                    // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[action.payload.columnId].indexNumber;

                    let newInsideElementIndex = 0;
                    const keys = Object.keys(
                        draft.elements[action.payload.tableId].value.columns
                    );

                    for (let i = 0; i < keys.length; i++) {
                        if (keys[i] === nextColumnKey) {
                            newInsideElementIndex = i;
                            break;
                        }
                    }

                    draft.focusedInsideElement = {
                        elementId: action.payload.tableId,
                        insideElementId: nextColumnKey,
                        insideElement: newInsideElement,
                        insideElementIndex: newInsideElementIndex,
                        insideElementType: 'column',
                    };
                }

                break;
            }
            case diagramActionTypes.FOCUS_ON_COLUMN_ABOVE: {
                const columnsKeys = Object.keys(
                    draft.elements[action.payload.tableId].value.columns
                );
                let prevColumnIndex = -1;
                for (let i = 0; i < columnsKeys.length; i++) {
                    if (columnsKeys[i] === action.payload.columnId) {
                        prevColumnIndex = i;
                        break;
                    }
                }

                // 1.1. MAKE FOCUS ON THE NEXT COLUMN
                if (
                    prevColumnIndex >= 1 &&
                    columnsKeys[prevColumnIndex] &&
                    columnsKeys[prevColumnIndex - 1]
                ) {
                    const nextColumnKey = columnsKeys[prevColumnIndex - 1];

                    const newInsideElement =
                        draft.elements[action.payload.tableId].value.columns[
                        nextColumnKey
                        ];

                    // TODO: use just indexNumber
                    // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[action.payload.columnId].indexNumber;

                    let newInsideElementIndex = 0;
                    const keys = Object.keys(
                        draft.elements[action.payload.tableId].value.columns
                    );

                    for (let i = 0; i < keys.length; i++) {
                        if (keys[i] === nextColumnKey) {
                            newInsideElementIndex = i;
                            break;
                        }
                    }

                    draft.focusedInsideElement = {
                        elementId: action.payload.tableId,
                        insideElementId: nextColumnKey,
                        insideElement: newInsideElement,
                        insideElementIndex: newInsideElementIndex,
                        insideElementType: 'column',
                    };
                } else if (
                    prevColumnIndex == 0 &&
                    columnsKeys[prevColumnIndex]
                ) {
                    // FOCUS ON THE TABLE HEADER
                    const newInsideElement =
                        draft.elements[action.payload.tableId].value.header;

                    draft.focusedInsideElement = {
                        elementId: action.payload.tableId,
                        insideElementId: null,
                        insideElement: newInsideElement,
                        insideElementIndex: null,
                        insideElementType: 'header',
                    };
                }

                break;
            }

            case diagramActionTypes.UPDATE_TABLE_COLUMN_NAME: {
                let isHistoryUpdated = false;
                // action.payload tableId, columnId, value, newWidth

                if (action.payload.tableId && action.payload.columnId) {
                    const prevColumn = state.elements[action.payload.tableId]?.value.columns[action.payload.columnId];

                    if (prevColumn?.name !== action.payload.value) {
                        draft.elements[action.payload.tableId].value.columns[action.payload.columnId].name = action.payload.value;
                        draft.elements[action.payload.tableId].width = action.payload.newWidth;

                        if (state.history.length) {
                            const historyLastItem = state.history[state.history.length - 1][0];

                            if (
                                historyLastItem.action == HISTORY.UPDATE_COLUMN_NAME &&
                                historyLastItem.elementId == action.payload.tableId &&
                                historyLastItem.prevValue.columnId == action.payload.columnId
                            ) {
                                isHistoryUpdated = true;
                                draft.history[state.history.length - 1][0].nextValue.name = action.payload.value;
                            }
                        }

                        if (!isHistoryUpdated) {
                            // ----------------------------------------------------------------
                            // HISTORY: GET VALUE
                            const historyValue = [
                                {
                                    elementId: action.payload.tableId,
                                    action: HISTORY.UPDATE_COLUMN_NAME,
                                    prevValue: {
                                        columnId: action.payload.columnId,
                                        name: state.elements[
                                            action.payload.tableId
                                        ].value.columns[action.payload.columnId]
                                            .name,
                                    },
                                    nextValue: {
                                        columnId: action.payload.columnId,
                                        name: draft.elements[
                                            action.payload.tableId
                                        ].value.columns[action.payload.columnId]
                                            .name,
                                    },
                                },
                            ];
                            // ----------------------------------------------------------------
                            // HISTORY: SAVE VALUE
                            saveHistoryValue(state, draft, historyValue);
                            // ----------------------------------------------------------------
                        }
                    }
                }

                draft.isContentChanged = true;
                break;
            }
            case diagramActionTypes.UPDATE_TABLE_COLUMN_TYPE: {
                let isHistoryUpdated = false;
                // action.payload tableId, columnId, value, newWidth

                if (action.payload.tableId && action.payload.columnId) {
                    const prevColumn = state.elements[action.payload.tableId]?.value.columns[action.payload.columnId];

                    if (prevColumn?.type !== action.payload.value) {
                        draft.elements[action.payload.tableId].value.columns[action.payload.columnId].type = action.payload.value;
                        draft.elements[action.payload.tableId].width = action.payload.newWidth;

                        if (state.history.length) {
                            const historyLastItem = state.history[state.history.length - 1][0];

                            if (
                                historyLastItem.action ==
                                HISTORY.UPDATE_COLUMN_TYPE &&
                                historyLastItem.elementId ==
                                action.payload.tableId &&
                                historyLastItem.prevValue.columnId ==
                                action.payload.columnId
                            ) {
                                isHistoryUpdated = true;
                                draft.history[
                                    state.history.length - 1
                                ][0].nextValue.type = action.payload.value;
                            }
                        }

                        if (!isHistoryUpdated) {
                            // ----------------------------------------------------------------
                            // HISTORY: GET VALUE
                            const historyValue = [
                                {
                                    elementId: action.payload.tableId,
                                    action: HISTORY.UPDATE_COLUMN_TYPE,
                                    prevValue: {
                                        columnId: action.payload.columnId,
                                        type: state.elements[
                                            action.payload.tableId
                                        ].value.columns[action.payload.columnId]
                                            .type,
                                    },
                                    nextValue: {
                                        columnId: action.payload.columnId,
                                        type: draft.elements[
                                            action.payload.tableId
                                        ].value.columns[action.payload.columnId]
                                            .type,
                                    },
                                },
                            ];
                            // ----------------------------------------------------------------
                            // HISTORY: SAVE VALUE
                            saveHistoryValue(state, draft, historyValue);
                            // ----------------------------------------------------------------
                        }
                    }
                }

                draft.isContentChanged = true;
                break;
            }
            case diagramActionTypes.UPDATE_TABLE_HEADER_NAME: {
                let isHistoryUpdated = false;
                // action.payload tableId, value

                if (action.payload.tableId) {
                    const prevHeader =
                        state.elements[action.payload.tableId]?.value.header;

                    if (prevHeader?.name !== action.payload.value) {
                        draft.elements[
                            action.payload.tableId
                        ].value.header.name = action.payload.value;

                        if (state.history.length) {
                            const historyLastItem =
                                state.history[state.history.length - 1][0];

                            if (
                                historyLastItem.action ==
                                HISTORY.UPDATE_TABLE_HEADER &&
                                historyLastItem.elementId ==
                                action.payload.tableId
                            ) {
                                isHistoryUpdated = true;
                                draft.history[
                                    state.history.length - 1
                                ][0].nextValue.name = action.payload.value;
                            }
                        }

                        if (!isHistoryUpdated) {
                            // ----------------------------------------------------------------
                            // HISTORY: GET VALUE
                            const historyValue = [
                                {
                                    elementId: action.payload.tableId,
                                    action: HISTORY.UPDATE_TABLE_HEADER,
                                    prevValue: {
                                        name: state.elements[
                                            action.payload.tableId
                                        ].value.header.name,
                                    },
                                    nextValue: {
                                        name: draft.elements[
                                            action.payload.tableId
                                        ].value.header.name,
                                    },
                                },
                            ];
                            // ----------------------------------------------------------------
                            // HISTORY: SAVE VALUE
                            saveHistoryValue(state, draft, historyValue);
                            // ----------------------------------------------------------------
                        }
                    }
                }

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.UPDATE_FOCUSED_INSIDE_ELEMENT: {
                if (!action.payload.tableId) {
                    draft.focusedInsideElement = null;
                } else {
                    if (action.payload.insideElementType === 'header') {
                        const newInsideElement =
                            draft.elements[action.payload.tableId].value.header;

                        draft.focusedInsideElement = {
                            elementId: action.payload.tableId,
                            insideElementId: null,
                            insideElement: newInsideElement,
                            insideElementIndex: null,
                            insideElementType: 'header',
                        };
                    } else {
                        const newInsideElement =
                            draft.elements[action.payload.tableId].value
                                .columns[action.payload.columnId];

                        // TODO: use just indexNumber
                        // const newInsideElementIndex = draft.elements[action.payload.tableId].value.columns[action.payload.columnId].indexNumber;

                        let newInsideElementIndex = 0;
                        const keys = Object.keys(
                            draft.elements[action.payload.tableId].value.columns
                        );

                        for (let i = 0; i < keys.length; i++) {
                            if (keys[i] === action.payload.columnId) {
                                newInsideElementIndex = i;
                                break;
                            }
                        }

                        draft.focusedInsideElement = {
                            elementId: action.payload.tableId,
                            insideElementId: action.payload.columnId,
                            insideElement: newInsideElement,
                            insideElementIndex: newInsideElementIndex,
                            insideElementType: 'column',
                        };
                    }
                }

                // if (action.payload.prevColumnState?.elementId && action.payload.prevColumnState?.insideElementId) {
                //     const prevColumn = state.elements[action.payload.prevColumnState?.elementId]?.value.columns[action.payload.prevColumnState?.insideElementId];

                //     if (prevColumn?.name !== action.payload.prevColumnState?.name) {
                //         draft.elements[action.payload.prevColumnState.elementId].value.columns[action.payload.prevColumnState.insideElementId].name = action.payload.prevColumnState?.name;
                //     }

                //     if (prevColumn?.type !== action.payload.prevColumnState?.type) {
                //         draft.elements[action.payload.prevColumnState.elementId].value.columns[action.payload.prevColumnState.insideElementId].type = action.payload.prevColumnState?.type;
                //     }
                // }

                break;
            }
            case diagramActionTypes.DELETE_LINE: {
                if (state.focusedLineId && draft.lines[state.focusedLineId]) {
                    // 1. delete from elements
                    delete draft.lines[state.focusedLineId];

                    // 2. delete from focused line
                    draft.focusedLine = null;

                    // 3. delete from focused line id
                    draft.focusedLineId = null;

                    const line = state.lines[state.focusedLineId];
                    const {
                        value: {
                            fromElementId,
                            fromInsideElementId,
                            toElementId,
                            toInsideElementId,
                        },
                    } = line;

                    draft.elements[fromElementId].value.columns[
                        fromInsideElementId
                    ].isRelated = false;
                    draft.elements[toElementId].value.columns[
                        toInsideElementId
                    ].isRelated = false;

                    if (
                        draft.focusedElementsIds.includes(fromElementId) &&
                        draft.focusedElements[fromElementId]
                    ) {
                        draft.focusedElements[fromElementId].value.columns[
                            fromInsideElementId
                        ].isRelated = false;
                    }
                    if (
                        draft.focusedElementsIds.includes(toElementId) &&
                        draft.focusedElements[toElementId]
                    ) {
                        draft.focusedElements[toElementId].value.columns[
                            toInsideElementId
                        ].isRelated = false;
                    }

                    // ----------------------------------------------------------------
                    // HISTORY: GET VALUE
                    const historyValue = [
                        {
                            lineId: state.focusedLineId,
                            action: HISTORY.DELETE_LINE,
                            prevValue: {
                                line: state.lines[state.focusedLineId],
                            },
                            nextValue: {
                                line: null,
                            },
                        },
                    ];
                    // ----------------------------------------------------------------
                    // HISTORY: SAVE VALUE
                    saveHistoryValue(state, draft, historyValue);
                    // ----------------------------------------------------------------
                }

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.TOGGLE_BTN_RED_GREEN_VIEW: {
                // draft.elementsChanges = getElementsChangesView(state.elementsLastCommited, state.elements);

                const { NdVmqb5y1G32E14VFk0qF, ...rest } =
                    state.elementsLastCommited;
                draft.elementsChanges = getElementsChangesView(
                    {
                        ...rest,
                        xdY00r0wTzNjGF7wnvITy: {
                            id: 'xdY00r0wTzNjGF7wnvITy',
                            type: 'table',
                            x: 200,
                            y: 350,
                            height: 1020,
                            width: 378,
                            value: {
                                header: {
                                    name: 'bo.accounts',
                                },
                                columns: {
                                    JcWXzFnkjlLwjBQ5rBC81: {
                                        id: 'JcWXzFnkjlLwjBQ5rBC81',
                                        name: 'balance',
                                        type: 'datetime',
                                        isNN: true,
                                    },
                                    '8v0UmP_iUtQjSlVrJW6j8': {
                                        id: '8v0UmP_iUtQjSlVrJW6j8',
                                        name: 'descristion',
                                        type: 'varchar(45)',
                                    },
                                    chsN5S23V9TxyOOqNl7hX: {
                                        id: 'chsN5S23V9TxyOOqNl7hX',
                                        name: 'description',
                                        // "name": "ckcalculated_at",
                                        type: 'tdate',
                                    },
                                    AIP2LCU1H8R_VkDXXMj16: {
                                        id: 'AIP2LCU1H8R_VkDXXMj16',
                                        name: 'dncid',
                                        type: 'int',
                                        isNN: true,
                                    },
                                    '2F4rOWeSINl_UApIVlCAX': {
                                        id: '2F4rOWeSINl_UApIVlCAX',
                                        name: 'created_at',
                                        type: 'datetime',
                                        index: {
                                            type: 'composite',
                                        },
                                    },
                                    jjN8jR1sjgcNhIjnrjz9t: {
                                        id: 'jjN8jR1sjgcNhIjnrjz9t',
                                        name: 'jxfloating_profit',
                                        type: 'decimal(20,6)',
                                    },
                                    h_pezAsrAt0iGxiBLInqa: {
                                        id: 'h_pezAsrAt0iGxiBLInqa',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    'JKQnmQDExFR3AbJEl-5GB': {
                                        id: 'JKQnmQDExFR3AbJEl-5GB',
                                        name: 'lzgroup_id',
                                        type: 'int',
                                        isNN: true,
                                        index: {
                                            type: 'fullText',
                                        },
                                    },
                                    'HhJD7v-MrQGGZl3dG5uJF': {
                                        id: 'HhJD7v-MrQGGZl3dG5uJF',
                                        name: 'mid',
                                        isFK: true,
                                        type: 'int',
                                    },
                                    '0EvMcIJof8Oeo2Diwmjpy': {
                                        id: '0EvMcIJof8Oeo2Diwmjpy',
                                        name: 'nleverage',
                                        type: 'decimal(10,2)',
                                    },
                                    '5MO2PckZQ8r-EpzDiBH8R': {
                                        id: '5MO2PckZQ8r-EpzDiBH8R',
                                        name: 'olifetime_profit',
                                        type: 'decimal(20,6)',
                                    },

                                    111: {
                                        id: '111',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    222: {
                                        id: '222',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    333: {
                                        id: '333',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },

                                    o84D75GxwZOhqLt2kKQKn: {
                                        id: 'o84D75GxwZOhqLt2kKQKn',
                                        name: 'pmargin',
                                        type: 'decimal(20,6)',
                                    },
                                    I8wxQYcICNnrpH6k6QGR1: {
                                        id: 'I8wxQYcICNnrpH6k6QGR1',
                                        name: 'qmargin_level',
                                        type: 'decimal(20,6)',
                                    },
                                    AAD1R3wxkU8lvXZoUrgOS: {
                                        id: 'AAD1R3wxkU8lvXZoUrgOS',
                                        name: 'rmessage_timestamp',
                                        type: 'datetime(3)',
                                        index: {
                                            type: 'composite',
                                        },
                                    },
                                    nRLtt8EfHinYnlo38uvrT: {
                                        id: 'nRLtt8EfHinYnlo38uvrT',
                                        name: 'smode',
                                        type: 'enum()',
                                    },
                                    COKdqhdkyV8VQonBm4itk: {
                                        id: 'COKdqhdkyV8VQonBm4itk',
                                        name: 'tmt_login',
                                        type: 'int',
                                    },
                                    'LvDiFosVAgZWBOh-myP93': {
                                        id: 'LvDiFosVAgZWBOh-myP93',
                                        name: 'umt_server_id',
                                        type: 'varchar(255)',
                                    },
                                    XHGTpinzVph9Ysz6kVEDQ: {
                                        id: 'XHGTpinzVph9Ysz6kVEDQ',
                                        name: 'vstatus_id',
                                        type: 'enum()',
                                    },
                                    '7Z03Um13_1dqxOhmNcL0z': {
                                        id: '7Z03Um13_1dqxOhmNcL0z',
                                        name: 'wsystem_balance',
                                        type: 'decimal(20,6)',
                                    },
                                    mOwtdW3gWbfNXmX0keL4M: {
                                        id: 'mOwtdW3gWbfNXmX0keL4M',
                                        name: 'xsystem_equity',
                                        type: 'decimal(20,6)',
                                    },
                                    '9LVBQ0YVvnvR8TsvWXxOT': {
                                        id: '9LVBQ0YVvnvR8TsvWXxOT',
                                        name: 'ysystem_free_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    'dwI9XVMJuZuv-RBZfebLa': {
                                        id: 'dwI9XVMJuZuv-RBZfebLa',
                                        name: 'ztrading_mode',
                                        type: 'enum()',
                                    },
                                    I5CWpDOZW_YA5sSMWeEBh: {
                                        id: 'I5CWpDOZW_YA5sSMWeEBh',
                                        name: 'updated_at',
                                        type: 'datetime',
                                    },
                                },
                            },
                        },
                        111222: {
                            id: '111222',
                            type: 'table',
                            x: -275,
                            y: 350,
                            height: 1020,
                            width: 378,
                            value: {
                                header: {
                                    name: 'bo.test',
                                },
                                columns: {
                                    JcWXzFnkjlLwjBQ5rBC81: {
                                        id: 'JcWXzFnkjlLwjBQ5rBC81',
                                        name: 'sum_balance',
                                        type: 'datetime',
                                        isNN: true,
                                    },
                                    '8v0UmP_iUtQjSlVrJW6j8': {
                                        id: '8v0UmP_iUtQjSlVrJW6j8',
                                        name: 'descristion',
                                        type: 'varchar(45)',
                                    },
                                    chsN5S23V9TxyOOqNl7hX: {
                                        id: 'chsN5S23V9TxyOOqNl7hX',
                                        name: 'description',
                                        // "name": "ckcalculated_at",
                                        type: 'tdate',
                                    },
                                    AIP2LCU1H8R_VkDXXMj16: {
                                        id: 'AIP2LCU1H8R_VkDXXMj16',
                                        name: 'status',
                                        type: 'int',
                                    },
                                    '2F4rOWeSINl_UApIVlCAX': {
                                        id: '2F4rOWeSINl_UApIVlCAX',
                                        name: 'created_at',
                                        type: 'datetime',
                                        index: {
                                            type: 'composite',
                                        },
                                    },
                                    jjN8jR1sjgcNhIjnrjz9t: {
                                        id: 'jjN8jR1sjgcNhIjnrjz9t',
                                        name: 'jxfloating_profit',
                                        type: 'decimal(20,6)',
                                    },
                                    h_pezAsrAt0iGxiBLInqa: {
                                        id: 'h_pezAsrAt0iGxiBLInqa',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    'JKQnmQDExFR3AbJEl-5GB': {
                                        id: 'JKQnmQDExFR3AbJEl-5GB',
                                        name: 'lzgroup_id',
                                        type: 'int',
                                        isNN: true,
                                        index: {
                                            type: 'fullText',
                                        },
                                    },
                                    'HhJD7v-MrQGGZl3dG5uJF': {
                                        id: 'HhJD7v-MrQGGZl3dG5uJF',
                                        name: 'mid',
                                        isPK: true,
                                        type: 'int',
                                    },
                                    '0EvMcIJof8Oeo2Diwmjpy': {
                                        id: '0EvMcIJof8Oeo2Diwmjpy',
                                        name: 'nleverage',
                                        type: 'decimal(10,2)',
                                    },
                                    '5MO2PckZQ8r-EpzDiBH8R': {
                                        id: '5MO2PckZQ8r-EpzDiBH8R',
                                        name: 'olifetime_profit',
                                        type: 'decimal(20,6)',
                                    },

                                    111: {
                                        id: '111',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    222: {
                                        id: '222',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    333: {
                                        id: '333',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },

                                    o84D75GxwZOhqLt2kKQKn: {
                                        id: 'o84D75GxwZOhqLt2kKQKn',
                                        name: 'pmargin',
                                        type: 'decimal(20,6)',
                                    },
                                    I8wxQYcICNnrpH6k6QGR1: {
                                        id: 'I8wxQYcICNnrpH6k6QGR1',
                                        name: 'qmargin_level',
                                        type: 'decimal(20,6)',
                                    },
                                    AAD1R3wxkU8lvXZoUrgOS: {
                                        id: 'AAD1R3wxkU8lvXZoUrgOS',
                                        name: 'rmessage_timestamp',
                                        type: 'datetime(3)',
                                        index: {
                                            type: 'composite',
                                        },
                                    },
                                    nRLtt8EfHinYnlo38uvrT: {
                                        id: 'nRLtt8EfHinYnlo38uvrT',
                                        name: 'smode',
                                        type: 'enum()',
                                    },
                                    COKdqhdkyV8VQonBm4itk: {
                                        id: 'COKdqhdkyV8VQonBm4itk',
                                        name: 'tmt_login',
                                        type: 'int',
                                    },
                                    'LvDiFosVAgZWBOh-myP93': {
                                        id: 'LvDiFosVAgZWBOh-myP93',
                                        name: 'umt_server_id',
                                        type: 'varchar(255)',
                                    },
                                    XHGTpinzVph9Ysz6kVEDQ: {
                                        id: 'XHGTpinzVph9Ysz6kVEDQ',
                                        name: 'vstatus_id',
                                        type: 'enum()',
                                    },
                                    '7Z03Um13_1dqxOhmNcL0z': {
                                        id: '7Z03Um13_1dqxOhmNcL0z',
                                        name: 'wsystem_balance',
                                        type: 'decimal(20,6)',
                                    },
                                    mOwtdW3gWbfNXmX0keL4M: {
                                        id: 'mOwtdW3gWbfNXmX0keL4M',
                                        name: 'xsystem_equity',
                                        type: 'decimal(20,6)',
                                    },
                                    '9LVBQ0YVvnvR8TsvWXxOT': {
                                        id: '9LVBQ0YVvnvR8TsvWXxOT',
                                        name: 'ysystem_free_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    'dwI9XVMJuZuv-RBZfebLa': {
                                        id: 'dwI9XVMJuZuv-RBZfebLa',
                                        name: 'ztrading_mode',
                                        type: 'enum()',
                                    },
                                    I5CWpDOZW_YA5sSMWeEBh: {
                                        id: 'I5CWpDOZW_YA5sSMWeEBh',
                                        name: 'updated_at',
                                        type: 'datetime',
                                    },
                                },
                            },
                        },
                    },
                    state.elements
                );

                // draft.elementsChanges = getElementsChangesView(state.elementsLastCommited, state.elements);

                draft.isBtnRedGreenViewEnabled =
                    !state.isBtnRedGreenViewEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnRedGreenViewEnabled'
                );

                break;
            }

            case diagramActionTypes.HISTORY_UNDO: {
                let pointer = state.historyPointer;

                let isUndoAvailable =
                    state.historyIsUndoAvailable && !!state.history[pointer];
                if (isUndoAvailable) {
                    // UNFOCUS FOCUSED ITEMS
                    draft = unfocusFocusedItems(state, draft);

                    // HISTORY: UNDO
                    draft = utilHistoryUndo(draft, state.history[pointer]);
                    pointer = pointer - 1;

                    draft.isHistoryClicked = true;
                }

                draft.historyPointer = pointer;
                draft.historyIsUndoAvailable = pointer >= 0;
                draft.historyIsRedoAvailable = !!state.history[pointer + 1];

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                break;
            }
            case diagramActionTypes.HISTORY_REDO: {
                let pointer = state.historyPointer;

                let isRedoAvailable =
                    state.historyIsRedoAvailable &&
                    !!state.history[pointer + 1];
                if (isRedoAvailable) {
                    // UNFOCUS FOCUSED ITEMS
                    draft = unfocusFocusedItems(state, draft);

                    // HISTORY: REDO
                    draft = utilHistoryRedo(draft, state.history[pointer + 1]);
                    pointer = pointer + 1;

                    draft.isHistoryClicked = true;
                }

                draft.historyPointer = pointer;
                draft.historyIsUndoAvailable = pointer >= 0;
                draft.historyIsRedoAvailable = !!state.history[pointer + 1];

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                break;
            }

            case diagramActionTypes.SET_NOTE_ALIGNMENT: {
                // action.payload: elementId, alignmentValue

                let alignmentHorizontal = '';
                let alignmentVertical = '';

                switch (action.payload.alignmentValue) {
                    case 'left': {
                        alignmentHorizontal = 'left';
                        break;
                    }
                    case 'horizontalCenter': {
                        alignmentHorizontal = 'center';
                        break;
                    }
                    case 'right': {
                        alignmentHorizontal = 'right';
                        break;
                    }
                    case 'top': {
                        alignmentVertical = 'top';
                        break;
                    }
                    case 'verticalCenter': {
                        alignmentVertical = 'center';
                        break;
                    }
                    case 'bottom': {
                        alignmentVertical = 'bottom';
                        break;
                    }
                    default:
                        break;
                }

                // 1. update elements
                if (alignmentHorizontal) {
                    draft.elements[
                        action.payload.elementId
                    ].value.alignmentHorizontal = alignmentHorizontal;
                } else {
                    draft.elements[
                        action.payload.elementId
                    ].value.alignmentVertical = alignmentVertical;
                }

                // 3. update focused elements
                if (
                    draft.focusedElements &&
                    draft.focusedElements[action.payload.elementId]
                ) {
                    draft.focusedElements[
                        action.payload.elementId
                    ].value.alignmentHorizontal =
                        draft.elements[
                            action.payload.elementId
                        ].value.alignmentHorizontal;
                    draft.focusedElements[
                        action.payload.elementId
                    ].value.alignmentVertical =
                        draft.elements[
                            action.payload.elementId
                        ].value.alignmentVertical;
                }

                // ----------------------------------------------------------------
                // HISTORY: GET VALUE
                // const historyValue = [{
                //     elementId: action.payload.tableId,
                //     action: HISTORY.UPDATE_COLUMN_KEY,
                //     prevValue: {
                //         columnId: action.payload.columnId,
                //         isPK: state.elements[action.payload.tableId].value.columns[action.payload.columnId].isPK,
                //         isFK: state.elements[action.payload.tableId].value.columns[action.payload.columnId].isFK,
                //     },
                //     nextValue: {
                //         columnId: action.payload.columnId,
                //         isPK: draft.elements[action.payload.tableId].value.columns[action.payload.columnId].isPK,
                //         isFK: draft.elements[action.payload.tableId].value.columns[action.payload.columnId].isFK,
                //     },
                // }];

                // ----------------------------------------------------------------
                // HISTORY: SAVE VALUE
                // saveHistoryValue(state, draft, historyValue);
                // ----------------------------------------------------------------

                draft.isContentChanged = true;
                break;
            }
            case diagramActionTypes.SET_NOTE_FONT_MONOSPACE: {
                // action.payload: elementId

                // 1. update elements
                draft.elements[action.payload.elementId].value.isFontMonospace =
                    !state.elements[action.payload.elementId].value
                        .isFontMonospace;

                // 3. update focused elements
                if (
                    draft.focusedElements &&
                    draft.focusedElements[action.payload.elementId]
                ) {
                    draft.focusedElements[
                        action.payload.elementId
                    ].value.isFontMonospace =
                        draft.elements[
                            action.payload.elementId
                        ].value.isFontMonospace;
                }

                draft.isContentChanged = true;
                break;
            }
            case diagramActionTypes.SET_NOTE_FONT_STYLE: {
                // action.payload: elementId, fontStyleType, value
                // fontStyleType = bold, italic, underline, strikethrough

                // // 1. update elements
                // draft.elements[action.payload.elementId].value.isFontMonospace = !state.elements[action.payload.elementId].value.isFontMonospace;

                // // 3. update focused elements
                // if (draft.focusedElements &&
                //     draft.focusedElements[action.payload.elementId]
                // ) {
                //     draft.focusedElements[action.payload.elementId].value.isFontMonospace = draft.elements[action.payload.elementId].value.isFontMonospace;
                // }

                draft.isContentChanged = true;
                break;
            }

            case diagramActionTypes.CLICK_ESCAPE: {
                // custom logic for focused inside element and focused element
                // FOCUSED ELEMENTS
                if (state.focusedLine || state.focusedLineId) {
                    draft.focusedLine = null;
                    draft.focusedLineId = null;
                }
                if (state.focusedArea || state.focusedAreaId) {
                    draft.focusedArea = null;
                    draft.focusedAreaId = null;
                }
                if (state.focusedInsideElement) {
                    draft.focusedInsideElement = null;
                } else if (state.focusedElementsIds?.length) {
                    draft.focusedElements = {};
                    draft.focusedElementsIds = [];
                }

                // focused group elements and areas
                if (state.focusedGroupElementsIds?.length) {
                    draft.focusedGroupElements = {};
                    draft.focusedGroupElementsIds = [];
                }
                if (state.focusedGroupAreasIds?.length) {
                    draft.focusedGroupAreas = {};
                    draft.focusedGroupAreasIds = [];
                }

                if (draft.isHistoryClicked) {
                    draft.isHistoryClicked = false;
                }
                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isCursorHand');

                break;
            }

            case diagramActionTypes.SET_IS_HISTORY_CLICKED: {
                if (draft.isHistoryClicked) {
                    draft.isHistoryClicked = false;
                }
                break;
            }

            case diagramActionTypes.SET_COLOR_NEW_PEN: {
                draft.newPen.color = action.payload.color;
                break;
            }
            case diagramActionTypes.SET_WIDTH_NEW_PEN: {
                draft.newPen.width = action.payload.width;
                break;
            }
            case diagramActionTypes.CLEAR_NEW_PEN: {
                let prevElementsForHistory = {};

                // delete elements with 'newPenId'
                const keys = Object.keys(state.elements);
                keys.forEach((elementId) => {
                    if (
                        state.elements[elementId].newPenId &&
                        state.elements[elementId].newPenId == state.newPen.id
                    ) {
                        // ----------------------------------------------------------------
                        // HISTORY: GET VALUE
                        prevElementsForHistory[elementId] =
                            draft.elements[elementId];
                        // ----------------------------------------------------------------

                        delete draft.elements[elementId];
                    }
                });

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                // 3. delete from focused inside element
                draft.focusedInsideElement = null;

                // 4. delete from focused elements
                draft.focusedElements = {};

                // 5. delete from focused elements ids
                draft.focusedElementsIds = [];

                if (Object.keys(prevElementsForHistory).length) {
                    // ----------------------------------------------------------------
                    // HISTORY: GET VALUE
                    const historyValue = [
                        {
                            // elementId: action.payload.id,
                            elementsIds: Object.keys(prevElementsForHistory),
                            action: HISTORY.DELETE_ELEMENTS,
                            prevValue: {
                                elements: prevElementsForHistory,
                                lines: {},
                            },
                            nextValue: {
                                elements: null,
                                linesIds: [],
                            },
                        },
                    ];
                    // ----------------------------------------------------------------
                    // HISTORY: SAVE VALUE
                    saveHistoryValue(state, draft, historyValue);
                    // ----------------------------------------------------------------
                }

                break;
            }


            case diagramActionTypes.ENABLE_ELEMENT_DESCRIPTION_EDITING: {
                if (!draft.isEnabledElementDescriptionEditing) {
                    draft.isEnabledElementDescriptionEditing = true;
                }
                break;
            }
            case diagramActionTypes.DISABLE_ELEMENT_DESCRIPTION_EDITING: {
                if (draft.isEnabledElementDescriptionEditing) {
                    draft.isEnabledElementDescriptionEditing = false;
                }
                break;
            }


            // // FETCH DIAGRAM
            // case diagramActionTypes.FETCH_DIAGRAM_REQUEST: {
            //     draft.loading = true;
            //     break;
            // }
            // case diagramActionTypes.FETCH_DIAGRAM_SUCCESS: {
            //     draft.loading = false;
            //     draft.diagramDetails = action.payload.data;
            //     draft.diagramDbEngine = action.payload.data?.db_engine;
            //     draft.error = null;
            //     break;
            // }
            // case diagramActionTypes.FETCH_DIAGRAM_FAILURE: {
            //     draft.loading = false;
            //     draft.error = action.payload;
            //     break;
            // }



            // ------------------------------------------------------------------
            // MODAL SIGN IN RESTRICT (PRICING PLAN LIMIT)
            case diagramActionTypes.ENABLE_MODAL_SIGN_IN_RESTRICT: {
                draft.isModalSignInRestrictEnabled = true;
                draft.modalSignInRestrictType = action.payload.type;
                break;
            }
            case diagramActionTypes.DISABLE_MODAL_SIGN_IN_RESTRICT: {
                draft.isModalSignInRestrictEnabled = false;
                draft.modalSignInRestrictType = null;
                break;
            }
            // ------------------------------------------------------------------
            // MODAL SIGN IN REMIND
            case diagramActionTypes.ENABLE_MODAL_SIGN_IN_REMIND: {
                draft.isModalSignInRemindEnabled = true;
                break;
            }
            case diagramActionTypes.DISABLE_MODAL_SIGN_IN_REMIND: {
                draft.isModalSignInRemindEnabled = false;
                break;
            }
            // ------------------------------------------------------------------
            // LOAD_SAMPLE_PROJECT DATA
            case diagramActionTypes.LOAD_SAMPLE_PROJECT_DATA: {
                // draft.scale = 0.72;
                draft.scale = 0.74;
                draft.originXY = { x: 918, y: 955 };

                draft.elements = {
                    "XyOWzraTvzAudlKUy6jBm": {
                        "id": "XyOWzraTvzAudlKUy6jBm",
                        "type": "table",
                        "x": -507.71503813834045,
                        "y": -733.9146555167079,
                        "width": 342.22222222222223,
                        "height": 300,
                        "value": {
                            "header": {
                                "name": "users"
                            },
                            "columns": {
                                "eFMUSKazSLNq7xAforL6B": {
                                    "id": "eFMUSKazSLNq7xAforL6B",
                                    "name": "id",
                                    "type": "int",
                                    "indexNumber": 0,
                                    "isPK": true,
                                    "isRelated": true
                                },
                                "FyS7MZgyPqba1EMVofix2": {
                                    "id": "FyS7MZgyPqba1EMVofix2",
                                    "name": "username",
                                    "type": "varhar(50)",
                                    "indexNumber": 1,
                                    "isRelated": false
                                },
                                "GeMhnnh9Qm68-_bgTt8ip": {
                                    "id": "GeMhnnh9Qm68-_bgTt8ip",
                                    "name": "email",
                                    "type": "varchar(100)",
                                    "indexNumber": 2
                                },
                                "tRvrMAyfRzAkeC8HFfDdI": {
                                    "id": "tRvrMAyfRzAkeC8HFfDdI",
                                    "name": "password_hash",
                                    "type": "varchar(255)",
                                    "indexNumber": 3
                                },
                                "uWgigj_-1Uatsg0plBhJq": {
                                    "id": "uWgigj_-1Uatsg0plBhJq",
                                    "name": "role_id",
                                    "type": "int",
                                    "indexNumber": 4,
                                    "isPK": false,
                                    "isFK": true,
                                    "fkRefTable": "table1",
                                    "fkRefColumn": "column1",
                                    "isRelated": true
                                },
                                "KhejNiqgLK8fC73GrCKgY": {
                                    "id": "KhejNiqgLK8fC73GrCKgY",
                                    "name": "last_login",
                                    "type": "datetime",
                                    "indexNumber": 5
                                },
                                "S4G3VAvVfew5H9FLuaFQm": {
                                    "id": "S4G3VAvVfew5H9FLuaFQm",
                                    "name": "created_at",
                                    "type": "datetime",
                                    "indexNumber": 6
                                }
                            }
                        },
                        "translateX": 0,
                        "translateY": 0
                    },
                    "7odfFby3A--WYGUu0RXUk": {
                        "id": "7odfFby3A--WYGUu0RXUk",
                        "type": "table",
                        "x": -47.68895861154641,
                        "y": -689.5613654110114,
                        "width": 320,
                        "height": 228,
                        "value": {
                            "header": {
                                "name": "roles"
                            },
                            "columns": {
                                "a0-R0MZIZX1QClsrbBI0K": {
                                    "id": "a0-R0MZIZX1QClsrbBI0K",
                                    "name": "id",
                                    "type": "int",
                                    "indexNumber": 0,
                                    "isPK": true,
                                    "isRelated": true
                                },
                                "wziD5nGmHajJMQJUxczS2": {
                                    "id": "wziD5nGmHajJMQJUxczS2",
                                    "name": "name",
                                    "type": "varchar(50)",
                                    "indexNumber": 1,
                                    "isRelated": false,
                                    "isPK": false,
                                    "isFK": false,
                                    "fkRefTable": null,
                                    "fkRefColumn": null
                                },
                                "qJP0LEeZetpkVYatUR8C2": {
                                    "id": "qJP0LEeZetpkVYatUR8C2",
                                    "name": "description",
                                    "type": "text",
                                    "indexNumber": 2,
                                    "isRelated": false,
                                    "isPK": false,
                                    "isFK": false,
                                    "fkRefTable": null,
                                    "fkRefColumn": null
                                },
                                "qz9aZquUrnv5SZi7oP1E9": {
                                    "id": "qz9aZquUrnv5SZi7oP1E9",
                                    "name": "is_active",
                                    "type": "bool",
                                    "indexNumber": 3
                                },
                                "IXPGK49ZnSq_zKqhIbXxH": {
                                    "id": "IXPGK49ZnSq_zKqhIbXxH",
                                    "name": "created_at",
                                    "type": "datetime",
                                    "indexNumber": 4
                                }
                            }
                        },
                        "translateX": 0,
                        "translateY": 0
                    },
                    "FAtynCCvf8712Q_e3Wym-": {
                        "id": "FAtynCCvf8712Q_e3Wym-",
                        "type": "table",
                        "x": 796.0647383314704,
                        "y": -677.1302254814758,
                        "width": 342.0316510124323,
                        "height": 192,
                        "value": {
                            "header": {
                                "name": "permissions"
                            },
                            "columns": {
                                "oEYml_TUMtc4AgoLXsOdz": {
                                    "id": "oEYml_TUMtc4AgoLXsOdz",
                                    "name": "id",
                                    "type": "int",
                                    "indexNumber": 0,
                                    "isPK": true,
                                    "isRelated": true
                                },
                                "VPUcT_EnfP8_sPSkxzan4": {
                                    "id": "VPUcT_EnfP8_sPSkxzan4",
                                    "name": "permission_key",
                                    "type": "varchar(100)",
                                    "indexNumber": 1,
                                    "isRelated": false,
                                    "isPK": false,
                                    "isFK": false,
                                    "fkRefTable": null,
                                    "fkRefColumn": null
                                },
                                "iRFs-IJjg7P_P_GmAMEtM": {
                                    "id": "iRFs-IJjg7P_P_GmAMEtM",
                                    "name": "description",
                                    "type": "text",
                                    "indexNumber": 2
                                },
                                "YVZYFPN8lU8iYSFS_mOfk": {
                                    "id": "YVZYFPN8lU8iYSFS_mOfk",
                                    "name": "created_at",
                                    "type": "datetime",
                                    "indexNumber": 3
                                }
                            }
                        },
                        "translateX": 0,
                        "translateY": 0
                    },
                    "AgErmegSyjRG3vWd-TPRh": {
                        "id": "AgErmegSyjRG3vWd-TPRh",
                        "type": "table",
                        "x": 376.5900341871849,
                        "y": -635.5613654110114,
                        "width": 320,
                        "height": 120,
                        "value": {
                            "header": {
                                "name": "roles_permissions"
                            },
                            "columns": {
                                "RGmchtMi0MvbOlKGStJed": {
                                    "id": "RGmchtMi0MvbOlKGStJed",
                                    "name": "role_id",
                                    "type": "int",
                                    "indexNumber": 0,
                                    "isPK": false,
                                    "isFK": true,
                                    "fkRefTable": "table1",
                                    "fkRefColumn": "column1",
                                    "isRelated": true
                                },
                                "qS2De6ZD5scmxZHjgsJSr": {
                                    "id": "qS2De6ZD5scmxZHjgsJSr",
                                    "name": "permission_id",
                                    "type": "int",
                                    "indexNumber": 1,
                                    "isPK": false,
                                    "isFK": true,
                                    "fkRefTable": "table1",
                                    "fkRefColumn": "column1",
                                    "isRelated": true
                                }
                            }
                        },
                        "translateX": 0,
                        "translateY": 0
                    },
                    "rRPZxejex0HHWDNPSwWdC": {
                        "id": "rRPZxejex0HHWDNPSwWdC",
                        "type": "table",
                        "x": -962.0529812749993,
                        "y": -969.6877513745876,
                        "width": 320,
                        "height": 300,
                        "value": {
                            "header": {
                                "name": "user_sessions"
                            },
                            "columns": {
                                "ky2A__5A93c5tOS7fRByR": {
                                    "id": "ky2A__5A93c5tOS7fRByR",
                                    "name": "id",
                                    "type": "uuid",
                                    "indexNumber": 0,
                                    "isPK": true
                                },
                                "hxyg-NMSBVpwMKiE76UXk": {
                                    "id": "hxyg-NMSBVpwMKiE76UXk",
                                    "name": "user_id",
                                    "type": "int",
                                    "indexNumber": 1,
                                    "isPK": false,
                                    "isFK": true,
                                    "fkRefTable": "table1",
                                    "fkRefColumn": "column1",
                                    "isRelated": true
                                },
                                "G7WfjtzE605XsN77YU3AC": {
                                    "id": "G7WfjtzE605XsN77YU3AC",
                                    "name": "ip",
                                    "type": "varchar(45)",
                                    "indexNumber": 2
                                },
                                "EvBOEUeqr6iFBiazUkrNO": {
                                    "id": "EvBOEUeqr6iFBiazUkrNO",
                                    "name": "user_agent",
                                    "type": "text",
                                    "indexNumber": 3
                                },
                                "5T9deUcexESKx7lQ-2tE9": {
                                    "id": "5T9deUcexESKx7lQ-2tE9",
                                    "name": "created_at",
                                    "type": "datetime",
                                    "indexNumber": 4
                                },
                                "nHpR8LYdyB52QP9-DIj-N": {
                                    "id": "nHpR8LYdyB52QP9-DIj-N",
                                    "name": "expires_at",
                                    "type": "datetime",
                                    "indexNumber": 5
                                },
                                "HRfoOVMruMutvCw6gx4PO": {
                                    "id": "HRfoOVMruMutvCw6gx4PO",
                                    "name": "is_active",
                                    "type": "bool",
                                    "indexNumber": 6
                                }
                            }
                        },
                        "translateX": 0,
                        "translateY": 0
                    },
                    "8K2U0Vh1wrZM1Gg6gw_zK": {
                        "id": "8K2U0Vh1wrZM1Gg6gw_zK",
                        "type": "table",
                        "x": -962.0529812749993,
                        "y": -582.7248367366318,
                        "width": 320,
                        "height": 264,
                        "value": {
                            "header": {
                                "name": "user_settings"
                            },
                            "columns": {
                                "MZdWuPaS0vrtsvgNbNX9x": {
                                    "id": "MZdWuPaS0vrtsvgNbNX9x",
                                    "name": "id",
                                    "type": "int",
                                    "indexNumber": 0,
                                    "isPK": true
                                },
                                "tDEWUx5nA5ZUnWVq7UUky": {
                                    "id": "tDEWUx5nA5ZUnWVq7UUky",
                                    "name": "user_id",
                                    "type": "int",
                                    "indexNumber": 1,
                                    "isRelated": true,
                                    "isPK": false,
                                    "isFK": true,
                                    "fkRefTable": "table1",
                                    "fkRefColumn": "column1"
                                },
                                "Qcgs1vFiiuVIN3kcJwPqL": {
                                    "id": "Qcgs1vFiiuVIN3kcJwPqL",
                                    "name": "language",
                                    "type": "varchar(2)",
                                    "indexNumber": 2
                                },
                                "xE3yjWCxxSNr-XAfar6Yy": {
                                    "id": "xE3yjWCxxSNr-XAfar6Yy",
                                    "name": "theme",
                                    "type": "varchar(4)",
                                    "indexNumber": 3
                                },
                                "FryJFCoWbnw5_UoXrlRni": {
                                    "id": "FryJFCoWbnw5_UoXrlRni",
                                    "name": "notifications_enabled",
                                    "type": "bool",
                                    "indexNumber": 4
                                },
                                "dnys-0Y5ePgQWtfbhyBrX": {
                                    "id": "dnys-0Y5ePgQWtfbhyBrX",
                                    "name": "timezone",
                                    "type": "varchar(50)",
                                    "indexNumber": 5
                                }
                            }
                        },
                        "translateX": 0,
                        "translateY": 0
                    }
                }
                draft.lines = {
                    "xIGVSqUNh504SlbuK7-oq": {
                        "id": "xIGVSqUNh504SlbuK7-oq",
                        "type": "line",
                        "value": {
                            "fromElementId": "AgErmegSyjRG3vWd-TPRh",
                            "fromInsideElementId": "RGmchtMi0MvbOlKGStJed",
                            "fromRelationType": "many",
                            "toElementId": "7odfFby3A--WYGUu0RXUk",
                            "toInsideElementId": "a0-R0MZIZX1QClsrbBI0K",
                            "toRelationType": "one",
                            "path": [
                                [
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 272.3110413884536,
                                            "y": -627.5613654110114
                                        },
                                        "p2": {
                                            "x": 295.3110413884536,
                                            "y": -627.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 376.5900341871849,
                                            "y": -581.5613654110114
                                        },
                                        "p2": {
                                            "x": 356.5900341871849,
                                            "y": -573.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 356.5900341871849,
                                            "y": -573.5613654110114
                                        },
                                        "p2": {
                                            "x": 376.5900341871849,
                                            "y": -565.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 376.5900341871849,
                                            "y": -573.5613654110114
                                        },
                                        "p2": {
                                            "x": 342.5900341871849,
                                            "y": -573.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 306.3110413884536,
                                            "y": -627.5613654110114
                                        },
                                        "p2": {
                                            "x": 314.45053778781926,
                                            "y": -627.5613654110114
                                        }
                                    },
                                    {
                                        "type": "angle",
                                        "width": 10,
                                        "turn": 1,
                                        "point": {
                                            "x": 324.45053778781926,
                                            "y": -617.5613654110114
                                        },
                                        "p1": {
                                            "x": 324.45053778781926,
                                            "y": -627.5613654110114
                                        },
                                        "p2": {
                                            "x": 324.45053778781926,
                                            "y": -617.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 324.45053778781926,
                                            "y": -617.5613654110114
                                        },
                                        "p2": {
                                            "x": 324.45053778781926,
                                            "y": -583.5613654110114
                                        }
                                    },
                                    {
                                        "type": "angle",
                                        "width": 10,
                                        "turn": 0,
                                        "point": {
                                            "x": 334.45053778781926,
                                            "y": -573.5613654110114
                                        },
                                        "p1": {
                                            "x": 324.45053778781926,
                                            "y": -573.5613654110114
                                        },
                                        "p2": {
                                            "x": 342.5900341871849,
                                            "y": -573.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 334.45053778781926,
                                            "y": -573.5613654110114
                                        },
                                        "p2": {
                                            "x": 342.5900341871849,
                                            "y": -573.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 376.5900341871849,
                                            "y": -573.5613654110114
                                        },
                                        "p2": {
                                            "x": 353.5900341871849,
                                            "y": -573.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 290.3110413884536,
                                            "y": -635.5613654110114
                                        },
                                        "p2": {
                                            "x": 290.3110413884536,
                                            "y": -619.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 272.3110413884536,
                                            "y": -627.5613654110114
                                        },
                                        "p2": {
                                            "x": 306.3110413884536,
                                            "y": -627.5613654110114
                                        }
                                    }
                                ],
                                [
                                    {
                                        "x": 272.3110413884536,
                                        "y": -627.5613654110114
                                    },
                                    {
                                        "x": 324.45053778781926,
                                        "y": -627.5613654110114
                                    },
                                    {
                                        "x": 324.45053778781926,
                                        "y": -573.5613654110114
                                    },
                                    {
                                        "x": 376.5900341871849,
                                        "y": -573.5613654110114
                                    }
                                ],
                                {
                                    "minX": 272.3110413884536,
                                    "maxX": 376.5900341871849,
                                    "minY": -627.5613654110114,
                                    "maxY": -573.5613654110114
                                },
                                [
                                    4,
                                    3
                                ]
                            ],
                            "fromElementX": 376.5900341871849,
                            "fromElementY": -635.5613654110114,
                            "toElementX": -47.68895861154641,
                            "toElementY": -689.5613654110114
                        },
                        "x": 1100,
                        "y": 448,
                        "width": 1,
                        "height": 1
                    },
                    "Qx--3I1WfRbSWr4sEEDQB": {
                        "id": "Qx--3I1WfRbSWr4sEEDQB",
                        "type": "line",
                        "value": {
                            "fromElementId": "AgErmegSyjRG3vWd-TPRh",
                            "fromInsideElementId": "qS2De6ZD5scmxZHjgsJSr",
                            "fromRelationType": "many",
                            "toElementId": "FAtynCCvf8712Q_e3Wym-",
                            "toInsideElementId": "oEYml_TUMtc4AgoLXsOdz",
                            "toRelationType": "one",
                            "path": [
                                [
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 696.590034187185,
                                            "y": -537.5613654110114
                                        },
                                        "p2": {
                                            "x": 719.590034187185,
                                            "y": -537.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 696.590034187185,
                                            "y": -545.5613654110114
                                        },
                                        "p2": {
                                            "x": 716.590034187185,
                                            "y": -537.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 716.590034187185,
                                            "y": -537.5613654110114
                                        },
                                        "p2": {
                                            "x": 696.590034187185,
                                            "y": -529.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 696.590034187185,
                                            "y": -537.5613654110114
                                        },
                                        "p2": {
                                            "x": 730.590034187185,
                                            "y": -537.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 730.590034187185,
                                            "y": -537.5613654110114
                                        },
                                        "p2": {
                                            "x": 736.3273862593277,
                                            "y": -537.5613654110114
                                        }
                                    },
                                    {
                                        "type": "angle",
                                        "width": 10,
                                        "turn": 0,
                                        "point": {
                                            "x": 746.3273862593277,
                                            "y": -547.5613654110114
                                        },
                                        "p1": {
                                            "x": 746.3273862593277,
                                            "y": -537.5613654110114
                                        },
                                        "p2": {
                                            "x": 746.3273862593277,
                                            "y": -605.1302254814758
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 746.3273862593277,
                                            "y": -547.5613654110114
                                        },
                                        "p2": {
                                            "x": 746.3273862593277,
                                            "y": -605.1302254814758
                                        }
                                    },
                                    {
                                        "type": "angle",
                                        "width": 10,
                                        "turn": 1,
                                        "point": {
                                            "x": 756.3273862593277,
                                            "y": -615.1302254814758
                                        },
                                        "p1": {
                                            "x": 746.3273862593277,
                                            "y": -615.1302254814758
                                        },
                                        "p2": {
                                            "x": 762.0647383314704,
                                            "y": -615.1302254814758
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 756.3273862593277,
                                            "y": -615.1302254814758
                                        },
                                        "p2": {
                                            "x": 762.0647383314704,
                                            "y": -615.1302254814758
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 796.0647383314704,
                                            "y": -615.1302254814758
                                        },
                                        "p2": {
                                            "x": 773.0647383314704,
                                            "y": -615.1302254814758
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 778.0647383314704,
                                            "y": -623.1302254814758
                                        },
                                        "p2": {
                                            "x": 778.0647383314704,
                                            "y": -607.1302254814758
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": 796.0647383314704,
                                            "y": -615.1302254814758
                                        },
                                        "p2": {
                                            "x": 762.0647383314704,
                                            "y": -615.1302254814758
                                        }
                                    }
                                ],
                                [
                                    {
                                        "x": 696.590034187185,
                                        "y": -537.5613654110114
                                    },
                                    {
                                        "x": 746.3273862593277,
                                        "y": -537.5613654110114
                                    },
                                    {
                                        "x": 746.3273862593277,
                                        "y": -615.1302254814758
                                    },
                                    {
                                        "x": 796.0647383314704,
                                        "y": -615.1302254814758
                                    }
                                ],
                                {
                                    "minX": 696.590034187185,
                                    "maxX": 796.0647383314704,
                                    "minY": -615.1302254814758,
                                    "maxY": -537.5613654110114
                                },
                                [
                                    4,
                                    3
                                ]
                            ],
                            "fromElementX": 376.5900341871849,
                            "fromElementY": -635.5613654110114,
                            "toElementX": 796.0647383314704,
                            "toElementY": -677.1302254814758
                        },
                        "x": 1100,
                        "y": 448,
                        "width": 1,
                        "height": 1
                    },
                    "RiCJxDBI_E1sNkHK5awfB": {
                        "id": "RiCJxDBI_E1sNkHK5awfB",
                        "type": "line",
                        "value": {
                            "fromElementId": "XyOWzraTvzAudlKUy6jBm",
                            "fromInsideElementId": "uWgigj_-1Uatsg0plBhJq",
                            "fromRelationType": "many",
                            "toElementId": "7odfFby3A--WYGUu0RXUk",
                            "toInsideElementId": "a0-R0MZIZX1QClsrbBI0K",
                            "toRelationType": "one",
                            "path": [
                                [
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -165.49281591611822,
                                            "y": -527.9146555167079
                                        },
                                        "p2": {
                                            "x": -142.49281591611822,
                                            "y": -527.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -165.49281591611822,
                                            "y": -535.9146555167079
                                        },
                                        "p2": {
                                            "x": -145.49281591611822,
                                            "y": -527.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -145.49281591611822,
                                            "y": -527.9146555167079
                                        },
                                        "p2": {
                                            "x": -165.49281591611822,
                                            "y": -519.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -165.49281591611822,
                                            "y": -527.9146555167079
                                        },
                                        "p2": {
                                            "x": -131.49281591611822,
                                            "y": -527.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -131.49281591611822,
                                            "y": -527.9146555167079
                                        },
                                        "p2": {
                                            "x": -116.59088726383231,
                                            "y": -527.9146555167079
                                        }
                                    },
                                    {
                                        "type": "angle",
                                        "width": 10,
                                        "turn": 0,
                                        "point": {
                                            "x": -106.59088726383231,
                                            "y": -537.9146555167079
                                        },
                                        "p1": {
                                            "x": -106.59088726383231,
                                            "y": -527.9146555167079
                                        },
                                        "p2": {
                                            "x": -106.59088726383231,
                                            "y": -617.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -106.59088726383231,
                                            "y": -537.9146555167079
                                        },
                                        "p2": {
                                            "x": -106.59088726383231,
                                            "y": -617.5613654110114
                                        }
                                    },
                                    {
                                        "type": "angle",
                                        "width": 10,
                                        "turn": 1,
                                        "point": {
                                            "x": -96.59088726383231,
                                            "y": -627.5613654110114
                                        },
                                        "p1": {
                                            "x": -106.59088726383231,
                                            "y": -627.5613654110114
                                        },
                                        "p2": {
                                            "x": -81.68895861154641,
                                            "y": -627.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -96.59088726383231,
                                            "y": -627.5613654110114
                                        },
                                        "p2": {
                                            "x": -81.68895861154641,
                                            "y": -627.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -47.68895861154641,
                                            "y": -627.5613654110114
                                        },
                                        "p2": {
                                            "x": -70.68895861154641,
                                            "y": -627.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -65.68895861154641,
                                            "y": -635.5613654110114
                                        },
                                        "p2": {
                                            "x": -65.68895861154641,
                                            "y": -619.5613654110114
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -47.68895861154641,
                                            "y": -627.5613654110114
                                        },
                                        "p2": {
                                            "x": -81.68895861154641,
                                            "y": -627.5613654110114
                                        }
                                    }
                                ],
                                [
                                    {
                                        "x": -165.49281591611822,
                                        "y": -527.9146555167079
                                    },
                                    {
                                        "x": -106.59088726383231,
                                        "y": -527.9146555167079
                                    },
                                    {
                                        "x": -106.59088726383231,
                                        "y": -627.5613654110114
                                    },
                                    {
                                        "x": -47.68895861154641,
                                        "y": -627.5613654110114
                                    }
                                ],
                                {
                                    "minX": -165.49281591611822,
                                    "maxX": -47.68895861154641,
                                    "minY": -627.5613654110114,
                                    "maxY": -527.9146555167079
                                },
                                [
                                    4,
                                    3
                                ]
                            ],
                            "fromElementX": -507.71503813834045,
                            "fromElementY": -733.9146555167079,
                            "toElementX": -47.68895861154641,
                            "toElementY": -689.5613654110114
                        },
                        "x": 1100,
                        "y": 448,
                        "width": 1,
                        "height": 1
                    },
                    "-rJyGz3HJzzSq-h9ryvfy": {
                        "id": "-rJyGz3HJzzSq-h9ryvfy",
                        "type": "line",
                        "value": {
                            "fromElementId": "rRPZxejex0HHWDNPSwWdC",
                            "fromInsideElementId": "hxyg-NMSBVpwMKiE76UXk",
                            "fromRelationType": "many",
                            "toElementId": "XyOWzraTvzAudlKUy6jBm",
                            "toInsideElementId": "eFMUSKazSLNq7xAforL6B",
                            "toRelationType": "one",
                            "path": [
                                [
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -642.0529812749993,
                                            "y": -871.6877513745876
                                        },
                                        "p2": {
                                            "x": -619.0529812749993,
                                            "y": -871.6877513745876
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -642.0529812749993,
                                            "y": -879.6877513745876
                                        },
                                        "p2": {
                                            "x": -622.0529812749993,
                                            "y": -871.6877513745876
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -622.0529812749993,
                                            "y": -871.6877513745876
                                        },
                                        "p2": {
                                            "x": -642.0529812749993,
                                            "y": -863.6877513745876
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -642.0529812749993,
                                            "y": -871.6877513745876
                                        },
                                        "p2": {
                                            "x": -608.0529812749993,
                                            "y": -871.6877513745876
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -608.0529812749993,
                                            "y": -871.6877513745876
                                        },
                                        "p2": {
                                            "x": -584.8840097066699,
                                            "y": -871.6877513745876
                                        }
                                    },
                                    {
                                        "type": "angle",
                                        "width": 10,
                                        "turn": 1,
                                        "point": {
                                            "x": -574.8840097066699,
                                            "y": -861.6877513745876
                                        },
                                        "p1": {
                                            "x": -574.8840097066699,
                                            "y": -871.6877513745876
                                        },
                                        "p2": {
                                            "x": -574.8840097066699,
                                            "y": -861.6877513745876
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -574.8840097066699,
                                            "y": -861.6877513745876
                                        },
                                        "p2": {
                                            "x": -574.8840097066699,
                                            "y": -681.9146555167079
                                        }
                                    },
                                    {
                                        "type": "angle",
                                        "width": 10,
                                        "turn": 0,
                                        "point": {
                                            "x": -564.8840097066699,
                                            "y": -671.9146555167079
                                        },
                                        "p1": {
                                            "x": -574.8840097066699,
                                            "y": -671.9146555167079
                                        },
                                        "p2": {
                                            "x": -541.7150381383404,
                                            "y": -671.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -564.8840097066699,
                                            "y": -671.9146555167079
                                        },
                                        "p2": {
                                            "x": -541.7150381383404,
                                            "y": -671.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -507.71503813834045,
                                            "y": -671.9146555167079
                                        },
                                        "p2": {
                                            "x": -530.7150381383404,
                                            "y": -671.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -525.7150381383404,
                                            "y": -679.9146555167079
                                        },
                                        "p2": {
                                            "x": -525.7150381383404,
                                            "y": -663.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -507.71503813834045,
                                            "y": -671.9146555167079
                                        },
                                        "p2": {
                                            "x": -541.7150381383404,
                                            "y": -671.9146555167079
                                        }
                                    }
                                ],
                                [
                                    {
                                        "x": -642.0529812749993,
                                        "y": -871.6877513745876
                                    },
                                    {
                                        "x": -574.8840097066699,
                                        "y": -871.6877513745876
                                    },
                                    {
                                        "x": -574.8840097066699,
                                        "y": -671.9146555167079
                                    },
                                    {
                                        "x": -507.71503813834045,
                                        "y": -671.9146555167079
                                    }
                                ],
                                {
                                    "minX": -642.0529812749993,
                                    "maxX": -507.71503813834045,
                                    "minY": -871.6877513745876,
                                    "maxY": -671.9146555167079
                                },
                                [
                                    4,
                                    3
                                ]
                            ],
                            "fromElementX": -962.0529812749993,
                            "fromElementY": -969.6877513745876,
                            "toElementX": -507.71503813834045,
                            "toElementY": -733.9146555167079
                        },
                        "x": 1100,
                        "y": 448,
                        "width": 1,
                        "height": 1
                    },
                    "GAVTqBbnqBIYl3yxsjf7W": {
                        "id": "GAVTqBbnqBIYl3yxsjf7W",
                        "type": "line",
                        "value": {
                            "fromElementId": "8K2U0Vh1wrZM1Gg6gw_zK",
                            "fromInsideElementId": "tDEWUx5nA5ZUnWVq7UUky",
                            "fromRelationType": "many",
                            "toElementId": "XyOWzraTvzAudlKUy6jBm",
                            "toInsideElementId": "eFMUSKazSLNq7xAforL6B",
                            "toRelationType": "one",
                            "path": [
                                [
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -642.0529812749993,
                                            "y": -484.7248367366318
                                        },
                                        "p2": {
                                            "x": -619.0529812749993,
                                            "y": -484.7248367366318
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -642.0529812749993,
                                            "y": -492.7248367366318
                                        },
                                        "p2": {
                                            "x": -622.0529812749993,
                                            "y": -484.7248367366318
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -622.0529812749993,
                                            "y": -484.7248367366318
                                        },
                                        "p2": {
                                            "x": -642.0529812749993,
                                            "y": -476.7248367366318
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -642.0529812749993,
                                            "y": -484.7248367366318
                                        },
                                        "p2": {
                                            "x": -608.0529812749993,
                                            "y": -484.7248367366318
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -608.0529812749993,
                                            "y": -484.7248367366318
                                        },
                                        "p2": {
                                            "x": -584.8840097066699,
                                            "y": -484.7248367366318
                                        }
                                    },
                                    {
                                        "type": "angle",
                                        "width": 10,
                                        "turn": 0,
                                        "point": {
                                            "x": -574.8840097066699,
                                            "y": -494.7248367366318
                                        },
                                        "p1": {
                                            "x": -574.8840097066699,
                                            "y": -484.7248367366318
                                        },
                                        "p2": {
                                            "x": -574.8840097066699,
                                            "y": -661.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -574.8840097066699,
                                            "y": -494.7248367366318
                                        },
                                        "p2": {
                                            "x": -574.8840097066699,
                                            "y": -661.9146555167079
                                        }
                                    },
                                    {
                                        "type": "angle",
                                        "width": 10,
                                        "turn": 1,
                                        "point": {
                                            "x": -564.8840097066699,
                                            "y": -671.9146555167079
                                        },
                                        "p1": {
                                            "x": -574.8840097066699,
                                            "y": -671.9146555167079
                                        },
                                        "p2": {
                                            "x": -541.7150381383404,
                                            "y": -671.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -564.8840097066699,
                                            "y": -671.9146555167079
                                        },
                                        "p2": {
                                            "x": -541.7150381383404,
                                            "y": -671.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -507.71503813834045,
                                            "y": -671.9146555167079
                                        },
                                        "p2": {
                                            "x": -530.7150381383404,
                                            "y": -671.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -525.7150381383404,
                                            "y": -679.9146555167079
                                        },
                                        "p2": {
                                            "x": -525.7150381383404,
                                            "y": -663.9146555167079
                                        }
                                    },
                                    {
                                        "type": "line",
                                        "p1": {
                                            "x": -507.71503813834045,
                                            "y": -671.9146555167079
                                        },
                                        "p2": {
                                            "x": -541.7150381383404,
                                            "y": -671.9146555167079
                                        }
                                    }
                                ],
                                [
                                    {
                                        "x": -642.0529812749993,
                                        "y": -484.7248367366318
                                    },
                                    {
                                        "x": -574.8840097066699,
                                        "y": -484.7248367366318
                                    },
                                    {
                                        "x": -574.8840097066699,
                                        "y": -671.9146555167079
                                    },
                                    {
                                        "x": -507.71503813834045,
                                        "y": -671.9146555167079
                                    }
                                ],
                                {
                                    "minX": -642.0529812749993,
                                    "maxX": -507.71503813834045,
                                    "minY": -671.9146555167079,
                                    "maxY": -484.7248367366318
                                },
                                [
                                    4,
                                    3
                                ]
                            ],
                            "fromElementX": -962.0529812749993,
                            "fromElementY": -582.7248367366318,
                            "toElementX": -507.71503813834045,
                            "toElementY": -733.9146555167079
                        },
                        "x": 1100,
                        "y": 448,
                        "width": 1,
                        "height": 1
                    }
                }
                draft.areas = {};

                // CANVAS LIMITS
                draft = calculateCanvasLimits(state, draft);

                break;
            }

            // ------------------------------------------------------------------
            // LOAD QUICK SHARE PROJECT DATA
            case diagramActionTypes.LOAD_QUICK_SHARE_PROJECT_DATA: {
                draft.elements = action.payload.elements;
                draft.lines = action.payload.lines;
                draft.areas = action.payload.areas;

                draft.scale = action.payload.diagramDetails.scale;
                draft.originXY = action.payload.diagramDetails.originXY;
                break;
            }

            // ------------------------------------------------------------------
            // MODAL DIAGRAM EXPORT SQL
            case diagramActionTypes.TOGGLE_MODAL_DIAGRAM_EXPORT_SQL: {
                draft.isModalDiagramExportSqlEnabled = !state.isModalDiagramExportSqlEnabled;
                draft.modalDiagramExportSqlData = { dbEngine: action.payload.dbEngine };

                draft.isBtnExportCodeEnabled = false;
                draft.isBtnExportEnabled = false;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // // UNFOCUS ENABLED CONTROLS
                // draft = unfocusEnabledControls(state, draft, 'isBtnExportCodeEnabled');

                break;
            }
            case diagramActionTypes.DISABLE_MODAL_DIAGRAM_EXPORT_SQL: {
                draft.isModalDiagramExportSqlEnabled = false;
                draft.modalDiagramExportSqlData = { dbEngine: null };

                draft.isBtnExportCodeEnabled = false;
                draft.isBtnExportEnabled = false;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // // UNFOCUS ENABLED CONTROLS
                // draft = unfocusEnabledControls(state, draft, 'isBtnExportCodeEnabled');

                break;
            }

            // ------------------------------------------------------------------
            // FOCUS_ALL_ELEMENTS
            case diagramActionTypes.FOCUS_ALL_ELEMENTS: {
                const elementsKeys = Object.keys(draft.elements);
                const areasKeys = Object.keys(draft.areas);

                const elementsCount = elementsKeys.length;
                const areasCount = areasKeys.length;

                // if one area with no elements
                if (areasCount === 1 && elementsCount === 0) {
                    let focusedArea = null;
                    let focusedAreaId = null;

                    areasKeys.forEach((areaId) => {
                        focusedArea = state.areas[areaId];
                        focusedAreaId = areaId;
                    });

                    // 1. update focused area
                    draft.focusedAreaId = focusedAreaId;
                    draft.focusedArea = focusedArea;

                    // 2. update focused inside element
                    draft.focusedInsideElement = null;

                    // 3. update focused elements
                    draft.focusedElements = [];

                    // 4. update focused elements ids
                    draft.focusedElementsIds = [];

                    // 5. update focused line
                    draft.focusedLineId = null;
                    draft.focusedLine = null;

                    break;



                    // if one or more elements with no areas
                } else if (areasCount === 0 && elementsCount > 0) {
                    let focusedElements = {};
                    let focusedElementsIds = [];

                    if (elementsCount) {
                        elementsKeys.forEach((elementId) => {
                            focusedElementsIds.push(elementId);
                            focusedElements[elementId] = draft.elements[elementId];
                        });
                    }

                    // 1. update focused inside element
                    draft.focusedInsideElement = null;

                    // 2. update focused elements
                    draft.focusedElements = focusedElements;

                    // 3. update focused elements ids
                    draft.focusedElementsIds = focusedElementsIds;

                    // 4. update focused line
                    draft.focusedLineId = null;
                    draft.focusedLine = null;

                    draft.focusedAreaId = null;
                    draft.focusedArea = null;

                    break;


                    // if multiple areas with or without elements
                } else {
                    let focusedGroupElements = {};
                    let focusedGroupElementsIds = [];
                    let focusedGroupAreas = {};
                    let focusedGroupAreasIds = [];

                    if (elementsCount) {
                        elementsKeys.forEach((elementId) => {
                            focusedGroupElements[elementId] = state.elements[elementId];
                            focusedGroupElementsIds.push(elementId);
                        });
                    }

                    if (areasCount) {
                        areasKeys.forEach((areaId) => {
                            focusedGroupAreas[areaId] = state.areas[areaId];
                            focusedGroupAreasIds.push(areaId);
                        });
                    }

                    // 1. update focused inside element
                    draft.focusedInsideElement = null;
                    // 2. update focused elements
                    draft.focusedElements = {};
                    // 3. update focused elements ids
                    draft.focusedElementsIds = [];

                    // 4. update focused line
                    draft.focusedLineId = null;
                    draft.focusedLine = null;

                    // ? when you create new area and move mouse too fast => add new area + unfocus new area
                    draft.focusedAreaId = null;
                    draft.focusedArea = null;

                    // . update focused group elements
                    draft.focusedGroupElements = focusedGroupElements;
                    // . update focused group elements ids
                    draft.focusedGroupElementsIds = focusedGroupElementsIds;

                    // . update focused group areas
                    draft.focusedGroupAreas = focusedGroupAreas;
                    // . update focused group areas ids
                    draft.focusedGroupAreasIds = focusedGroupAreasIds;

                    break;
                }
            }

            case diagramActionTypes.ENABLE_BTN_EXPORT_IMAGE_CLICK: {
                draft.isBtnExportImageClickEnabled = true;
                break;
            }
            case diagramActionTypes.DISABLE_BTN_EXPORT_IMAGE_CLICK: {
                draft.isBtnExportImageClickEnabled = false;
                draft.isBtnExportImageEnabled = false;
                draft.isBtnExportEnabled = false;
                break;
            }


            case diagramActionTypes.TOGGLE_BTN_CANVAS_GRID: {
                draft.isCanvasGridEnabled = !state.isCanvasGridEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    null,
                    'isBtnExportImageEnabled'
                );

                break;
            }


            default:
                break;
        }
    });
};

export default diagramReducer;
